import type { PlateMaterial, MdfColor, PlatePrices } from './neon-plate-materials'
export { PLATE_MATERIALS, availablePlateMaterials, type PlateMaterial, type MdfColor } from './neon-plate-materials'
import type { StudioFreeComposition, FreeCompositionConfig } from './studio-composition'
import type { RoomScaleReference } from './room-preview'
import type { QualityAssessment } from './quality-assessment'
import type { NeonPriceDisplayMode } from './neon-customisation'
import type { SalesOptionsConfig, RgbOptionConfig } from './sales-options'
import type { VisionModelId } from './vision-model'
import type { NeonPlateVariant } from './neon-plate-variants'
import type { CustomerTextsConfig } from './customer-texts'
import type { ResolvedShippingConfig } from './shipping'
import type { LogoTraceBinding } from './logo-trace-binding'
import { LOGO_CENTERLINE_RADIUS_MM } from './tenant-config-contract'
export { LOGO_CENTERLINE_RADIUS_MM } from './tenant-config-contract'

/** Visual style vocabulary for automatic Logo captions; never an exact font claim. */
export const NEON_LOGO_CAPTION_STYLES = ['digital', 'stencil', 'handwritten', 'script', 'block', 'square', 'unknown'] as const
export type NeonLogoCaptionStyle = typeof NEON_LOGO_CAPTION_STYLES[number]
export interface NeonLogoCaptionStyleObservation {
  category: NeonLogoCaptionStyle
  /** Missing/invalid provider evidence must never be reported as an AI classification. */
  source: 'vision' | 'missing' | 'invalid'
}
export interface NeonLogoCaptionFontSelection extends NeonLogoCaptionStyleObservation {
  preferredFontId: string
  fontId: string | null
  fallbackReason: 'none' | 'unknown-style' | 'preferred-unavailable' | 'glyph-coverage' | 'no-covered-font' | 'placement-or-clearance'
  rejectedCandidates: Array<{ fontId: string; reason: 'disabled' | 'not-in-catalog' | 'glyph-coverage' }>
}

/** Resolve stored/legacy values through the same bounds as the production setting. */
export function resolveLogoCenterlineRadiusMm(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value)
    && value >= LOGO_CENTERLINE_RADIUS_MM.min && value <= LOGO_CENTERLINE_RADIUS_MM.max
    ? value : LOGO_CENTERLINE_RADIUS_MM.default
}

/**
 * Buisdiameter in mm. Was een vaste union (4|6|8|10); verbreed naar `number`
 * zodat een maakbedrijf eigen diameters kan voeren (bv. 12, 16 — zie
 * docs/PRODUCT-DOEL.md). De gangbare maten staan in TUBE_THICKNESS_OPTIONS
 * als fallback wanneer een tenant geen eigen lijst heeft ingesteld.
 */
export type TubeThicknessMm = number

/** Bestaande maximale plaat-/freeswerkmaat, in cm; alleen fallback bij ontbrekende instellingen. */
export const NEON_MAX_PLATE_SIZE_CM_DEFAULT = Object.freeze({ width: 244, height: 122 })

/** Instelbare hele millimeters, afgesproken voor alle neonproducten (30-9-2026). */
export const NEON_TUBE_MIN_MM = 4
export const NEON_TUBE_MAX_MM = 16
export const NEON_TUBE_DIAMETERS_MM = Object.freeze(Array.from(
  { length: NEON_TUBE_MAX_MM - NEON_TUBE_MIN_MM + 1 },
  (_, index) => NEON_TUBE_MIN_MM + index,
))
export const NEON_TUBE_DIAMETER_VALUES = Object.freeze(NEON_TUBE_DIAMETERS_MM.map(String))

export function isNeonTubeDiameterMm(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= NEON_TUBE_MIN_MM && value <= NEON_TUBE_MAX_MM
}

/** Vaste fallback-set buisdiameters wanneer een tenant geen eigen lijst voert. */
export const TUBE_THICKNESS_OPTIONS: number[] = [4, 6, 8, 10]

/**
 * D6 — de valuta's waarin een tenant zijn prijzen kan voeren.
 *
 * Europa-eerst, en bewust een GESLOTEN lijst: elke munt hier moet in het
 * portaal-formulier als optie staan, in `Intl.NumberFormat` correct uitpakken
 * en (later) door de betaal-koppeling ondersteund worden. Een vrij tekstveld
 * zou een tikfout ("EURO", "eur") stilletjes tot een kapotte prijsweergave
 * maken.
 *
 * De volgorde is die van het portaal-select: EUR eerst (de default en de
 * bestaande populatie), daarna de zeven niet-euro-munten van de EU/EER.
 */
export const SUPPORTED_CURRENCIES = [
  'EUR',
  'GBP',
  'CHF',
  'DKK',
  'SEK',
  'NOK',
  'PLN',
  'CZK',
] as const

export type Currency = (typeof SUPPORTED_CURRENCIES)[number]

/**
 * De valuta van een tenant die er geen kiest. Elke bestaande grant zit hier:
 * vóór D6 was € impliciet in de hele keten, dus alles wat vandaag draait is
 * per definitie EUR.
 */
export const DEFAULT_CURRENCY: Currency = 'EUR'

/**
 * B-3 (Pascal, 5-10-2026): voorlopig is alleen EUR kiesbaar én zichtbaar. De
 * betaalketen (Mollie, orders, publieke pagina's) rekent uitsluitend in euro;
 * een andere munt in de configurator tonen zou een geschil opleveren (£ getoond,
 * € afgeschreven). Een opgeslagen andere waarde blijft geldig volgens
 * `SUPPORTED_CURRENCIES`, maar wordt overal als `DEFAULT_CURRENCY` getoond.
 * Een valuta openzetten = hem hier toevoegen, nadat de hele keten hem aankan.
 */
export const ENABLED_CURRENCIES: readonly Currency[] = ['EUR']

export function isEnabledCurrency(x: unknown): x is Currency {
  return typeof x === 'string' && (ENABLED_CURRENCIES as readonly string[]).includes(x)
}

/**
 * Het STANDAARDtarief voor de tekstfrees — de waarde die geldt zolang een maker
 * in zijn neon-instellingen (`pricing.pricePerMeterTextRouting` en
 * `pricing.pricePerTextPlunge`) niets anders invult. In EURO's exclusief btw,
 * net als `pricePerMeterLed` en `pricePerBend`; de sleutels zijn die van
 * `Settings`, zodat een lezer `settings[k] ?? TEKST_FREES_TARIEF_STANDAARD[k]`
 * kan schrijven.
 *
 * Het is het tarief dat de MAKER aan zijn klant rekent, dus een instelling per
 * maker (besluit eigenaar via L0, 28-9-2026) en geen prijs van Mark2Form zelf —
 * daarom hier en niet in `@mark2form/catalog`. Het geldt alleen voor tekst die
 * als groef wordt gefreesd (tekst-engine V3, bewerking
 * `constant-width-channel`): de prijsregels `text-routing` en `text-plunge`.
 *
 * De getallen zijn een voorbeeld, afgeleid uit het bestaande prijsmodel (de
 * vormarbeid per meter neon verhuist van de buiger naar de frees); de
 * afleiding staat in `docs/metingen/text-engine-v3/TARIEF-FREES.md`. Dit is de
 * ENIGE plek waar de standaard staat: het formulier (`default` in
 * `tenant-config-schema.ts`) en de runtime lezen deze constante.
 */
export const TEKST_FREES_TARIEF_STANDAARD = {
  /** € per meter freesbaan (alle gangen van een kanaalbewerking samen). */
  pricePerMeterTextRouting: 1.57,
  /** € per invalling (één per freesgang). */
  pricePerTextPlunge: 0.08,
} as const satisfies Readonly<Record<'pricePerMeterTextRouting' | 'pricePerTextPlunge', number>>

/**
 * Plate cut-shape modes:
 *   - rect             — classic rounded rectangle
 *   - contour          — design-following raster contour (can be wobbly on
 *                        irregular tracings; honest to the source geometry)
 *   - contour-smooth   — aggressively smoothed cut-to-shape: detail wobbles
 *                        get filtered, but a near-circle stays circular
 *   - contour-standard — primitive-shape detection: circles render as perfect
 *                        circles, near-rectangles as rounded rects, ellipses
 *                        as ellipses; falls back to smooth contour otherwise
 */
export type PlateShape = 'rect' | 'contour' | 'contour-smooth' | 'contour-standard'
export type PreviewView = 'studio' | 'room'

/**
 * Eenhedenstelsel voor de KLANT-presentatie van maten (B10 / D-E2).
 * 'metric' (default) toont cm/mm zoals altijd; 'imperial' toont decimale
 * inches ("55.1 in") voor een Amerikaanse of Britse tenant. Puur weergave:
 * alle interne rekeneenheden (cm/mm in de pijplijn, prijs, DXF/CDR-export
 * voor de werkplaats) blijven metrisch.
 */
export type UnitSystem = 'metric' | 'imperial'

export interface NeonColor {
  id: string
  name: string
  hex: string
  /**
   * Wit-tint van deze LED (Plan C): 'warm' (≈2700-3000K) / 'cool' (≈6000K+) /
   * 'neutral'. De perceptuele mapping gebruikt dit om een achromatische bron
   * met warme zweem naar warm-wit te sturen i.p.v. blind naar de witste;
   * de preview kan er later de gloed-tint op afstemmen. Optioneel — alleen
   * zinvol op whitish palet-leden.
   */
  tint?: 'warm' | 'cool' | 'neutral'
}



/**
 * Het bereik van `Room.wallWidthCm` — één bron voor het portaalformulier (de
 * `min`/`max` in `RoomSchema`) en de zeef die vóór het opslaan klemt
 * (`sanitizeRoomEditor` in `@mark2form/config-model`). Tot 23-9-2026 zeiden die
 * twee iets anders: het schema `min: 1`, de zeef 10–2000 met terugval 300. Een
 * muur smaller dan 10 cm of breder dan 20 m is op een kamerfoto geen maat maar
 * een tikfout, en de runtime rekent er de ware schaal mee uit
 * (pixels-per-cm = signAreaPx.width / wallWidthCm) — dus de zeef wint, en het
 * schema zegt nu hetzelfde. `standaard` is wat de zeef invult als er geen
 * bruikbaar getal staat; de runtime begint een nieuwe kamer ook op 300.
 */
export const ROOM_MUURBREEDTE_CM = { min: 10, max: 2000, standaard: 300 } as const

export interface Room {
  id: string
  name: string
  /**
   * Een ABSOLUTE http(s)-URL. Een relatief pad wordt in de runtime opgelost
   * tegen de host van de runtime (de embed), niet tegen die van het portaal
   * dat de foto uploadde — dus de zeef weigert het.
   */
  image: string
  /** Ware muurbreedte in cm, binnen `ROOM_MUURBREEDTE_CM`. */
  wallWidthCm: number
  signAreaPx: { x: number; y: number; width: number; height: number }
  imageWidthPx: number
  imageHeightPx: number
  /**
   * Plan E5 — hoe sterk de neon-gloed op déze kamerfoto doorwerkt (muur-cast).
   * 0 = geen gloed, 1 = vol (default). Donkere foto's verdragen 1; lichte,
   * zonnige kamers ogen realistischer rond 0.5–0.7.
   */
  glowIntensity?: number
  /**
   * S4 (2026-07-17) — hoe het neon in het werkgebied (signAreaPx) wordt
   * geplaatst:
   *  - 'scale' (default, bestaand gedrag): op WARE schaal via wallWidthCm —
   *    pixels-per-cm = signAreaPx.width / wallWidthCm, gecentreerd, niet verkleind.
   *  - 'fill': grootst passend — het ontwerp vult het werkgebied maximaal
   *    (uniforme schaal, aspect behouden), gecentreerd.
   */
  placementMode?: 'scale' | 'fill'
  /** null explicitly means uncalibrated. Absent reads an existing configured wall reference. */
  scaleReference?: RoomScaleReference | null
}

export interface Settings {
  freeComposition?: FreeCompositionConfig
  /** Parcel shipping, distinct from delivery lead-time tiers. */
  shipping?: ResolvedShippingConfig
  /** Sparse safe customer-copy overrides; absent text falls back to system i18n. */
  customerTexts?: CustomerTextsConfig
  /** Visitor capabilities, explicitly enabled by the maker. */
  features?: { uvPrint?: boolean }
  /** Brongebonden lijn/boog-fit tijdens vectorisatie. Later geschikt voor feature-toegang. */
  logoRefinement?: { enabled: boolean; allowToggle: boolean }
  colors: NeonColor[]
  mdfColors: MdfColor[]
  pricePerMeterLed: number
  /** €/bend — labour cost added per rounded corner in the design. */
  pricePerBend?: number
  /** €/separate tube — fixed setup/lead-wire cost added per visible region. */
  pricePerTubeSetup?: number
  /**
   * €/meter freesbaan — het tarief tekstfrees van de maker (prijsregel
   * `text-routing`). Alleen voor tekst die als groef wordt gefreesd (tekst-
   * engine V3, `constant-width-channel`). Afwezig ⇒
   * `TEKST_FREES_TARIEF_STANDAARD.pricePerMeterTextRouting`. Bewust NIET in de
   * `DEFAULTS` van de runtime: een nieuwe sleutel in élke Settings zou de
   * trace-cachevingerafdruk van iedere tenant veranderen.
   */
  pricePerMeterTextRouting?: number
  /**
   * €/invalling — idem, prijsregel `text-plunge` (één invalling per freesgang).
   * Afwezig ⇒ `TEKST_FREES_TARIEF_STANDAARD.pricePerTextPlunge`.
   */
  pricePerTextPlunge?: number
  /** Optional merchant variants; absent preserves the existing three-material offer. */
  plateVariants?: NeonPlateVariant[]
  platePrices: PlatePrices
  maxPlateSizeCm: { width: number; height: number }
  defaultWidthCm: number
  /**
   * Compatibele lijst voor productievalidatie. De tenant-reader normaliseert
   * deze naar alle hele millimeters van 4 tot en met 16; de maker stelt
   * voortaan alleen defaultTubeThicknessMm in.
   */
  tubeDiametersMm: number[]
  /**
   * Standaard-buisdiameter (hele mm van 4 tot en met 16, standaard 8)
   * waarmee een nieuw ontwerp opent. Blijft als
   * TubeThicknessMm getypeerd voor backwards-compat met bestaande readers
   * (tubeWattsPerMeter-keys, persistence, share-url).
   */
  defaultTubeThicknessMm: TubeThicknessMm
  /**
   * Freesmaat (router-bit diameter, mm) waarmee het maakbedrijf de slogan-
   * letters uit de achterplaat freest. Puur een PRODUCTIE-instelling: raakt
   * de klant-preview NIET, maar stuurt wél de DXF/CDR-export. De slogan-
   * centerlijnen worden tool-gecompenseerd (offset met halve freesmaat) zodat
   * de uitgefreesde groef op maat (= buisdiameter breed) uitkomt. Per-tenant
   * configureerbaar; ontbreekt het veld dan valt de export terug op een
   * verstandige default (zie lib/settings.ts DEFAULTS). 0 = geen compensatie
   * (centerlijn 1-op-1 als enkele frees-pass).
   */
  freesmaatMm: number
  contourMarginCm: number
  /**
   * Auto-scale the contour margin based on the chosen logo width — small
   * signs look balanced with a thin 1cm rim while a 1.5m sign needs a
   * meatier 3cm rim to read as intentional rather than tight. When
   * `enabled` is true, the chosen widthCm picks one of {smallCm,
   * mediumCm, largeCm}. The user override (admin-tuning slider) wins
   * over both.
   */
  contourMarginAuto?: {
    enabled: boolean
    smallMaxCm: number
    mediumMaxCm: number
    smallCm: number
    mediumCm: number
    largeCm: number
  }
  /** Minimum corner radius (mm) for the contour-cut plate edge. */
  contourCornerRadiusMm: number
  /**
   * Modus voor hoekafronding van de logo-vormen (buigpaden), niet de bordrand.
   * 'asymmetric' = bolle en holle hoeken krijgen elk hun eigen radius;
   * 'legacy' = één uniforme radius (oude gedrag, = bendRadius-afleiding).
   */
  cornerRadiusMode?: 'asymmetric' | 'legacy'
  /**
   * Bolle (convexe) hoekradius in mm — vaste echte millimeters, correct
   * omgerekend per bordmaat (schaalt dus niet mee met de ontwerpgrootte).
   */
  outerCornerRadiusMm?: number
  /**
   * Holle (concave) hoekradius in mm — plafond: holle hoeken worden nooit
   * ruimer afgerond dan dit, zodat inkepingen de vorm niet "insnijden".
   */
  innerCornerRadiusMm?: number
  /**
   * Physical centreline corner radius for validated Logo SVG workflow paths.
   * Missing means 3 mm; 0 keeps sharp corners. Independent of tube diameter,
   * board edges and the legacy/Neon Text outer/inner radius controls.
   */
  logoCenterlineRadiusMm?: number
  /**
   * Tekst-render-modus. 'font-registered' = gedetecteerde tekst wordt getypt
   * in het best passende curated font (schone enkele lijn) en per letter op
   * zijn originele plek/hoogte geregistreerd; 'legacy' = oude gedrag
   * (inkt-skeleton + gelijke-gaten-lay-out). Default 'legacy' tot de
   * overlay-QA akkoord is (bewuste, aparte default-flip).
   */
  textMode?: 'font-registered' | 'legacy'
  /**
   * Kleur-modus (Plan C / masterplan F2). 'perceptual' = mapping en
   * kwantisatie in OKLab (ΔE_OK — volgt de menselijke waarneming);
   * 'legacy' = het oude HSV/RGB-gedrag, byte-identiek. Per-tenant noodrem.
   */
  colorMode?: 'perceptual' | 'legacy'
  /**
   * Potrace alphaMax voor de trace (hoek-afronding tijdens het vectoriseren).
   * Lager = scherpere hoeken uit de trace ("scherp tracen, bewust afronden" —
   * de asymmetrische hoekradius doet daarna de bewuste ronding). Bereik
   * 0.6–1.4; default 1.2 (historisch gedrag) tot de A/B-check de flip naar
   * 1.0 onderbouwt.
   */
  traceAlphaMax?: number
  /**
   * Maakbaarheids-poort voor INGESLOTEN kleurvlakken bij de kleur-samenvoeging
   * (kaas-gaten-case). Twee kleuren met een zachte grens worden in de
   * kwantisatie samengevoegd; een gat middenin het vlak (donkergele kaasgaten
   * in geel) verdwijnt daardoor volledig, want er blijft geen kleurgrens over
   * om te tracen. Een ingesloten vlak dat minstens `factor × tubeMm` breed is,
   * behoudt zijn grens en wordt een echt gat in het masker — dus één eigen
   * buislijn. Kleinere ingesloten vlakjes smelten juist wél weg: die zijn als
   * losse buisring niet te maken (vergelijk `printOnTop.minSideFactorVsTube`).
   * 0 of negatief zet de regel uit (historisch gedrag). Default 3
   * (= 24 mm bij een 8 mm buis).
   */
  holeMinSizeFactorVsTube?: number
  /**
   * Plan H — grootte-poort voor font-vervanging van herkende tekst.
   * Rijen hoger dan `factor × tubeMm` blijven de originele outline-trace
   * (grote dikke letters zijn als outline al buigbaar); alleen kleinere
   * tekst wordt vervangen door een single-line catalogus-font.
   * Default 10 (= 8cm bij 8mm buis).
   */
  outlineTextMinHeightFactorVsTube?: number
  /**
   * A10 — CLIENT-OPSCHONING v2 (componentmodel + fysieke drempels).
   *
   * Zet de nieuwe opruimregels in `expandAndCleanTrace` aan:
   *   - groep-bewust filteren: een gat (`role: 'hole'`) binnen zijn eigen
   *     component wordt nooit als losse spikkel of als duplicaat van zijn eigen
   *     buitencontour weggegooid;
   *   - de drie drempels (ruis, dedup, kruisings-gap) in BUISDIKTE-eenheden
   *     i.p.v. als fractie van de viewBox-diagonaal;
   *   - curve-behoud bij het oplossen van kruisingen (`splitPathAt`), zodat een
   *     doorgeknipte Bézier geen 180-punts polyline wordt en `bendCount` — en
   *     dus de PRIJS — niet opblaast.
   *
   * ONTBREEKT het veld (de normale situatie), dan is dit `false` en is het
   * client-gedrag byte-identiek aan dat van vóór dit bestekpunt. Bewust een
   * tenant-instelling en geen env-vlag: het gedrag zit in de BROWSER, waar
   * `process.env` niet bestaat, en de uitrol is per-tenant (default uit → één
   * tenant aan → default aan).
   *
   * Deze vlag zit BEWUST NIET in de trace-cachevingerafdruk (zie
   * `buildTraceCacheKey`): hij verandert niets aan de server-trace-output.
   */
  traceCleanupV2?: boolean
  /**
   * B3 — DEKKINGSINVARIANT VÓÓR DE SLOGAN-STRIP.
   *
   * Zet de drie poorten aan die voorkomen dat de slogan-strip vormen weggooit
   * die de font-vervanging niet terugtekent (`lib/trace-route/slogan-step.ts`):
   *   - `checkFontReplacement` eist 100 %-glyphdekking (`drawableGlyphs ===
   *     visibleChars`) in plaats van "minstens één tekenbare glyph"; ontbreekt
   *     er een teken, dan blijft de rij gewoon outline-trace (reason
   *     `'missing-glyphs'`, met de ontbrekende tekens in het debug-object);
   *   - de catch-tak bij een kapotte/onbereikbare fontcatalogus geeft
   *     `drawable: false` (reason `'catalog-error'`) + een server-warning, in
   *     plaats van "strip mag door zonder controle";
   *   - per rij mag de strip hooguit `3 × glyphs + 2` paden claimen, en de
   *     dekkingscheck (`REPLACEMENT_COVERAGE_MIN`) draait zodra er meer dan een
   *     kwart van de paden weggaat — niet pas bij ≥ 90 % verlies.
   *
   * ONTBREEKT het veld (de normale situatie), dan is dit `false` en is de
   * server-trace byte-identiek aan die van vóór dit bestekpunt.
   *
   * ANDERS DAN `traceCleanupV2`/`printOnTop.rowConsistencyV2`/
   * `printOnTop.trademarkOverlapV2` hoort deze vlag WÉL in de
   * trace-cachevingerafdruk (`buildTraceCacheKey`): hij verandert de
   * server-trace-OUTPUT (welke paden overleven), dus een gecachete respons uit
   * de andere vlagstand is aantoonbaar een ander bord. Hij staat daarom als
   * OPTIONEEL veld in `Settings` en wordt alleen geschreven wanneer de tenant
   * hem expliciet stuurt — een afwezige sleutel valt in `JSON.stringify` weg,
   * dus de vingerafdruk van elke tenant die hem niet zet blijft byte-identiek
   * (geen mass-recompute, geen Vision-kosten). Zet één tenant hem aan, dan
   * roteert alleen díé tenant zijn cache.
   */
  sloganStripGuardV2?: boolean
  /**
   * B6 — OCR-INVOER V2 (raster + structurele weigerings-poort).
   *
   * Zet twee samenhangende wijzigingen aan in `packages/ocr/src/claude-logo-ocr.ts`:
   *   - RASTER: het beeld dat naar Vision gaat wordt platgedrukt met
   *     `flattenToContrastBg` (dezelfde contrast-bewuste flatten die het
   *     TRACE-pad al gebruikt) in plaats van hard op wit, en wordt OPGESCHAALD
   *     naar minimaal 1000 px langste zijde (lanczos3; de cap van 1568 px
   *     blijft). Zonder dit werd een wit-op-transparant logo wit-op-wit — de
   *     trace zag alles, Vision niets — en bleef een 400 px upload op 400 px
   *     staan, waardoor een caption van 3 % beeldhoogte op ~12 px binnenkwam.
   *   - REFUSALS: de conversationele regex-filters (`CONVERSATIONAL_*_RE`)
   *     draaien alleen nog bij `confidence < 0.5` óf een ontbrekende/
   *     buiten-bereik-bbox. Een weigering komt per constructie zonder
   *     betrouwbare bbox; "The Design Studio" met confidence 0.9 en een nette
   *     bbox is een merknaam, geen apologie. De sjabloontekst-filter
   *     (`PLACEHOLDER_RE`, BUG-011) blijft bewust onvoorwaardelijk.
   *
   * ONTBREEKT het veld (de normale situatie), dan is dit `false` en is de
   * server-trace byte-identiek aan die van vóór dit bestekpunt.
   *
   * Net als `sloganStripGuardV2` (B3) — en ANDERS dan `traceCleanupV2` /
   * `printOnTop.rowConsistencyV2` / `printOnTop.trademarkOverlapV2` — hoort deze
   * vlag WÉL in de trace-cachevingerafdruk (`buildTraceCacheKey`): hij verandert
   * de server-trace-OUTPUT (welke tekst Vision leest, en dus welke rijen,
   * fontkeuzes en strips daaruit volgen). Hij staat daarom als OPTIONEEL veld in
   * `Settings` en wordt alleen geschreven wanneer de tenant hem expliciet
   * stuurt — een afwezige sleutel valt in `JSON.stringify` weg, dus de
   * vingerafdruk van elke tenant die hem niet zet blijft byte-identiek (geen
   * mass-recompute, geen Vision-kosten). Zet één tenant hem aan, dan roteert
   * alleen díé tenant zijn cache.
   *
   * De vlag zit óók als los component in de OCR-cachesleutel zelf
   * (`cacheKey` in `claude-logo-ocr.ts`): die sleutel hasht de ORIGINELE
   * uploadbytes, niet het raster, en zou zonder dat component de uitkomst van
   * de andere vlagstand blijven serveren.
   */
  ocrRasterV2?: boolean
  /**
   * B4 — RIJ-DETECTIE- EN TERUGVAL-DREMPELS.
   *
   * Zet de vier drempelwijzigingen aan die legitieme captions door de keten
   * laten komen in plaats van ze vóór de rij-vorming te laten sneuvelen:
   *   - `detectTextRows` krijgt eindelijk zijn opts mee: `textHeightRatio`
   *     0.13 → 0.20 en `minRowWidth` van `vbW × 0.25` naar
   *     `max(vbW × 0.10, 2.5 × mediane lidbreedte)` (verkenning Z10);
   *   - `filterImplausibleRows` slaat zijn twee heuristieken over bij
   *     `ocrConfidence: 'high'` — Vision ≥ 0.85 op "1898" wint van een
   *     padtelling (verkenning Z9);
   *   - de orphan-caption-terugval accepteert vanaf vertrouwen 0.70 in plaats
   *     van 0.85, mét een strengere maatpoort: niet meer de (structureel te
   *     hoge) Vision-bbox, maar de UNIE-HOOGTE van de werkelijk gevonden paden;
   *   - de OCR-validatie caps tekst op 80 in plaats van 60 tekens en draait de
   *     dichtheidscheck alleen nog onder vertrouwen 0.7.
   *
   * ONTBREEKT het veld (de normale situatie), dan is dit `false` en zijn de
   * rijen byte-identiek aan die van vóór dit bestekpunt.
   *
   * NET ALS `sloganStripGuardV2` (en anders dan `traceCleanupV2`) hoort deze
   * vlag WÉL in de trace-cachevingerafdruk: hij bepaalt server-side WELKE
   * rijen er zijn, en dus wat de slogan-stap daarna strip. Optioneel veld ⇒
   * afwezige sleutel valt in `JSON.stringify` weg ⇒ elke tenant die hem niet
   * zet houdt byte-identiek dezelfde cachesleutel. Zie `cache-step.ts`.
   */
  textRowsV2?: boolean
  /**
   * B2 — BOOG-/BADGE-TEKST (ronde logo's, verkenning Z1/Z2).
   *
   * Zet de boog-aware tak aan in de orphan-terugval van
   * `lib/trace-route/text-rows.ts` en het bijbehorende gedrag in de slogan-stap
   * (`lib/trace-route/slogan-step.ts`), voor elk Vision-element met
   * `curved: true`:
   *   - de rij wordt niet meer via `snapBboxToTrace` + `pathsInsideRowBbox`
   *     gezocht (die geven op een RING gegarandeerd `null` respectievelijk het
   *     hele centrale embleem), maar via `packages/ocr/src/arc-text.ts`: kleine
   *     paden binnen de Vision-bbox als kandidaten, een RANSAC-cirkelfit door
   *     hun middelpunten als validatie, en exact de inliers als `pathIndices`;
   *   - de vertrouwensdrempel voor curved captions is 0.60 in plaats van 0.85 —
   *     de arc-fit is zelf al een onafhankelijke geometrische controle;
   *   - de maatpoort (15 cm / 0.65 × wordmarkhoogte) meet op de MEDIANE
   *     LETTERHOOGTE in plaats van op de bbox-hoogte, want de unie-hoogte van
   *     een boog is de ringdiameter en zegt niets over de letters;
   *   - de slogan-poort gebruikt diezelfde letterhoogte en slaat de
   *     fragmentatie-check over; de strip verwijdert uitsluitend de inliers
   *     (géén restjes-zone rond de ringbbox — die zou het embleem meenemen).
   *
   * ONTBREEKT het veld (de normale situatie), dan is dit `false` en is de
   * server-trace byte-identiek aan die van vóór dit bestekpunt: elke tak
   * hierboven zit achter een strikte `=== true`.
   *
   * NET ALS `sloganStripGuardV2` (B3), `ocrRasterV2` (B6) en `textRowsV2` (B4)
   * — en anders dan `traceCleanupV2` — hoort deze vlag WÉL in de
   * trace-cachevingerafdruk (`buildTraceCacheKey`): hij bepaalt server-side
   * welke rijen er zijn en welke paden de strip overleven, dus een gecachete
   * respons uit de andere vlagstand is aantoonbaar een ander bord. Optioneel
   * veld ⇒ een afwezige sleutel valt in `JSON.stringify` weg ⇒ elke tenant die
   * hem niet zet houdt byte-identiek dezelfde cachesleutel (geen
   * mass-recompute, geen Vision-kosten). Zet één tenant hem aan, dan roteert
   * alleen díé tenant zijn cache.
   */
  arcTextV2?: boolean
  /**
   * B5 — GERICHTE BBOX-VERFIJNINGSCALL (een EXTRA, BETAALDE Vision-stap).
   *
   * Zet de refine-tak aan in de RECHTE orphan-terugval van
   * `lib/trace-route/text-rows.ts`. Voor een element dat anders definitief
   * wegvalt — `snapBboxToTrace` gaf origineel `null`, het is niet `curved`, het
   * vertrouwen is ≥ 0.55 en de rij zou dus gedropt worden — wordt één keer
   * `refineElementBbox` (`packages/ocr/src/claude-bbox-refine.ts`) aangeroepen:
   * een uitsnede rond de Vision-bbox (30 % marge per zijde, 512 px) gaat naar
   * `claude-sonnet-4-6` met de vraag om het STRAKKE INKT-KADER van precies die
   * al herkende tekst. De verfijnde bbox gaat daarna ONVERKORT opnieuw door
   * dezelfde snap-, pad- en maatpoorten — een betere meetlat, geen vrijstelling.
   *
   * MAXIMAAL 3 CALLS PER UPLOAD (teller in `buildTextRows`). De calls tellen mee
   * in `createAiUsageTracker` (dus in `metadata.ai` van het portaal-event) en
   * vallen onder exact dezelfde poorten als elke andere betaalde Vision-stap:
   * M-7/`visionAllowed` (grant vereist), het per-tenant Vision-dagbudget
   * (`checkVisionBudgetGate`) en de `withVisionSlot`-semafoor.
   *
   * ONTBREEKT het veld (de normale situatie), dan is dit `false`, wordt er geen
   * enkele extra call gedaan en is de server-trace byte-identiek aan die van
   * vóór dit bestekpunt.
   *
   * NET ALS `sloganStripGuardV2` (B3), `ocrRasterV2` (B6), `textRowsV2` (B4) en
   * `arcTextV2` (B2) hoort deze vlag WÉL in de trace-cachevingerafdruk
   * (`buildTraceCacheKey`): met de vlag aan kan een element dat anders wegvalt
   * alsnog een rij worden, en rijen sturen de slogan-strip aan — een gecachete
   * respons uit de andere vlagstand is aantoonbaar een ander bord. Optioneel
   * veld ⇒ een afwezige sleutel valt in `JSON.stringify` weg ⇒ elke tenant die
   * hem niet zet houdt byte-identiek dezelfde cachesleutel.
   *
   * DIT IS EEN KOSTEN-KWALITEITSKNOP, GEEN BUGFIX — en dat maakt hem
   * categorisch anders dan B2/B3/B4/B6. Elke refine-call kost $0,003-0,007
   * (worst case +$0,01-0,02 per upload, alléén op uploads met faal-elementen).
   * De vlag hoeft daarom NOOIT globaal default aan te gaan: de eigenaar zet hem
   * per tenant aan zodra de gemeten dekkingswinst per call (portaal-event
   * `metadata.textRefine`, naast `textDrops`) die prijs waard is.
   */
  bboxRefineEnabled?: boolean
  /**
   * B8 — LETTERREGISTRATIE EN -METING CONSISTENT (diakriet-vakken).
   *
   * Zet de diakriet-merge aan in de twee plekken die vandaag over een LOS
   * accent-vak struikelen:
   *   - `reconcileBoxes` (`packages/fonts/src/letter-registration.ts`) telt
   *     het accent van een É als een extra vak → glyph-mismatch → de hele rij
   *     valt terug op de gelijke-gaten-woordlay-out (letter-drift terug);
   *   - `correctTextCaseFromBoxes` (`packages/ocr/src/case-from-boxes.ts`)
   *     eist exact 1-op-1 vak/teken en slaat de case-correctie dus volledig
   *     over zodra er één accent in de rij staat ("Élan" → geen correctie).
   * Met de vlag aan draait `mergeDiacriticBoxes` er eerst overheen: een vak
   * van hooguit 0.45 × de mediane vakhoogte dat voor ≥ 60 % van zijn breedte
   * binnen een groter vak valt én er volledig bóven of ónder ligt, gaat op in
   * dat vak. Daarna zien beide stappen weer één vak per teken.
   *
   * ONTBREEKT het veld (de normale situatie), dan is dit `false` en zijn de
   * vakken, de rij-registratie en de case-correctie byte-identiek aan die van
   * vóór dit bestekpunt: beide takken zitten achter een strikte `=== true`.
   *
   * NET ALS `sloganStripGuardV2` (B3), `ocrRasterV2` (B6), `textRowsV2` (B4),
   * `arcTextV2` (B2) en `bboxRefineEnabled` (B5) hoort deze vlag WÉL in de
   * trace-cachevingerafdruk (`buildTraceCacheKey`): hij verandert de
   * `suggestedText` (case-correctie) én de `letterBoxes` van een rij, en dus
   * de gecachete SVG. Hij wordt daar niet uitgesloten en telt dus automatisch
   * mee. Optioneel veld ⇒ een afwezige sleutel valt in `JSON.stringify` weg ⇒
   * elke tenant die hem niet zet houdt byte-identiek dezelfde cachesleutel
   * (geen mass-recompute, geen Vision-kosten). Zet één tenant hem aan, dan
   * roteert alleen díé tenant zijn cache.
   *
   * KOST NIETS per upload: puur deterministische vak-rekenkunde, geen extra
   * AI-call.
   */
  letterRegV2?: boolean
  /**
   * B9 — EUROPESE SCHRIFTEN (Cyrillisch, Grieks, Latin Extended).
   *
   * De keten is op vijf plekken Latijn-only, en elke plek verminkt of blokkeert
   * een Grieks of Bulgaars logo NÁDAT Vision het correct gelezen heeft. Met de
   * vlag aan:
   *   - de logo-OCR krijgt een prompt die expliciet verbiedt te translitereren
   *     én levert per element een `script`-veld (`latin|cyrillic|greek|other`);
   *   - de per-rij-terugval-OCR (`claude-ocr.ts`) saneert op Unicode-classes
   *     (`[^\p{L}\p{N}&'.,\-\s]`) in plaats van `[^A-Z0-9&'.\-\s]`, en forceert
   *     alleen nog HOOFDLETTERS in Latijnse context — "Пекарня" overleeft de
   *     sanering nu byte-gelijk in plaats van tot niets te worden gestript;
   *   - de pixel-letter-OCR (`letter-ocr.ts`, charset `A-Z&`) wordt voor
   *     niet-Latijnse rijen OVERGESLAGEN in plaats van er Latijnse letters op
   *     te gokken (dropLog-reden `'legacy-ocr-unsupported-script'`);
   *   - de NL-woordenboekcorrectie (`text-correct.ts`) slaat tekst met
   *     Cyrillische of Griekse tekens volledig over;
   *   - de case-correctie op vak-geometrie (`case-from-boxes.ts`) laat
   *     niet-Latijnse rijen met rust (Visions case is dan leidend);
   *   - de fontkeuze levert GEEN `suggestedFontId` meer wanneer geen enkel
   *     catalogusfont de rijtekst volledig kan zetten. De rij blijft dan
   *     outline-trace — de veilige stand, en dezelfde vangrail als B3.
   *
   * ONTBREEKT het veld (de normale situatie), dan is dit `false` en is elk van
   * die zes takken byte-identiek aan vóór dit bestekpunt: alle zes zitten
   * achter een strikte `=== true`.
   *
   * PROMPT_VERSION: de logo-OCR draagt TWEE promptversies naast elkaar en kiest
   * per request op deze vlag. De cachesleutel van de OCR-laag bevat de
   * promptversie, dus vlag-aan en vlag-uit delen nooit een entry — en de
   * vlag-uit-sleutel blijft byte-identiek aan die van vóór dit bestekpunt. Een
   * `TRACE_CACHE_VERSION`-bump hoort daarom pas bij fase C (default aan).
   *
   * NET ALS `sloganStripGuardV2` (B3), `ocrRasterV2` (B6), `textRowsV2` (B4),
   * `arcTextV2` (B2), `bboxRefineEnabled` (B5) en `letterRegV2` (B8) hoort deze
   * vlag WÉL in de trace-cachevingerafdruk (`buildTraceCacheKey`): hij verandert
   * de `suggestedText` (sanering/case), de `suggestedFontId` (dekkingspoort) en
   * daarmee de gecachete SVG. `settingsFingerprintOf` neemt alle settings mee
   * behalve de expliciet uitgezonderde velden, en deze staat daar niet tussen —
   * er is dus niets voor nodig. Optioneel veld ⇒ een afwezige sleutel valt in
   * `JSON.stringify` weg ⇒ elke tenant die hem niet zet houdt byte-identiek
   * dezelfde cachesleutel (geen mass-recompute, geen Vision-kosten).
   *
   * KOSTEN: de prompt wordt ~60 tokens langer ≈ $0,0002 per upload; verder €0.
   */
  scriptsEuropeV1?: boolean
  /**
   * LETTERVERVANGING V2 (plan 10-9-2026, §A) — de plaatsingsketen achter één
   * schakelaar.
   *
   * WAT DE VLAG AANZET. De bijschrift-strip laat de rij niet meer door de
   * kolomprojectie op het bronbeeld lopen, maar door de nieuwe, slot-vrije
   * keten: kolom-inktprofiel (ook ONDER de 400-px-rastervloer, opgeschaald
   * naar ≥ 160 px bandhoogte) → woordvakken uit de grote dalen → DP-snede met
   * het aantal tekens als HARDE randvoorwaarde → registratie met het anker
   * hoogte-óf-breedte. De uitkomst gaat als `placement` mee met de rij, en
   * klant, server-render en beoordelingsrender zetten er per constructie
   * dezelfde letters mee.
   *
   * WELK DEFECT DAT WEGNEEMT. Vandaag valt élke mismatch tussen het aantal
   * gemeten vakken en het aantal tekens terug op de rij-layout met GELIJKE
   * gaten: "VAN  PAASSEN" wordt één egaal gespatieerde regel en de
   * woordafstand van het logo is weg. Dat is letterlijk de klacht. Met de
   * vlag aan is de slechtste uitkomst een vak per WOORD — speling blijft
   * binnen een woord en kan de regel niet meer uitsmeren.
   *
   * ONTBREEKT het veld (de normale situatie), dan is dit `false` en is er
   * geen `placement` in de respons: de hele keten hangt achter een strikte
   * `=== true`, en zowel de server-trace als beide renders zijn byte-identiek
   * aan die van vóór dit bestekpunt. Daarom is er NU geen
   * `TRACE_CACHE_VERSION`-bump; die hoort in dezelfde commit als het moment
   * waarop de default omgaat.
   *
   * NET ALS `sloganStripGuardV2` (B3) c.s. hoort de vlag WÉL in de
   * trace-cachevingerafdruk (`settingsFingerprintOf` sluit hem niet uit): hij
   * verandert de rij-inhoud van de gecachete respons. Optioneel veld ⇒ een
   * afwezige sleutel valt in `JSON.stringify` weg ⇒ elke tenant die hem niet
   * zet houdt byte-identiek dezelfde sleutel.
   *
   * ENV-NOODREM: `LETTER_PLACEMENT_V2=off` (zie `lib/trace-route/
   * trace-flags.ts`) zet de keten uit voor álle tenants zonder deploy.
   *
   * KOST NIETS per upload: puur deterministisch reken- en meetwerk, geen
   * extra AI-call. Dat laatste is de aparte vlag hieronder.
   */
  letterPlacementV2?: boolean
  /**
   * AI-VERFIJNING VAN DE LETTER-VAKKEN (plan 10-9-2026, §A3) — de twijfelband.
   *
   * Alleen zinvol samen met `letterPlacementV2`. Komt de DP-segmentatie van
   * een rij in de band 0,35–0,6 uit, dan mag met deze vlag een gerichte
   * Vision-call per woord-crop de vakken overdoen; zonder de vlag valt die
   * band naar de woordvloer. Buiten de band verandert de vlag niets: boven
   * 0,6 is de segmentatie goed genoeg, onder 0,35 is er te weinig om te
   * verfijnen.
   *
   * KOST GELD per upload (≈ $0,004–0,010 per woord, gecapt per upload) —
   * daarom een aparte, expliciete tenant-knop en nooit een globale default,
   * zelfde regel als `bboxRefineEnabled` (B5).
   *
   * IN GOLF 0/1 IS DEZE VLAG NOG INERT: de verfijner zelf is WP4. Het
   * injectiepunt staat in `lib/trace-route/letter-placement-step.ts` en valt
   * zolang er geen verfijner geregistreerd is altijd naar de woordvloer —
   * de vlag aanzetten kost dan dus ook niets.
   */
  letterBoxesAiRefine?: boolean
  /**
   * PLAN VISION-KOSTEN, FASE 2 — het model van élke betaalde Vision-call van
   * deze tenant (logo lezen, bronlezing, rij-OCR, bbox, letters,
   * kwaliteitscheck, diff, adviseur). Afwezig ⇒ `STANDAARD_VISION_MODEL`
   * (`claude-sonnet-4-6`), en dan is elke request byte-gelijk aan vóór fase 2.
   *
   * KOST GELD en verandert de server-trace-output (een ander model leest
   * anders), dus het veld telt mee in de trace-cachevingerafdruk — behalve op
   * het standaardmodel, dat `cache-step.ts` eruit laat zodat die sleutel
   * byte-gelijk blijft. Register en regels: `./vision-model`.
   */
  visionModel?: VisionModelId
  /**
   * LAGE-RESOLUTIE-TEKSTREDDING — de enige vlag in deze reeks die DEFAULT AAN
   * staat; `false` is de noodrem.
   *
   * WAAROM DIT GEEN GEWONE UITROLVLAG IS. Alle poorten die tekst als outline
   * laten staan (`keepTextAsOutline`, de woordmerk-uitzondering
   * `isPrimaryBrand`) rusten op één aanname: dat de ORIGINELE lettervormen het
   * ontwerp zijn en dus de moeite waard om te behouden. Die aanname is een
   * uitspraak over het BRONBEELD, niet over het bord. Meet de letterhoogte in
   * BRON-pixels en hij valt onder een handvol pixels, dan bestaan die
   * lettervormen niet meer: wat de trace dan overtrekt is JPEG-ruis rond een
   * uitgesmeerde vlek, en het resultaat is de dubbellijnige brij die de klant
   * meldt (Van Paassen-casus: woordmerk ~20 bron-px, bijschrift ~11 bron-px op
   * een 277 px-thumbnail). Voor zulke rijen is een schoon single-line-font
   * ALTIJD beter — ook voor het woordmerk, want er is geen merkvorm meer om te
   * beschermen.
   *
   * De redding is dus geen versoepeling van de poorten maar een correctie op
   * hun vooronderstelling, en daarom hoort hij default aan: hij vuurt alleen
   * waar het alternatief aantoonbaar onbruikbaar is. De drempel
   * (`LOWRES_RESCUE_MAX_SRC_PX`, `lib/trace-route/slogan-step.ts`) is geijkt op
   * de 100-logo-testset zodat geen enkel hoog-resolutie-logo hem raakt.
   *
   * CACHE: de vlag verandert de server-trace-output (welke rijen gestript
   * worden) en telt dus mee in de trace-cachevingerafdruk. Omdat hij default
   * AAN gaat, verandert de output voor élke tenant — vandaar de
   * `TRACE_CACHE_VERSION`-bump naar 66 in dezelfde wijziging.
   */
  lowresTextRescue?: boolean
  /**
   * WOORDMERK-MAATPOORT — de merkrij-uitzondering geldt voortaan alléén nog
   * voor een woordmerk dat FYSIEK GROOT GENOEG is. Default AAN; `false` is de
   * noodrem terug naar het absolute gedrag.
   *
   * WAT ER MIS WAS. De uitzondering "de merk-lettervormen ZIJN het ontwerp"
   * (eigenaars-besluit 2026-07-15) was ABSOLUUT: `checkFontReplacement`
   * weigerde een `isPrimaryBrand`-rij ongeacht de bordmaat. Dat klopt op een
   * groot bord — "met Van Paassen wil je normaal altijd trace, zo veel
   * mogelijk" — maar het is een uitspraak over LETTERVORMEN DIE MAAKBAAR ZIJN.
   * Op een klein bord houdt die maakbaarheid op: hetzelfde woordmerk dat op
   * 100 cm een prachtige dubbele contour geeft, is op 30 cm een rij letters van
   * ~35 mm op een buis van 8 mm. Twee contourlijnen van 8 mm passen daar niet
   * naast elkaar; wat de klant krijgt is de dubbellijnige brij die de
   * maatpoort voor élke andere tekstrij al jaren voorkomt.
   *
   * DE REGEL. Boven `outlineTextMinHeightFactorVsTube × buisdikte` blijft de
   * uitzondering onverkort gelden (woordmerk = trace, precies zoals nu).
   * Daaronder wordt de merkrij behandeld als elk ander bijschrift: dezelfde
   * slogan-vlaggen, dezelfde strip-poorten, dezelfde per-letter-registratie,
   * en dus single-line fontletters. GEEN NIEUWE DREMPEL: dit is exact de
   * bestaande fysieke maatlat, nu ook toegepast op de rij die er tot nu toe
   * per definitie buiten viel. Een tenant die de grens verlegt (per-tenant
   * `outlineTextMinHeightFactorVsTube`) verlegt hem hiermee automatisch mee.
   *
   * ONTBREEKT DE METING (`inkHmm` afwezig of 0), dan HANDHAAFT de poort de
   * uitzondering: niet-meten is nooit een reden om merkvormen te verwijderen.
   * Dezelfde fail-safe als overal in deze keten.
   *
   * VERHOUDING TOT `lowresTextRescue`: de redding is de STERKERE opzij-zetter
   * en gaat vóór. Die zegt "de bron draagt geen lettervorm meer, dus er is
   * niets te beschermen" — dat geldt ongeacht de bordmaat, ook boven de grens.
   * Deze poort zegt "de vorm bestaat wél, maar past niet op dit bord". Beide
   * heffen de uitzondering op, om verschillende redenen; de volgorde staat in
   * `computeSloganFlags` (`lib/trace-route/slogan-step.ts`).
   *
   * CACHE: de vlag verandert de server-trace-output (welke rijen gestript
   * worden) en telt mee in de trace-cachevingerafdruk. Omdat hij default AAN
   * gaat, verandert de output voor élke tenant met een klein bord — vandaar de
   * `TRACE_CACHE_VERSION`-bump naar 68 in dezelfde wijziging.
   */
  wordmarkSizeGate?: boolean
  /**
   * B6 (18-8, eenheden-ronde) — DE STRIP-POORTEN TELLEN IN LETTERS, NIET IN
   * PADEN. Default AAN; `false` is de noodrem.
   *
   * WAT ER MIS WAS. Twee poorten in `applySloganStrip` vergelijken een
   * GLYPH-telling met een PAD-telling: de dekkingspoort ("verdwijnt er meer
   * vorm dan de tekst terugtekent?") en de overclaim-poort ("claimt deze rij
   * meer paden dan haar tekst kan hebben opgeleverd?"). Beide leunen op de
   * aanname "één letter ≈ één pad". Die aanname geldt alleen voor de
   * PERIMETER-tak: daar ligt het gat van een `o` genest in zijn outer en houdt
   * `splitTraceComponents` de letter bij elkaar (gemeten: 17 paden voor 17
   * glyphs). In de SKELETON-tak geldt zij niet — multi-color-trace voegt alle
   * middenlijn-takken van één letter samen tot één multi-subpad-pad, maar die
   * takken zijn niet genest, dus de splitsing trekt ze weer los (gemeten: 33
   * paden voor diezelfde 17 glyphs). De poorten vergeleken dan appels met
   * peren en vuurden vals: `17 >= 33 × 0,75` is onwaar, dus ging de HELE strip
   * terug en tekende het bord de wiebelige zelfgemaakte middenlijn in plaats
   * van het catalogusfont.
   *
   * WAT DE VLAG DOET. Aan: beide poorten tellen EENHEDEN — het aantal
   * onderscheiden `pathSourceComponents` (≈ letters) in plaats van paden, aan
   * beide kanten van de vergelijking. De drempels (0,75 respectievelijk
   * `3n+2`) blijven onveranderd; alleen de eenheid verandert. Uit: exact de
   * padtelling van vóór dit bestekpunt.
   *
   * WAAROM DEFAULT AAN. De vlag repareert een MEETFOUT, geen smaakkwestie: de
   * poorten meten sinds hun invoering iets anders dan wat hun eigen docstring
   * zegt te meten. Een vlag-uit-default zou betekenen dat elke tenant tot hij
   * hem zet de valse terugdraaiing blijft krijgen.
   *
   * ONTBREEKT `pathSourceComponents` (pure-SVG-pad, gecachete respons, de
   * losse stap-tests), dan valt de telling terug op identiteit — één pad is
   * dan één eenheid en het gedrag is per constructie exact dat van vandaag,
   * ongeacht deze vlag.
   *
   * NOODREM ZONDER DEPLOY: `TRACE_SLOGAN_UNITS=paths` doet hetzelfde als
   * `false`, zodat productie kan terugvallen zonder settings-wijziging.
   *
   * Zoals elke vlag die de server-trace-OUTPUT verandert telt hij mee in de
   * trace-cachevingerafdruk; omdat hij default AAN gaat vangt die vingerafdruk
   * de wijziging per definitie niet en hoort er een `TRACE_CACHE_VERSION`-bump
   * bij (v70).
   */
  sloganStripUnitsV3?: boolean
  /**
   * B11 (18-8) — EEN HERKEND BIJSCHRIFT WORDT ALTIJD VERVANGEN. Default AAN;
   * `false` is de noodrem.
   *
   * EIGENAARSREGEL (Pascal, 18-8, chat): "Zorg je dat die nooit meer zelf zo'n
   * wiebelige lijn tekent als de ai hem herkent als bijschrift en altijd gewoon
   * juist vervangt?" Dat is een productregel, geen drempel: herkent Vision een
   * niet-woordmerk-rij als tekst en kan het catalogusfont hem zetten, dan komt
   * hij als `renderMode:'font'` terug — ongeacht de bordmaat.
   *
   * WAT DE VLAG DOET. De maatpoort `outlineTextMinHeightFactorVsTube`
   * ("groot genoeg → outline houden") en de dikte-poort erna gelden voortaan
   * UITSLUITEND voor WOORDMERK-rijen; die houden hun eigen maatpoort van
   * V1.250.0 (`wordmarkSizeGate`) onveranderd. Voor bijschriften vervallen
   * beide. Daarmee verdwijnt het randgebied waarin een bijschrift op een groot
   * bord (boven ~199,9 cm) een zelfgetekende centerline hield.
   *
   * WAT ER NIET VERVALT — en dat is de reden dat dit een eigen vlag is en geen
   * `if (true)`: de poorten die over HERKENNING gaan blijven staan. Een rij van
   * ≤ 2 tekens zonder hoge OCR-zekerheid (hallucinatie-guard) en een rij waarvan
   * de paden ver buiten haar eigen band reiken (fragmentatie-guard) zijn niet
   * "herkend als bijschrift" — daar spreekt de meting de tekst-hypothese tegen.
   * Zij blijven outline, met hun bestaande reden in de dropLog.
   *
   * EIGEN VLAG EN NIET MEELIFTEN OP `sloganStripUnitsV3`: dat zijn twee
   * onafhankelijke assen (tel-eenheid versus poort-bereik) en één noodrem voor
   * beide zou betekenen dat terugvallen op de padtelling ongemerkt ook de
   * bijschrift-invariant uitzet. Noodrem zonder deploy:
   * `TRACE_CAPTION_ALWAYS_FONT=off`.
   *
   * Verandert de server-trace-OUTPUT voor élke tenant (default aan) ⇒ zelfde
   * `TRACE_CACHE_VERSION`-bump (v70).
   */
  captionAlwaysFont?: boolean
  /**
   * Minimum cut-clearance (cm). Narrow inward notches in the plate contour
   * smaller than this distance are filled in via morphological closing —
   * keeps the cutter from slicing thin "necks" the laser/CNC can't manage.
   */
  minCutClearanceCm: number
  /**
   * Hoe krap deze werkplaats een buis kan buigen, uitgedrukt in buisdiktes.
   *
   * Een ONBEWERKTE buis haalt ongeveer 2× zijn eigen diameter: het materiaal
   * aan de binnenkant van de bocht moet stuiken en knikt daaronder. Kan de
   * werkplaats INSNIJDEN — de binnenbocht inkepen zodat het materiaal daar mag
   * opvouwen — dan kan het krapper, tot ruwweg 1×. Dat is dus geen natuurwet
   * maar vakmanschap, en hoort daarom per maker instelbaar te zijn.
   *
   * Ontbreekt dit veld, dan geldt de veilige vuistregel 2 (`DEFAULT_BEND_FACTOR`
   * in `packages/pricing/src/bend-radius.ts`). Te laag zetten zonder werkelijk
   * te kunnen insnijden levert een tekening op die de eigen werkplaats niet kan
   * maken: deze radius staat als belofte in de stuklijst, de DXF en de CAM-SVG.
   */
  bendRadiusFactorVsTube?: number
  /**
   * Vaste ondergrens (mm) voor de buigradius, los van de buisdiameter.
   * De effectieve buigradius is max(factor × buisdiameter, minBendRadiusMm).
   * Optioneel: ontbreekt dit veld, dan valt de afleiding terug op
   * bendRadiusMm en anders op de ingebouwde default. Puur per-tenant
   * tuning — de afleiding uit de buisdiameter blijft de hoofdregel.
   */
  minBendRadiusMm?: number
  /**
   * Legacy/handmatige minimum-buigradius (mm). Wordt nu gebruikt als
   * ondergrens-fallback in de fysieke afleiding (zie lib/bend-radius.ts);
   * de zichtbare buigradius schaalt voortaan mee met de buisdiameter.
   */
  bendRadiusMm: number
  /**
   * Shape regularization defaults — production-tuning admins can enable
   * this so traced paths that "look like" a circle/line/arc/rect snap to
   * the perfect primitive. Critical for cleaning up wobbly traces from
   * blurry raster uploads.
   */
  shapeRegularize?: {
    enabled: boolean
    sensitivity: number
  }
  /**
   * Pad-uittekenen: tekent elk getraced pad na met strakke lijn/boog/
   * cirkel-primitieven (segment-niveau) voordat er neon van gemaakt
   * wordt. Hobbelige rondjes worden perfecte cirkels, golvende rechte
   * stukken strakke lijnen. Default aan.
   */
  pathBeautify?: {
    enabled: boolean
    sensitivity: number
  }
  /** Contour-cut snap-to-primitive toggle default. */
  contourSnap?: {
    enabled: boolean
    sensitivity: number
  }
  /**
   * Auto-monoline switch. For caption-scale text where the letter
   * stroke would be too thin to take TWO parallel neon tubes, the
   * trace pipeline re-renders the WHOLE ROW as single-line skeletons
   * so the result reads as a clean centerline tube per stroke.
   *
   *   minStrokeFactorVsTube — physical letter-stroke thickness must be
   *     ≥ this × tube diameter for the row to keep its perimeter
   *     (double-outline) trace.
   *
   *     GEKOPPELD AAN DE BUIGFACTOR (eigenaarsregel 19-8: "pas single
   *     line als de outline niet maakbaar is"). Outline is onmaakbaar
   *     zodra de streek smaller is dan 2 × de buigradius, en de
   *     buigradius is `bendRadiusFactorVsTube × buis`. Houd deze factor
   *     dus op 2 × bendRadiusFactorVsTube: met insnijden (buigfactor 1,
   *     sinds 18-8) is dat 2; gaat het insnijden ooit terug naar
   *     buigfactor 2, dan deze mee terug naar 4. De oude live-waarde 4
   *     was precies dat: een fossiel van de oude buigfactor.
   *   hysteresisFactor — when the user resizes near the switch point,
   *     widen the threshold by this factor in whichever direction
   *     would cause the mode to STAY the same. Prevents flicker
   *     during slider drag.
   *   debug — when true, the trace API emits per-row decision data
   *     in the response so the configurator can render a debug
   *     overlay (measured stroke mm vs threshold vs chosen mode).
   */
  monolineSwitch?: {
    enabled: boolean
    minStrokeFactorVsTube: number
    hysteresisFactor: number
    debug?: boolean
  }
  /**
   * Print-on-top routing. Per-path classifier (`lib/print-classifier.ts`)
   * decides whether a traced path can survive as a glass neon tube or
   * should be UV-printed flat onto the back plate. Factors below tune
   * the manufacturability thresholds; `autoAccept` routes flagged paths
   * straight to print without a user banner click.
   */
  printOnTop?: {
    enabled: boolean
    minLengthFactorVsTube: number
    minSideFactorVsTube: number
    bendRadiusFactor: number
    autoAccept: boolean
    /**
     * C6 — RIJ-CONSISTENTIE 2.0.
     *
     * Zet de tweede generatie van `applyRowConsistency` aan
     * (`apps/runtime/lib/editor/print-routing.ts`):
     *   - rijlidmaatschap telt alleen ZICHTBARE paden (`effectiveHidden`-filter),
     *     zodat de al verborgen originelen van een font-vervanging de
     *     printers-teller niet meer opblazen;
     *   - een GEMENGDE rij met ≥ 3 leden wordt uniform: ≥ 75% print-willers ⇒
     *     de hele rij print, anders ⇒ de hele rij neon. Bij de oude 50%-grens
     *     kantelde een woord te makkelijk half; 75% betekent "driekwart van de
     *     glyphs is fysiek onmaakbaar", en dan is heel-print de eerlijke
     *     uitkomst. Rijen met < 3 leden houden hun per-pad-beslissing (een los
     *     symbool naast één letter is geen woord);
     *   - `isPrimaryBrand` blijft onveranderd ALTIJD neon;
     *   - de font-stroke-uitsluiting is gecentraliseerd: elk pad dat als
     *     caption-/font-glyph is ingevoegd staat hard op `shouldPrint: false`
     *     vóórdat suggesties, `minWidthCmForAllNeon` en de klant-badges rekenen.
     *
     * ONTBREEKT het veld of staat het op `false` (de normale situatie), dan is
     * `applyRowConsistency` byte-identiek aan het gedrag van V1.166.0. Bewust
     * een tenant-instelling en geen env-vlag: de beslissing valt in de BROWSER,
     * waar `process.env` niet bestaat, en de uitrol is per-tenant (default uit →
     * één tenant aan na corpus-review → default aan).
     *
     * Deze vlag zit BEWUST NIET in de trace-cachevingerafdruk (zie
     * `buildTraceCacheKey`), om exact dezelfde reden als `traceCleanupV2`: hij
     * verandert niets aan de server-trace-output.
     */
    rowConsistencyV2?: boolean
    /**
     * C7 — MERKTEKEN-VAK OP OVERLAP-FRACTIE.
     *
     * Vervangt het criterium "bbox-CENTRUM binnen het met 25% opgeblazen
     * ®-vak" door "≥ 70% van de pad-bbox ligt binnen het met 10% gepadde vak"
     * (gedeelde helper `trademarkOverlapFraction` /
     * `pathMatchesTrademarkBox` in `apps/runtime/lib/print-visibility.ts`,
     * gebruikt door ZOWEL `derivePrintDecisions` als `trademarkHiddenIndices`).
     *
     * Waarom: een merkteken-vak is klein, en 25% opblazen zet er een brede
     * halo omheen. Een letterpoot of accent strak naast de ® heeft zijn
     * centrum dan al snel binnen die halo en wordt platgedrukt naar UV-print
     * — of, met print-op-plaat UIT, volledig verwijderd — terwijl het overgrote
     * deel van dat pad buiten het vak ligt. De overlap-fractie meet hoeveel van
     * het pad écht in het vak ligt: het symbool zelf ≥ 95%, een buurletter
     * typisch < 40%; 0,7 laat naar beide kanten marge en de 10% padding vangt
     * de jitter van de Vision-bboxdetectie.
     *
     * ONTBREEKT het veld of staat het op `false` (de normale situatie), dan
     * blijft het oude criterium exact zoals het was.
     *
     * Net als `rowConsistencyV2` een tenant-instelling en geen env-vlag (de
     * beslissing valt in de BROWSER), en om dezelfde reden BEWUST buiten de
     * trace-cachevingerafdruk (`settingsFingerprintOf` in
     * `lib/trace-route/cache-step.ts`): de server-trace-output is in beide
     * standen identiek.
     */
    trademarkOverlapV2?: boolean
    /**
     * C8 — MAAT-INVARIANTE CLASSIFIER-SAMPLING.
     *
     * De drie print-regels zijn fysiek (mm), maar de MEETLAT waarmee de
     * classifier ze toetste was dat niet: `samplePoints` klemde op 200 punten
     * en het drie-punts-venster liep met `floor(n/50)`, dus élk pad werd op
     * ~50 meetpunten beoordeeld — 20 mm of 2 m, dat maakte niet uit. Met de
     * vlag aan (`packages/pricing/src/print-classifier.ts`):
     *   - één sample per 1,5 mm FYSIEK, geklemd op 32–4000 punten;
     *   - het drie-punts-venster beslaat ~een halve buisdikte, ongeacht
     *     padlengte;
     *   - `SUSTAINED_TURN_DEG` blijft 200 — die drempel klopt pas écht zodra
     *     het venster fysiek constant is.
     *
     * Waarom dit ertoe doet: op een groot pad werd kromming van kleine details
     * gemist, en omgekeerd kon een reeks STOMPE hoeken (zigzag, gekartelde
     * rand) cumulatief de 200° halen zodra het meegroeiende venster ongeveer
     * een halve golfperiode besloeg — een grote letter werd dan onterecht
     * `tight-bend` en plat gedrukt.
     *
     * ONTBREEKT het veld of staat het op `false` (de normale situatie), dan
     * meet de classifier byte-identiek aan de situatie vóór C8.
     *
     * Net als `rowConsistencyV2` en `trademarkOverlapV2` een tenant-instelling
     * en geen env-vlag (de beslissing valt in de BROWSER), en om dezelfde reden
     * BEWUST buiten de trace-cachevingerafdruk (`settingsFingerprintOf` in
     * `apps/runtime/lib/trace-route/cache-step.ts`): de server-trace-output
     * is in beide standen identiek — deze vlag beslist alleen hoe de EDITOR de
     * al getraceerde paden classificeert.
     */
    samplingV2?: boolean
    /**
     * C8 — SCHADUWMETING van `samplingV2` (geen productie-schakelaar).
     *
     * Staat dit veld aan, dan rekent de editor BEIDE samplingvarianten door en
     * meldt hij elke kantelende beslissing als client-KPI
     * (`print-sampling-flip` / `print-sampling-shadow`, zie
     * `apps/runtime/lib/client-kpi.ts`). Wat de klant ziet, blijft de
     * uitkomst van `samplingV2` zelf — de schaduw wijzigt geen enkel bord.
     *
     * Bedoeld voor de meetweek vóór de flip: de eigenaar ziet dan op echte
     * uploads hoeveel en welke beslissingen C8 verschuift. Bewust een eigen
     * veld: schaduw kost een tweede klassificatie per herberekening, en die
     * rekening hoort alleen bij de tenant te liggen die aan het meten is.
     * Ontbreekt het veld (de normale situatie), dan draait er precies één
     * klassificatie — exact zoals vóór C8.
     */
    samplingShadow?: boolean
    /**
     * C9 — TARIEF VOOR DE UV-PRINT OP DE PLAAT (€ per cm²), default `0`.
     *
     * Een naar print gerouteerd detail is geen buis maar inkt: het kost geen
     * strekkende meter LED-neon maar OPPERVLAKTE op de plaat. Dit veld is het
     * tarief waarmee `calculatePrice` die oppervlakte in rekening brengt
     * (`printAreaCm2 × printPricePerCm2` → `PriceResult.printPrice`).
     *
     * Waarom default 0: het invullen van een tarief is een COMMERCIEEL besluit
     * van de eigenaar, geen technische migratie. Zolang het veld 0 (of afwezig)
     * is, verschijnt er geen `printPrice` in `PriceResult` en verandert er geen
     * cent aan een bestaande offerte — de sleutel wordt dan niet eens
     * geschreven, zodat elke bestaande serialisatie (offerte-JSON, share-URL,
     * order-mail, golden-snapshots) byte-identiek blijft.
     *
     * ONAFHANKELIJK van `pricing.excludePrintedFromTube`: dat veld haalt de
     * print-paden UIT de buismeters, dit veld zet er een eigen prijs voor
     * TERUG. De eigenaar kan ze los van elkaar aanzetten — en hoort dat ook te
     * kunnen, want het zijn twee aparte besluiten (stoppen met buis rekenen /
     * beginnen met inkt rekenen).
     *
     * Net als de andere C6/C7/C8-velden BEWUST buiten de
     * trace-cachevingerafdruk (`settingsFingerprintOf` in
     * `apps/runtime/lib/trace-route/cache-step.ts`): een prijs verandert
     * de server-trace-output niet.
     */
    printPricePerCm2?: number
  }
  /**
   * Caption auto-apply. When a caption row comes back from trace with
   * one or more OCR candidates, pick the best one deterministically and
   * replace the wobbly trace with clean Hershey-font glyphs immediately
   * — no side-by-side compare view. The user can still open the manual
   * RowPanel via a "Wijzig" button to override.
   *
   *   preferEngine — 'claude' trusts Claude Vision first; 'tesseract'
   *     trusts Tesseract first; 'dictionary-first' picks whichever
   *     candidate matches the dictionary-corrected form.
   */
  captionAutoApply?: {
    enabled: boolean
    preferEngine: 'claude' | 'tesseract' | 'dictionary-first'
  }
  /**
   * Watts-per-metre per tube thickness — drives the power-draw estimate
   * and driver-size recommendation. Adjust to match the actual LED rolls
   * you ship.
   */
  tubeWattsPerMeter?: Partial<Record<TubeThicknessMm, number>>
  /**
   * Threshold above which a detected text row qualifies as a primary brand
   * name (glyphHeight / viewBoxHeight). Above this ratio, the UI hides
   * font-replacement controls and de print-routering stelt de paden van de
   * rij vrij van SOLO-auto-print (zie `isPrimaryBrand` hierboven). Default
   * 0.18 — "clearly larger than body text".
   */
  brandHeightRatio?: number
  /**
   * Welke productie-bestanden in de order-mail meegaan, als CSV van
   * format-codes (de bestaande `exportFormats`-representatie, bv.
   * `"svg,dxf,cdr,preview-png"`). Het maakbedrijf kiest dit via vinkjes in de
   * admin-instellingen; de order-mail-route (`/api/export-design`) filtert de
   * bijlages hierop. Codes + extensie-mapping staan in `lib/export-formats.ts`.
   * Ontbreekt het veld, dan vallen UI en route terug op "alles aan".
   */
  exportFormats?: string
  /**
   * Order-flow van de configurator (taak E — "naar winkelwagen").
   *
   *   - `type: 'mail-quote'`      → bezoeker vraagt een offerte aan per e-mail.
   *   - `type: 'cart-webhook'`    → "Naar winkelwagen"-knop POST't het ontwerp
   *     + prijs naar de eigen webshop van het maakbedrijf (`webhookUrl`),
   *     HMAC-ondertekend met de webhook-secret. De webshop bouwt de cart en
   *     regelt de betaling; de configurator stuurt de bezoeker door naar de
   *     teruggegeven `redirect_url`.
   *   - `type: 'direct-download'` (of leeg) → de bestaande download-knoppen.
   *
   * In productie is dit per-tenant ingesteld in het klantenportaal
   * (`software_configs.config.order`). Deze lokale representatie is bedoeld
   * voor de single-tenant admin + testen. De **webhook-secret hoort NIET in
   * deze lokale settings/JSON** (nooit committen) — die blijft portaal-only;
   * hier staat alleen de mode + de publieke webhook-URL.
   */
  order?: {
    type?: 'mail-quote' | 'cart-webhook' | 'direct-download'
    webhookUrl?: string
    buttonLabel?: string
    successMessage?: string
  }
  rooms: Room[]
  defaultRoomId: string
  allowMultipleRooms?: boolean
  /**
   * Bewaartermijn (dagen) van het trace-archief voor deze tenant. Optioneel;
   * ontbreekt het veld dan geldt de default van 30 dagen. De op het moment van
   * archiveren geldende waarde gaat in elk trace-record en wordt door de
   * retentie-sweep gerespecteerd. Geklemd op 1–365 dagen (zie
   * clampRetentionDays in packages/ocr/trace-archive.ts). AVG: een kortere
   * retentie betekent minder lang persoonsgegevens (geüploade logo's) bewaren.
   */
  retentionDays?: number
  /**
   * Eenhedenstelsel voor de klant-presentatie van maten (B10 / D-E2).
   * Optioneel; ontbreekt het veld dan geldt 'metric' (bestaand gedrag,
   * zie DEFAULTS). 'imperial' toont decimale inches — puur presentatie,
   * de interne cm/mm-pijplijn en de productie-exports blijven metrisch.
   */
  unitSystem?: UnitSystem
  /**
   * Font-schakelaars (eigenaarsopdracht 18-8-2026): catalogus-font-ids die
   * DEZE tenant heeft uitgezet in het portaal (`config.fonts`, checkbox per
   * font — zie `apps/runtime/lib/fonts-config.ts` voor de sectievorm).
   * Een uitgezet font verdwijnt uit de font-kiezer en wordt server-side nooit
   * meer als `suggestedFontId` voorgesteld; blijft er geen kandidaat over dan
   * blijft de rij gewone outline-trace (er valt nooit iets van het bord).
   * Al toegepaste vervangingen met een later uitgezet font blijven staan.
   *
   * OPTIONEEL en alleen aanwezig wanneer er écht iets uitstaat: het veld telt
   * BEWUST mee in de trace-cachevingerafdruk (`settingsFingerprintOf` in
   * `cache-step.ts` — de server-suggesties veranderen erdoor, dus een
   * gecachete respons uit de andere stand is een ander bord), en "afwezig"
   * houdt de sleutel byte-identiek voor iedereen die niets uitzet — dezelfde
   * regel als `sloganStripGuardV2` c.s.
   */
  disabledFontIds?: string[]
  /** Centrally managed display labels; stable font ids and glyph paths do not change. */
  fontNames?: Record<string, string>
  /** Preferred visitor font, constrained by the currently approved catalogue. */
  visitorDefaultFontId?: string
  /**
   * Fontbeheer-staat van het PORTAAL (plan 10-9-2026, §C1). Puur weergave van
   * de beheerpagina: `verwijderd` zijn de fonts die achter het tabblad
   * "Verwijderd" liggen (terug te halen), `volgorde` is de kaartvolgorde.
   *
   * DIT VELD STUURT DE APP NIET. Of een font gebruikt mag worden staat
   * uitsluitend in de `fonts`-sectie (`fonts[id] = false` →
   * `disabledFontIds` hierboven); het portaal schrijft bij "verwijderen"
   * allebei tegelijk, zodat de app ook zonder dit veld exact hetzelfde doet.
   * Daarom staat het BUITEN de trace-cachevingerafdruk (`fontsBeheer:
   * undefined` in `settingsFingerprintOf`, `cache-step.ts`): kaartjes
   * herordenen mag geen hertrace van het hele archief kosten.
   */
  fontsBeheer?: {
    verwijderd: string[]
    volgorde?: string[]
  }
  /**
   * Per-tenant afzenderadres voor tenant-context-mails (B10 / D-E7, fase 1).
   * Bijv. `offerte@klantnaam.de` — het eigen domein van de tenant. Wordt
   * UITSLUITEND gebruikt wanneer het domein van dit adres in Resend status
   * 'verified' heeft (DKIM/SPF via DNS-records die de klant zet); anders valt
   * de mail stil terug op de platform-afzender (`MAIL_FROM`). Zie
   * `apps/runtime/lib/email-sender.ts` (resolveSenderAddress). Puur
   * mail-presentatie: hoort NIET in de trace-cachevingerafdruk (zelfde regel
   * als `unitSystem`/`retentionDays` — zie `settingsFingerprintOf`).
   * adminOnly in het portaal: een verkeerd gezet adres is klant-zichtbare
   * schade (mails van een niet-geverifieerd/verkeerd domein).
   */
  emailFromAddress?: string
  /**
   * Neon Studio (uitvoerplan v4) — het tekst-eerst product naast de logo-flow.
   * Per-tenant ingesteld in het portaal (`software_configs.config.studio`) en
   * geflattend door `lib/tenant-settings.ts`. Ontbreekt het veld, dan gelden de
   * defaults uit `lib/studio/config.ts` (`enabled: false` → bestaande tenants
   * merken niets).
   */
  studio?: StudioConfig
  /**
   * Gebruiksstand van dít product in deze embed (plan 9-9-2026). Ontbreekt het
   * veld → `GEBRUIK_CONFIG_DEFAULTS` (`stand: 'volledig'`, `prijs: 'tonen'`),
   * dus letterlijk het gedrag van vandaag.
   *
   * HOORT NIET IN DE TRACE-CACHEVINGERAFDRUK, en dat is geen detail maar de
   * hele reden dat het veld optioneel is: `gebruik` beslist welke PANELEN de
   * browser toont en of er bedragen bij staan — de server traceert in elke
   * stand letterlijk dezelfde paden. Zou het meetellen, dan kostte het
   * omzetten van een instellingsknop een volledige hertrace van élk gecachet
   * logo van die tenant, Vision-facturen incluis. Uitgesloten in
   * `settingsFingerprintOf` (`lib/trace-route/cache-step.ts`), zelfde regel als
   * `editor`/`pricing`.
   */
  gebruik?: GebruikConfig
  /**
   * Levertijd-tiers (uitvoerplan v4, taak CV1 — config-contract A-CV2).
   * Ontbreekt het veld → `DELIVERY_CONFIG_DEFAULTS` (`enabled: false`), dus
   * bestaande tenants zien geen tier-keuze en betalen geen toeslag.
   */
  delivery?: DeliveryConfig
  /**
   * Upsells (uitvoerplan v4, taak CV3 — config-contract A-CV2).
   * Ontbreekt het veld → `UPSELLS_CONFIG_DEFAULTS` (`enabled: false`).
   */
  upsells?: UpsellsConfig
  /** Optional visitor selling choices, independent of legacy upsells. */
  /** Merchant presentation order; canonical normalization lives in customer-layout. */
  customerLayout?: import("./customer-layout").CustomerLayout
  salesOptions?: SalesOptionsConfig
  /** Optional RGB visitor choice; absent means disabled. */
  rgbOption?: RgbOptionConfig
  /**
   * Kortingscodes (uitvoerplan v4, taak CV2 — A-CV3). De CODES zelf staan in
   * de database (`discount_codes`), niet hier; dit is alleen de vlag die het
   * portaal aanzet zodra er ≥1 actieve code is, zodat de configurator het
   * kortingsveld pas toont als er iets in te vullen valt.
   */
  discounts?: DiscountsConfig
  /**
   * Maakbaarheids-badges voor de klant (uitvoerplan v4, taak CV4). Ontbreekt
   * het veld → `QUALITY_CONFIG_DEFAULTS` (`customerBadges: true`). Bewust
   * default AAN; zie de toelichting bij `QualityConfig`.
   */
  quality?: QualityConfig
  /**
   * 24-uurs-designhulp (uitvoerplan v4, taak WF3). Ontbreekt het veld →
   * `DESIGN_HELP_CONFIG_DEFAULTS` (`enabled: true` sinds C-E2), dus kaart,
   * knop en aanvraag-kanaal staan open tot de maker ze met een expliciete
   * `enabled: false` dichtzet.
   */
  designHelp?: DesignHelpConfig
  /**
   * AI-ontwerpadviseur voor de eindklant (uitvoerplan v4, taak GR3). Ontbreekt
   * het veld → `ADVISOR_CONFIG_DEFAULTS` (`customerEnabled: false`): de
   * adviseur blijft precies wat hij was, een admin-gereedschap. De
   * admin-toegang hangt NIET aan dit veld.
   */
  advisor?: AdvisorConfig
  /**
   * C12 — omkeerbare editor-overrides. Ontbreekt het veld →
   * `EDITOR_CONFIG_DEFAULTS` (`reversibleOverridesV2: false`), dus exact de
   * bestaande code-paden. Zie `EditorConfig`.
   */
  editor?: EditorConfig
  /**
   * C9 — PRIJSMODEL-SCHAKELAARS. Ontbreekt de sectie (de normale situatie),
   * dan rekent de configurator exact zoals vóór dit bestekpunt.
   *
   * HUISREGEL VOOR DEZE SECTIE — hier horen UITSLUITEND knoppen in die in de
   * BROWSER rekenen, op een al getraceerd ontwerp. De hele sectie is namelijk
   * uit de trace-cachevingerafdruk gefilterd (`settingsFingerprintOf` in
   * `apps/runtime/lib/trace-route/cache-step.ts`) zodat een tenant die
   * een prijsknop omzet niet zijn complete cache — inclusief Vision-facturen —
   * opnieuw hoeft te verdienen. Een veld dat de SERVER-trace stuurt hoort dus
   * NIET in `pricing`, want dan zou die filter een cache-hit uit de verkeerde
   * stand serveren.
   */
  pricing?: {
    /** Public price presentation; missing preserves the legacy detailed view. */
    displayMode?: NeonPriceDisplayMode
    /**
     * D6 — DE VALUTA WAARIN DEZE TENANT PRIJST. Default (veld afwezig) `'EUR'`.
     *
     * Puur WEERGAVE: elk bedrag in de engine is en blijft een getal in de munt
     * van de tenant, er wordt nergens omgerekend (zie `format-money.ts`). Een
     * Zwitserse maker die `pricePerMeterLed: 10` invult verkoopt voor 10 CHF
     * per meter; alleen het teken en de notatie eromheen veranderen.
     *
     * WAAROM DIT VELD IN `pricing` HOORT EN NIET ELDERS — deze hele sectie
     * valt buiten de trace-cachevingerafdruk (`settingsFingerprintOf`), en dat
     * is hier precies goed: de valuta raakt geen enkele server-trace-uitkomst
     * (geen pad, geen kleur, geen tekstrij, geen Vision-call). Een maker die
     * van EUR naar CHF omzet hoort zijn complete cache — inclusief betaalde
     * Vision-resultaten — te BEHOUDEN. Zat het veld buiten deze sectie, dan
     * zou één muntwissel de hele cache van die tenant weggooien voor een
     * verandering die letterlijk alleen een teken op het scherm is.
     *
     * De huisregel van de sectie ("uitsluitend knoppen die in de BROWSER
     * rekenen op een al getraceerd ontwerp") houdt daarmee stand: dit is een
     * weergave-knop op een al berekende prijs.
     */
    currency?: Currency
    /**
     * C9 — HAAL PRINT-PADEN UIT DE BUISPRIJS. Default `false`.
     *
     * Een naar UV-print gerouteerd detail wordt vandaag als gebogen neonbuis
     * geprijsd (`sumVisibleLength` filtert alleen `effectiveHidden`) én telt
     * als apart bedraad tube-segment (`countVisibleTubes`), terwijl de preview
     * het als platte inkt tekent en de maker er nooit een buis voor buigt. Met
     * deze vlag aan tellen print-paden niet meer mee in `visiblePathLengthPx`
     * en niet meer als tube.
     *
     * Waarom achter een vlag en niet direct: de prijs DAALT hierdoor (ruwweg
     * de printlengte × het meter-tarief). Dat is een commercieel besluit van de
     * eigenaar — hij kiest het moment, eventueel samen met een tarief in
     * `printOnTop.printPricePerCm2` zodat de inkt zijn eigen prijs terugkrijgt.
     *
     * ONTBREEKT het veld of staat het op `false`, dan is de prijs byte-identiek
     * aan die van vóór C9. De BOM-regel en de klant-badge zijn bewust NIET aan
     * deze vlag gekoppeld: die corrigeren alleen wat er op de specsheet staat
     * (een print-element is geen neon-meter) en raken geen enkel bedrag.
     */
    excludePrintedFromTube?: boolean
    /**
     * C10 — WELKE TELLER DE BOCHTPRIJS BEPAALT. Default (veld afwezig) `'corners'`.
     *
     * `'corners'` = de bestaande hoek-telling: het aantal L→L-hoeken dat de
     * fillet afrondt, gesommeerd over de paden die de klant werkelijk als
     * gebogen buis krijgt. Dat laatste is sinds C10 een vlag-LOZE bugfix (een
     * verborgen of geprint pad genereerde bochtkosten voor buiswerk dat nooit
     * gebogen wordt); de vlag hier gaat uitsluitend over het MODEL.
     *
     * `'curvature'` = `bendEquivalents` (@mark2form/geometry): de gesommeerde
     * richtingsverandering binnen krappe straal, gedeeld door 90°. Dat telt óók
     * het handwerk in een volledig curvilineair script-logo, waar de
     * hoek-telling structureel 0 geeft — €0 buigarbeid terwijl de buiger van
     * begin tot eind buigt.
     *
     * WAAROM ACHTER EEN VLAG: het krommingsmodel VERHOOGT de prijs van precies
     * de ontwerpen die vandaag te goedkoop zijn, en zijn twee drempels (de
     * 2mm-stap en de factor 5 tussen "krappe bocht" en "vloeiende zwaai") zijn
     * beginwaarden die tegen de werkplaats geijkt moeten worden. Tot die ijking
     * draait het model in de SCHADUW: beide tellers worden altijd berekend, de
     * delta gaat als KPI (`bend-model-shadow`) mee, en de prijs verandert niet.
     * Omzetten is daarna een commercieel besluit van de eigenaar.
     */
    bendModel?: 'corners' | 'curvature'
    /**
     * C11 — REKEN DE PLAAT OP DE ZICHTBARE BBOX. Default `false`.
     *
     * `calculatePrice` leidt de plaathoogte vandaag af uit de VOLLEDIGE
     * viewBox-verhouding (`(viewBoxHeight / viewBoxWidth) × widthCm`), terwijl
     * de preview de plaat om de `visibleBbox` + contourmarge tekent. Bij
     * witruimte in het bronbestand of verborgen paden betaalt de klant dus voor
     * een andere plaat dan hij ziet — meestal een te grote.
     *
     * Met deze vlag aan geeft de pijplijn een expliciete `plateSizeCm` mee
     * (zichtbare bbox omgerekend naar cm + 2 × `contourMarginCm`) en rekent
     * `calculatePrice` de plaatprijs daarop.
     *
     * Waarom achter een vlag: de plaatprijs BEWEEGT hierdoor — omlaag bij
     * witruimte, omhoog bij een ontwerp dat de viewBox vult met een dikke
     * contourmarge eromheen. Dat is een prijsmodel-wijziging en dus een besluit
     * van de eigenaar, niet van de software. ONTBREEKT het veld of staat het op
     * `false`, dan is elk bedrag byte-identiek aan dat van vóór C11.
     */
    plateFromVisibleBbox?: boolean
  }
  /**
   * C14 — MAAKBAARHEIDS-WEERGAVE. Ontbreekt de sectie (de normale situatie),
   * dan tekent de configurator exact zoals vóór dit bestekpunt.
   *
   * HUISREGEL VOOR DEZE SECTIE — hier horen UITSLUITEND client-side
   * maakbaarheids-weergaveknoppen in: knoppen die in de BROWSER bepalen hoe een
   * al getraceerd ontwerp getekend en geteld wordt. De hele sectie is namelijk
   * uit de trace-cachevingerafdruk gefilterd (`settingsFingerprintOf` in
   * `apps/runtime/lib/trace-route/cache-step.ts`) — zelfde afspraak als
   * `pricing` (C9) en `editor` (C12) — zodat een tenant die zo'n knop omzet
   * niet zijn complete cache, inclusief Vision-facturen, opnieuw hoeft te
   * verdienen. Een veld dat de SERVER-trace stuurt hoort dus NIET in
   * `manufacturing`, want dan zou die filter tot 30 dagen lang een cache-hit
   * uit de verkeerde stand serveren.
   */
  manufacturing?: {
    /**
     * C14 — LAAT DE HOEK-FILLET DE FYSIEKE BUIGRADIUS VOLGEN. Default `false`.
     *
     * De fillet gebruikt vandaag vaste millimeters (`outerCornerRadiusMm` 2 /
     * `innerCornerRadiusMm` 1) ONAFHANKELIJK van de buisdikte, terwijl de
     * fysieke minimale buigradius `max(2 × tubeMm, minBendRadiusMm)` is
     * (`bendRadiusMmFor`, `packages/pricing/src/bend-radius.ts`). Bij een
     * 12mm-buis is dat 24mm: de preview toont dan hoeken van 2mm die de buiger
     * nooit kan maken, en `cornersRounded` — en dus de prijs — telt ze wel mee.
     *
     * Met de vlag aan klemt `resolveManufacturingKnobs` beide radii op de
     * buigradius: `effectiveOuter = max(outerCornerRadiusMm, bendRadiusMm)`,
     * `effectiveInner = max(innerCornerRadiusMm, bendRadiusMm)`. De
     * buis-CENTERLINE kan fysiek niet krapper dan die radius; alles daaronder
     * in de preview is een belofte die de werkplaats breekt.
     *
     * Waarom achter een vlag en niet direct: hoeken worden zichtbaar RONDER en
     * `cornersRounded` kan DALEN (meer hoeken smelten samen tot één echte
     * boog), dus de prijs kan mee-bewegen. Dat is een eigenaars-besluit na
     * visuele controle op het corpus — de hoeken die je dan ziet zijn de
     * hoeken die de klant geleverd krijgt.
     *
     * ONTBREEKT het veld of staat het op `false`, dan is zowel de geometrie
     * als de bochten-telling byte-identiek aan die van vóór C14.
     */
    filletFollowsBendRadius?: boolean
  }

  /**
   * MAAKBAARHEID W20 — de versieerbare maakbaarheidsprofielen van het bedrijf
   * (`production.manufacturingProfiles` in de tenantconfig). Vorm:
   * `TenantManufacturingProfileStoreSchema` in `./manufacturing-profile-store`.
   *
   * Bewust `unknown` en niet het type: dat schema bouwt op de groepsschema's van
   * `./manufacturing`, dat zelf uit dit bestand importeert — een type-import
   * terug zou een cyclus zijn (`pnpm boundaries`). Het maakt in de praktijk
   * niets uit: ELKE lezer moet het bestand hoe dan ook opnieuw controleren
   * (`verifyTenantManufacturingProfileStore`: schema + versiehashes), want
   * `Settings` reist ook naar de browser en een versie mag nooit overschreven
   * zijn. De mapper neemt de waarde ongewijzigd over; afwezig = legacy, exact
   * het gedrag van vóór W20 (en dan staat de sleutel ook niet in `Settings`, dus
   * de `outputFingerprint` van een tenant zonder profiel blijft gelijk).
   */
  manufacturingProfiles?: unknown

}


/**
 * C12 — FASERING-SCHAKELAAR VAN DE OMKEERBARE EDITOR-OVERRIDES.
 *
 * `reversibleOverridesV2: false` (de default, en de stand zonder config) = EXACT
 * het huidige gedrag. Aan zet drie dingen tegelijk om, en dat is een eis — ze
 * beschrijven één en dezelfde deur die nu maar één kant op draait:
 *
 *   1. `nextAutoAcceptPrintMask` respecteert `pathPrintUserTouched`: een pad
 *      waar de klant zélf een print/neon-keuze op maakte wordt bij de volgende
 *      hercalculatie (één slider-tik) niet meer overschreven.
 *   2. "Geprinte details verbergen" schrijft de snapshot-boolean
 *      `printedElementsHidden` in plaats van `pathHidden` van elk print-pad.
 *      Daardoor blijft `pathPrintMode` intact, blijft de banner met het vinkje
 *      staan (die rendert op `pathPrintMode.some(Boolean)`) en is het vinkje
 *      omkeerbaar. Met `pathHidden` was het een eenrichtingsdeur: de auto-accept
 *      zag "verborgen" en zette `pathPrintMode` overal op false, waarna de
 *      banner — en dus de weg terug — verdween.
 *   3. "Tonen" op een AUTOMATISCH verborgen pad (merkteken- of dust-regel) zet
 *      `pathForceVisible` in plaats van `pathHidden` te toggelen. Voorheen zette
 *      die klik `pathHidden` van false naar TRUE: het pad bleef onzichtbaar en
 *      de knop leek kapot. Bij die melding hoort de klant-waarschuwing
 *      `auto-hidden-paths`, die alleen in deze stand wordt geproduceerd — met de
 *      vlag uit zou hij naar een lijst wijzen waar de knop nog niets doet.
 *
 * De drie snapshot-velden (`pathPrintUserTouched`, `pathForceVisible`,
 * `printedElementsHidden`) gaan ONVOORWAARDELIJK mee in `EditorSnapshot`: ze
 * moeten door share-URL, autosave en undo/redo kunnen reizen vóórdat er ergens
 * een tenant is die de vlag aanzet. Met de vlag uit schrijft niets ze en zijn ze
 * dus overal all-false/false — inert.
 *
 * Net als `printOnTop.rowConsistencyV2` (C6) een tenant-instelling en geen
 * env-vlag: de beslissing valt in de BROWSER, waar `process.env` niet bestaat.
 * En om dezelfde reden BEWUST buiten de trace-cachevingerafdruk
 * (`settingsFingerprintOf`, `apps/runtime/lib/trace-route/cache-step.ts`):
 * de server-trace-output is in beide standen identiek — deze vlag beslist
 * uitsluitend wat de EDITOR met de al getraceerde paden mag doen.
 *
 * Rollback = veld op false; er is niets te migreren.
 */
export interface EditorConfig {
  reversibleOverridesV2: boolean
  /**
   * C13 laag 2 — RE-TRACE ALLEEN WANNEER DE MONOLINE-BESLISSING KAN OMSLAAN.
   * Default `false` = exact het huidige gedrag (élke `widthCm`-commit re-POST
   * `/api/trace`).
   *
   * Staat de vlag aan, dan vergelijkt `useTraceFlow` de nieuwe maat met de
   * `monolineBand` die de server bij de HUIDIGE trace meestuurde: de band
   * waarbinnen de rij-/logo-classificatie (mono vs. dubbele contour) gegarandeerd
   * hetzelfde uitvalt. Binnen die band is een maatwijziging puur client-side —
   * de geometrie-trap rekent alle mm-afhankelijke stappen tóch al per run
   * opnieuw. Dat scheelt een servertrace (en dus een Vision-call) per
   * slider-beweging, en — belangrijker — het scheelt de `reset` die vandaag
   * alle klantkeuzes wist.
   *
   * ONTBREEKT de band in de respons (oude client, gecachete entry van vóór dit
   * bestekpunt, svg-direct-pijplijn, of een hertrace die won), dan re-tracet de
   * client zoals altijd. "Geen band bekend" is dus nooit "geen re-trace nodig".
   */
  retraceOnlyOnBandExit: boolean
  /**
   * C13 laag 3 — NEEM KLANTWERK MEE OVER EEN ONVERMIJDELIJKE RE-TRACE.
   * Default `false` = exact het huidige gedrag (`freshEditorSnapshot`: alles
   * terug naar de beginstand).
   *
   * Aan zet `handleTrace` de verse snapshot om in een GEMIGREERDE snapshot:
   * per oud pad wordt het corresponderende nieuwe pad gezocht (centroid +
   * lengte, zie `lib/editor/snapshot-migrate.ts`) en de klant-keuzes daarop
   * overgezet. Onafhankelijk van `retraceOnlyOnBandExit`: ook mét laag 2 blijft
   * er een categorie re-traces over (buisdikte, band-uitgang), en juist dáár
   * verdween tot nu toe het werk.
   */
  migrateSnapshotOnRetrace: boolean
  /**
   * PLAATCONTOUR A (besluit Pascal 29-9) — RASTER DE PLAATRAND ZONDER CANVAS2D.
   * Default `false` = het huidige pad: de browser rastert via Canvas2D, de
   * server (zonder Canvas2D) valt terug op de omtrekcontour — met als gevolg
   * dat de serverpoort de contour van de configurator niet kan narekenen
   * (`production-geometry-mismatch`) en de servercontour door het ontwerp kan
   * snijden.
   *
   * Aan rastert `buildRasterContour` in browser én server met dezelfde pure
   * JavaScript (`rasterizePathsJs`): gelijke invoer → gelijke contour. De
   * plaatrand in de configurator en in de DXF verandert daardoor licht; de
   * prijs niet (de contour telt niet mee in de plaatmaat).
   */
  plateContourRasterJs: boolean
  /**
   * B-E4 — OPTISCHE KERNING VOOR FONTS ZONDER KERNTABEL.
   * Default `false` = exact de zetting van vandaag (kale advance-breedtes, plus
   * de `<hkern>`-paren waar het font ze meebrengt).
   *
   * Precies één catalogusfont levert kern-paren (relief-singleline, 2260 stuks);
   * de andere hebben er nul en zetten dus op de kale advance. Op papier heet dat
   * "wat luchtig", maar op een neonbord — waar elke letter een LOSSE BUIS is met
   * zichtbare ruimte ertussen — leest het gapende "AV"-gat als een fout in het
   * ontwerp. Aan schat de zetter de spatiëring uit de glyph-VORMEN: de kleinste
   * werkelijke afstand tussen de inkt van twee opeenvolgende letters wordt naar
   * een vast streefgetal geduwd, hard geklemd op een halve advance-tiende
   * (`packages/fonts/src/optical-kern.ts`).
   *
   * EEN BESTAAND KERN-PAAR WINT ALTIJD. De ontwerper van het font heeft naar dat
   * paar gekeken; wij meten alleen zijn randen. De vlag vult dus uitsluitend de
   * paren in waarvoor geen oordeel bestaat.
   *
   * WAAROM DEZE VLAG NIET IN DE TRACE-CACHEVINGERAFDRUK HOEFT: hij zit in de
   * `editor`-sectie, die `settingsFingerprintOf` sowieso al buiten de sleutel
   * houdt (zie `reversibleOverridesV2`), én hij is alleen doorgegeven aan de
   * KLANT-render (`POST /api/text-to-paths`). De server-trace zelf — inclusief
   * `final-render` en de slogan-dekkingsmeting — vraagt de spatiëring
   * ongewijzigd op, dus de gecachete SVG is in beide standen identiek en er is
   * geen `TRACE_CACHE_VERSION`-bump voor nodig.
   */
  opticalKerning: boolean
  /**
   * B-fix 18-8 — MOGEN DE INGEVOEGDE CATALOGUS-GLYPHS DE GEOMETRIE-TRAP
   * OVERSLAAN? Engine-default `true`; de enige vlag in deze sectie die AAN
   * begint (zie `EDITOR_CONFIG_DEFAULTS` voor de motivatie).
   *
   * DE MELDING (eigenaar, 18-8, Van Paassen en Martin Stolze): "in de trace zie
   * je mooie gelijke letters maar in het neon logo is de v en n niet goed
   * doordat die niet af en te klein is", en "de binnenkant van de V of de n
   * heeft ineens een punt … de a is niet gelijk".
   *
   * DE OORZAAK. Een font-vervanging voegt de glyph-paden van het catalogusfont
   * toe aan `svgPaths`. Vanaf dat moment behandelde de editor ze als gewone
   * getraceerde geometrie: stap 1 tekende ze na (`beautify`) en stap 2a rondde
   * hun hoeken af (`fillet`) — terwijl `buildFinalRenderSvg` (de trace-pagina,
   * en dus de belofte aan de klant) ze onaangeraakt tekent. Gemeten op de Van
   * Paassen-rij (112 cm, 8 mm buis): 10 van de 14 glyph-paden werden gewijzigd
   * en er werden 13 hoeken afgerond. Een catalogus-glyph is per definitie al
   * perfect; hem "uittekenen" kan alleen maar kwaad.
   *
   * GEEN `TRACE_CACHE_VERSION`-BUMP NODIG: dit is puur een editor-vlag in de
   * `editor`-sectie, die `settingsFingerprintOf` sowieso buiten de sleutel houdt.
   * De server-trace-OUTPUT (inclusief `final-render`) verandert niet — die
   * tekende de glyphs altijd al onaangeraakt. Deze fix laat de KLANT die
   * bestaande server-waarheid eindelijk zien.
   */
  captionGlyphsBypassPipeline: boolean
  /**
   * B-fix 18-8 — REGISTREERT DE KLANT-EDITOR OP DEZELFDE SCHAAL ALS DE SERVER?
   * Engine-default `true`.
   *
   * `prepareCaptionApply` gaf `tube` (de adem-marge) mee aan
   * `registerRowOrLetters`, `buildFinalRenderSvg` niet. Die marge verkleint
   * `maxInkW` en laat via de rij-uniforme breedte-veiligheid de HELE rij
   * krimpen: gemeten op Van Paassen (112 cm, 8 mm) een rijhoogte van 52,35 bij
   * de klant tegen 62,32 op de trace-pagina — 84 %. Dit verschil stond al als
   * bewust overgeslagen restpunt (C-O5) in `caption-render-parity.test.ts`; de
   * eigenaar heeft de knoop nu doorgehakt richting de server-render.
   *
   * GEEN `TRACE_CACHE_VERSION`-BUMP NODIG: de server-kant beweegt niet. Alleen
   * de klant-registratie laat de marge los, en die draait volledig in de
   * browser.
   */
  captionServerScaleParity: boolean
}

/**
 * C13 laag 2 — DE MAAT-BAND WAARBINNEN DE MONOLINE-BESLISSING VASTLIGT.
 *
 * `minWidthCm`/`maxWidthCm` in cm; `null` = die kant is onbegrensd (geen enkele
 * maat aan die zijde draait de beslissing om). De band is OPEN:
 * `minWidthCm < w < maxWidthCm` geeft dezelfde classificatie als de maat
 * waarmee deze trace is opgehaald. Bewust open en niet half-open: de grenzen
 * ZIJN de omslagpunten, en of het omslagpunt zelf nog aan deze of aan de
 * overkant hoort verschilt per vergelijking (`>` bij de caption-hoogte, `<` bij
 * de dikte-drempel). Precies óp de grens re-tracen is de kant waar niets stuk
 * kan gaan.
 *
 * BEGRENSDE BELOFTE — lees dit vóór je de vlag aanzet. De band bindt de
 * MONOLINE-classificatie (`analyzeTextRows`), en dat is de maat-afhankelijke
 * beslissing die de VORM van het ontwerp bepaalt (één centerline vs. dubbele
 * contour per rij/element). Er zijn server-side nog twee kleinere fysieke
 * poorten die óók van `widthCm` afhangen en NIET in de band verwerkt zijn:
 *
 *   1. de spook-/accentvloer (`tubeFloorAreaPx`,
 *      `packages/ocr/src/multi-color-trace.ts`) — een kleur overleeft alleen
 *      met een component boven een cirkel Ø 2 × buisdikte;
 *   2. de slogan-poort (`inkHmm >= factor × tubeMm`,
 *      `apps/runtime/lib/trace-route/slogan-step.ts`) — een tekstrij wordt
 *      font-slogan of blijft getracede outline.
 *
 * (De gat-poort — `holeMinSizePx`, zelfde bestand — stond hier als derde,
 * maar beslist sinds A-E4 (`TRACE_HOLE_GATE_V2`, default aan) niet meer over
 * de geometrie: een ingesloten vlak blijft een gat ongeacht maat, en de
 * maakbaarheids-vraag is de client-side warning `hole-too-small` geworden.
 * Binnen de band is een sliderbeweging wat gaten betreft dus geometrie-vast.)
 *
 * De twee resterende meten in mm en schuiven dus mee met de maat. Binnen de
 * band kan hun uitkomst dus in theorie verschillen van wat een verse trace zou
 * geven. Waarom dat te verantwoorden is bij het AANZETTEN van de vlag: beide
 * zijn MAAKBAARHEIDS-ondergrenzen, en ze schuiven de goede kant op wanneer het
 * bord GROEIT (een accent wordt maakbaar, een tekstrij wordt groot genoeg voor
 * outline). Krimpen is de risicorichting. Wil de eigenaar een harde "geen
 * re-trace ⇒ identieke trace"-garantie, dan moeten die poorten hun kritieke
 * maten net zo aanleveren als de monoline-analyse dat nu doet — dat is
 * meetbaar werk in `quantize` en `slogan-step`, geen herontwerp.
 */
export interface MonolineBand {
  minWidthCm: number | null
  maxWidthCm: number | null
}

export interface ColorRegion {
  pathIndices: number[]
  originalFill: string
  assignedColorId: string
}

// ---------------------------------------------------------------------------
// C4 — GESTRUCTUREERDE KLANT-WAARSCHUWINGEN
// ---------------------------------------------------------------------------
// Tot dit bestekpunt was een waarschuwing één Nederlandse ZIN in `warnings[]`.
// De client moest daar met een regex (`AI_PATTERN` in `lib/editor/warnings.ts`)
// betekenis uit terugwinnen: matchte hij, dan rode storingsbanner, anders één
// generieke gele regel. Twee gevolgen die dit type wegneemt:
//
//   1. MISCLASSIFICATIE. De regex keek naar het WOORD "OCR". Een
//      kwaliteits-samenvatting van het taalmodel ("...de OCR-tekst wijkt af...")
//      belandde daardoor in de rode storingsbanner terwijl er niets stuk was.
//      Een `code` kan niet misvuren: hij wordt gezet op de plek die het weet.
//   2. GEEN HANDELINGSPERSPECTIEF. Eén generieke zin ("upload een scherpe
//      foto") kan per definitie niet vertellen wat er aan de hand is én wat de
//      klant eraan kan doen. Met een code kiest de UI per geval twee zinnen:
//      wat er is, en wat de klant kan doen.
//
// ADDITIEF. `warnings[]` blijft exact wat het was — oude clients en gecachete
// respons-entries van vóór deze versie kennen `warningsV2` niet en volgen
// gewoon het bestaande pad.
// ---------------------------------------------------------------------------

/**
 * Waarom een waarschuwing verschijnt. Machine-leesbaar en taal-onafhankelijk;
 * de UI kiest er een i18n-tekst bij.
 *
 * De eerste elf codes staan zo in het bestek. De rest is er tijdens de
 * uitvoering bij gekomen omdat het bestek nog uitging van de warning-bronnen
 * van vóór golf 0/1: er zijn sindsdien meer plekken die de klant iets te
 * melden hebben, en een bron zonder eigen code zou terugvallen op de generieke
 * regel — precies wat dit punt afschaft. Elke toevoeging staat hieronder met
 * zijn bron erbij.
 */
export type WarningCode =
  // ── uit het bestek ───────────────────────────────────────────────────────
  /** Bron-afbeelding kleiner dan de aanbevolen korte zijde. `params.px`. */
  | 'low-resolution'
  /** Te weinig pad-knopen: nauwelijks herkenbaar detail in de bron. */
  | 'low-detail'
  /** Vision-zelfcheck: het overtrekken wijkt af van het origineel.
   *  `params.percent` = hoeveel procent NIET herkend is (dus 100 − score). */
  | 'deviation'
  /** Dekkings-/poortmeting: bron-inkt die de trace niet dekt. `params.percent`. */
  | 'missing-shapes'
  /** Dekkings-/poortmeting: trace-inkt die niet in de bron staat. */
  | 'extra-noise'
  /** Resultaat in de band [`QUALITY_THRESHOLDS.gate`, `.retry`): de
   *  hertrace-ladder heeft het al geprobeerd, dus melden zonder te blokkeren.
   *  Producent sinds C3: `quality-step.ts`. */
  | 'borderline-quality'
  /** Het globale vangnet draaide de slogan-strip terug. */
  | 'slogan-strip-reverted'
  /** ®/™ wordt op de plaat geprint (te klein voor een buis). `params.count`. */
  | 'trademark-printed'
  /** ®/™ is weggelaten omdat print-op-plaat uit staat. `params.count`. */
  | 'trademark-removed'
  /** Te kleine lijntjes automatisch verborgen. `params.count`. */
  | 'auto-hidden-paths'
  /** Tekstherkenning is niet uitgevoerd — STORING (rode banner).
   *  `params.reason` onderscheidt de oorzaak voor de admin-diagnose. */
  | 'ocr-unavailable'
  // ── toegevoegd tijdens de uitvoering (bron erbij) ─────────────────────────
  /** A2 — de beeldopschoning mislukte; het logo is onbewerkt overgetrokken
   *  (`route.ts`, ledger-reden `preprocess-failed`). */
  | 'preprocess-degraded'
  /** A2 — één kleur kon niet worden overgetrokken en ontbreekt
   *  (`route.ts`, ledger-reden `potrace-failed`). */
  | 'trace-degraded'
  /** A8(b) — de perimeter-tak liet componenten liggen. `params.count`
   *  (`multiColorTrace.failedComponentCount`). */
  | 'shapes-untraceable'
  /** B3 — de fontcatalogus gooide een fout tijdens de tekenbaarheids-check;
   *  de slogan-strip is overgeslagen en de kleine tekst blijft ruwe trace
   *  (`route.ts`, mapping op `SLOGAN_CATALOG_ERROR_WARNING`). */
  | 'font-catalog-error'
  /** A11 — levende tekst in een SVG is via een rasterstap overgetrokken
   *  (`SVG_TEXT_VIA_RASTER_WARNING`). */
  | 'svg-text-rasterised'
  /** A11 — levende tekst/ingesloten beeld in een SVG kon niet worden omgezet
   *  en ontbreekt (`SVG_RASTER_ONLY_LOST_WARNING`). */
  | 'svg-content-lost'
  /** A4 — kleurverlopen/foto-achtige delen vereenvoudigd (`GRADIENT_WARNING`). */
  | 'gradient-simplified'
  /** A5 — de server verwijderde randartefacten van het bronbestand.
   *  `params.count`. */
  | 'border-artifacts-removed'
  /** C11 — de gekozen breedte is teruggezet omdat het bord anders HOGER zou
   *  worden dan `maxPlateSizeCm.height`. `params.max` = de nieuwe breedte in
   *  cm, `params.maxH` = de hoogte-limiet van deze aanbieder. Client-side
   *  afgeleid (`sizeClampedWarning`, `lib/editor/warnings.ts`): alleen de
   *  browser kent de zichtbare bbox ná de trace, en dus de verhouding waarmee
   *  de limiet naar een breedte omgerekend wordt. */
  | 'size-clamped'
  /** A-E4 — {count} ingesloten gaten zijn bij de HUIDIGE maat te klein voor
   *  een buisring (korte bbox-zijde < `holeMinSizeFactorVsTube × tubeMm`) en
   *  worden in de werkplaats dichtgezet. De geometrie behoudt het gat (de
   *  server-gat-poort is sinds `TRACE_HOLE_GATE_V2` maat-onafhankelijk); dit
   *  is uitsluitend de maakbaarheids-melding. Client-side afgeleid
   *  (`holeTooSmallWarning`, `lib/editor/warnings.ts` +
   *  `lib/editor/hole-gate.ts`): alleen de browser kent de actuele
   *  slider-maat. `params.count`. */
  | 'hole-too-small'
  /** A-O2 — de gerichte regio-reparatie (`TRACE_REGION_REPAIR`) heeft
   *  {count} paden toegevoegd in gebieden waar de dekkingsmeting bron-inkt
   *  miste. Info en geen warn: de melding beschrijft een verbetering die al
   *  is doorgevoerd, geen probleem dat de klant nog moet oplossen. Producent:
   *  `repair-step.ts` via `quality-step.ts`. `params.count`. */
  | 'region-repaired'
  /** Interne diagnose (`[debug] …`), alleen bij `?debug=1`. Bewust ZONDER
   *  klant-tekst: hij hoort in het admin-blok met de ruwe regels. */
  | 'debug-note'

/**
 * Eén gestructureerde waarschuwing.
 *
 * `severity` bepaalt WAAR hij landt, `code` WAT er staat:
 *   `error` → de rode storingsbanner (er is iets stuk aan onze kant)
 *   `warn`  → gele tip-regel; er is iets aan het RESULTAAT dat aandacht vraagt
 *   `info`  → gele tip-regel; puur uitleg van wat de software heeft gedaan
 *
 * `params` vult de plaatshouders in de i18n-tekst (`{px}`, `{percent}`,
 * `{count}`). Getallen blijven getallen: de UI formatteert ze in de locale van
 * de bezoeker, en een server-side geformatteerde string zou dat onmogelijk
 * maken.
 */
export interface StructuredWarning {
  code: WarningCode
  severity: 'info' | 'warn' | 'error'
  params?: Record<string, string | number>
}

/**
 * A10 — rol van één subpad binnen een getraced component: de buitencontour van
 * een letter/vorm, of een gat daarbinnen (de counter van een `o`, `e`, `a`).
 */
export type PathSubpathRole = 'outer' | 'hole'

export interface LogoRefinementSnapshot {
  svgPaths: string[]
  pathFills: string[]
  viewBoxWidth: number
  viewBoxHeight: number
  pathComponentGroups?: number[]
  pathSubpathRoles?: PathSubpathRole[][]
}

export interface LogoRefinementReport {
  /** Bounded partial source comparison; all unselected paths are preserved exactly. */
  pathBudget?: { inspectedPaths: number; candidatePaths: number[]; preservedPaths: number[] }

  algorithmVersion: 'source-fit-v1'
  requested: boolean
  effective: boolean
  status: 'disabled' | 'applied' | 'unchanged' | 'skipped' | 'failed'
  reason: string
  changedPaths: number[]
  rejectedPaths: number[]
  maxSourceDeviationPx: number | null
  durationMs: number
  sourceWidthPx: number | null
  sourceHeightPx: number | null
  /** Echte vroege contouren; alleen bij beheerinspectie meegestuurd. */
  before?: LogoRefinementSnapshot
  after?: LogoRefinementSnapshot
}

export interface SourceVectorizationReport {
  /** Bounded partial source comparison; all unselected paths are preserved exactly. */
  pathBudget?: { inspectedPaths: number; candidatePaths: number[]; preservedPaths: number[] }

  algorithmVersion: 'source-vector-v1'
  status: 'applied' | 'unchanged' | 'skipped' | 'failed'
  processed: boolean
  reason: string
  changedPaths: number[]
  rejectedPaths: number[]
  maxSourceDeviationPx: number | null
  maxAllowedDeviationPx: number
  durationMs: number
  sourceWidthPx: number | null
  sourceHeightPx: number | null
  before?: LogoRefinementSnapshot
  after?: LogoRefinementSnapshot
}

export interface TraceResult {
  /** Standard source-bound vectorization, before optional restoration/manufacturing. */
  sourceVectorization?: SourceVectorizationReport
  /** Ook uitgeschakeld vastgelegd: late maakbaarheid mag de bron niet opnieuw natekenen. */
  logoRefinement?: LogoRefinementReport
  svgPaths: string[]
  pathFills: string[]
  /**
   * C15 — DE ARCHIEFSLEUTEL VAN DÍT ANTWOORD.
   *
   * De browser moet zijn klant-snapshot (`POST /api/trace-preview`) aan het
   * juiste archiefrecord kunnen hangen; zonder deze id kent hij zijn eigen
   * record niet en is elk klant-beeld onkoppelbaar. Additief en optioneel:
   * afwezig wanneer het archief uit staat.
   *
   * STAAT BEWUST NIET IN DE GECACHETE BODY (zie `app/api/trace/route.ts`): de
   * id hoort bij één request. Een gecachete id zou de snapshot van een nieuwe
   * upload aan het record van een oude hangen.
   */
  traceArchiveId?: string
  /** Private UV source evidence issued outside the shared trace cache. */
  uvPrintSource?: string
  /**
   * A6(c) — DE GEMETEN BRONKLEUR per pad, parallel aan `svgPaths`.
   *
   * `pathFills` is een RENDER-kleur: de kwantisatie tilt hem op (`vivify`),
   * snapt hem naar een LED-kleur en schrijft élke grijs/zwart-cluster naar
   * `#ffffff` (er bestaat geen grijze neonbuis). Voor een clubembleem — zwarte
   * leeuw in een witte cirkel — betekende dat: twee paden, dezelfde fill, één
   * regio, en de leeuw niet los omkleurbaar. `pathSourceFills` draagt daarnaast
   * wat er ÉCHT gemeten is, zodat regio-vorming, de warm-wit-regel en de
   * paletmapping op de bronkleur kunnen werken.
   *
   * ADDITIEF en OPTIONEEL: bestaande consumenten van `pathFills` veranderen
   * niet. Het veld ONTBREEKT wanneer de bron geen aparte meting had — een
   * ge-uploade SVG draagt zijn eigen fills, dáár ís de fill al de bronkleur. De
   * juiste lezing is dus altijd `pathSourceFills ?? pathFills`.
   */
  pathSourceFills?: string[]
  /**
   * A10 — HET COMPONENTMODEL, parallel aan `svgPaths`.
   *
   * De server groepeert een getraced pad in letters/vormen
   * (`splitTraceComponents`): één component = één buitencontour mét zijn gaten.
   * De client splitst dat daarna wéér los (`splitSubpaths`) omdat elke lijn
   * apart bewerkbaar moet zijn — en verloor daarmee precies de wetenschap dat
   * het gat van een `o` bij die `o` hoort. Gevolg: het ruisfilter zag een losse
   * kleine gesloten vorm en wiste hem, waarna de letter een dichte blob werd
   * (zwakte 9 van de vectorisatie-verkenning).
   *
   * `pathComponentGroups[i]` is het groeps-ID van pad `i`. Paden met hetzelfde
   * ID horen bij dezelfde letter/vorm. Het ID is een volgnummer zonder
   * betekenis buiten "gelijk = zelfde component"; het overleeft filterstappen
   * (paden worden weggeknipt, ID's niet hernummerd), dus gaten in de reeks zijn
   * normaal.
   *
   * ADDITIEF en OPTIONEEL: ontbreekt het veld (oude server, gecachete respons
   * van vóór cache-v57, pure-SVG-pad), dan valt de client terug op het gedrag
   * van vóór dit bestekpunt — elk subpad is dan weer "los".
   */
  pathComponentGroups?: number[]
  /**
   * B1 (18-8, eenheden-ronde) — SERVER-INTERN: uit welk INVOERPAD van
   * `splitTraceComponents` kwam pad `i`? Parallel aan `svgPaths`.
   *
   * WAAROM NAAST `pathComponentGroups` EN NIET IN PLAATS DAARVAN. Die groepen
   * krijgen hun ID in de BINNENlus van de splitsing (`group++` per uitvoerpad),
   * dus daar is per constructie een bijectie pad→groep: twee paden dragen er
   * nooit hetzelfde nummer. Dit veld doet juist dát wél — het is het
   * volgnummer van het getraceerde component (≈ één letter) waaruit het pad
   * ontstond. Bij een OUTLINE-trace vallen de twee samen (gat genest in outer ⇒
   * één pad per letter); bij een SKELETON-trace niet (de niet-geneste takken
   * van dezelfde letter worden N paden met N verschillende groepen, maar met
   * ÉÉN bron-component).
   *
   * WAAR HET VOOR IS: de strip-poorten in `slogan-step.ts` (dekking + overclaim)
   * vergelijken een glyph-telling met een paden-telling. Zonder dit veld telt
   * de skeleton-tak 33 paden waar de rij 17 letters heeft en vuren die poorten
   * vals (het bord viel terug op een wiebelige zelfgemaakte middenlijn). Met
   * dit veld tellen ze EENHEDEN — het aantal onderscheiden bron-componenten.
   *
   * SERVER-INTERN, en dat is een harde afspraak, geen bijzin: het veld hoort
   * NIET in de respons (`respond.ts` neemt het niet over) en de client heeft er
   * geen consument voor. Het staat in dit type omdat `TraceResult` de vorm is
   * waarin de server-pijplijn zelf rekent — dezelfde reden waarom
   * `pathLengthPx` erin staat.
   *
   * OPTIONEEL: ontbreekt het veld (pure-SVG-pad, gecachete respons, de losse
   * stap-tests), dan vallen de poorten terug op identiteit — één pad is dan één
   * eenheid en het gedrag is exact dat van vóór dit bestekpunt.
   */
  pathSourceComponents?: number[]
  /**
   * C6 fase 1 — STABIELE PAD-IDENTITEIT, parallel aan `svgPaths`.
   *
   * `pathIds[i]` is een korte hash (fnv1a-hex) over de gequantiseerde
   * centroid-FRACTIE van de viewBox, de gequantiseerde lengte-fractie van de
   * viewBox-diagonaal en de fill van pad `i` — zie
   * `@mark2form/geometry/path-identity`. Omdat alles een fractie is, geeft
   * dezelfde upload op een andere `widthCm` (zelfde geometrie, andere schaal)
   * identieke ids. De client gebruikt ze in de snapshot-migratie
   * (`lib/editor/snapshot-migrate.ts`) om klantwerk over een re-trace exact te
   * koppelen vóórdat de geometrische matcher hoeft te raden.
   *
   * ADDITIEF en OPTIONEEL — en dat "optioneel" is het contract: gecachete
   * responsen van vóór dit bestekpunt missen het veld, en élke consument moet
   * absentie aankunnen. De enige consument (de id-lookup in de
   * snapshot-migratie) doet alleen iets wanneer ZOWEL de oude als de nieuwe
   * trace ids draagt; ontbreekt het veld ergens, dan geldt exact het gedrag
   * van vandaag (alleen geometrisch matchen). GEEN `TRACE_CACHE_VERSION`-bump
   * dus: een entry zonder het veld is geen degradatie maar het huidige gedrag
   * — zelfde argument als `monolineBand`, zie de toelichting in
   * `lib/trace-route/respond.ts`.
   */
  pathIds?: string[]
  /**
   * A10 — DE ROL VAN ELK SUBPAD binnen een pad, parallel aan `svgPaths`.
   *
   * `pathSubpathRoles[i][j]` is de rol van het `j`-de subpad (M-blok) van pad
   * `i`, in exact de leesvolgorde van de `d`-string: `'outer'` = buitencontour,
   * `'hole'` = gat (binnencontour). De nesting is server-side al bekend —
   * `splitPathComponents` bepaalt hem met punt-in-polygoon + diepte-pariteit —
   * en zou anders client-side opnieuw (en slechter) geraden moeten worden.
   *
   * WAAROM GENEST EN NIET PLAT. De rol hoort bij een SUBPAD, de rest van de
   * respons bij een PAD. Een platte array zou de client dwingen te weten hoe de
   * server splitste; nu telt hij zijn eigen subpaden en leest de rol op
   * dezelfde index. Loopt die telling uiteen (een SVG-upload met `m`-subpaden
   * bijvoorbeeld), dan negeert de client de rollen van dát pad en gedraagt hij
   * zich als voorheen — nooit een verkeerde rol op een pad plakken.
   */
  pathSubpathRoles?: PathSubpathRole[][]
  /**
   * WP2 (kleuronderzoek 25-9) — DE ONGEWIJZIGDE BRONMETING per pad, parallel
   * aan `svgPaths`. Alleen aanwezig met `TRACE_GROUP_COLOR=on`.
   *
   * WAAROM EEN DERDE KLEURVELD. Met de vlag aan krijgt elke samenhangende
   * neontrace (tracegroep, zie `pathTraceGroups`) één automatische buiskleur:
   * `pathFills[i]` én `pathSourceFills[i]` van een gemengde groep worden
   * allebei die ene groepskleur, zodat editor, serverrender, archief en export
   * vanzelf dezelfde kleur lezen. Daarmee zou de gemeten bronkleur (de witte
   * letter op de rode badge) verloren gaan — en juist die is nodig om te
   * begrijpen waarom een deel anders was, en om later bewust om te kleuren.
   * Dit veld bewaart hem: `pathSourceFills[i] ?? pathFills[i]` zoals het
   * vóór de groepskleurstap was.
   *
   * ADDITIEF en OPTIONEEL: geen bestaande consument leest het; vlag uit ⇒ het
   * veld ontbreekt en de respons is byte-identiek aan die van vóór WP2.
   */
  pathMeasuredFills?: string[]
  /**
   * WP2 — DE TRACEGROEP per pad, parallel aan `svgPaths`. Alleen aanwezig met
   * `TRACE_GROUP_COLOR=on`.
   *
   * Paden met hetzelfde getal vormen één samenhangende neontrace (nesting of
   * een gedeelde grens binnen 0,8 buisdikte — zie
   * `@mark2form/engine-kleur/trace-groups`) en dragen automatisch één
   * buiskleur. Het getal is een volgnummer zonder betekenis buiten "gelijk =
   * zelfde groep"; een latere filterstap (de slogan-strip) knipt paden weg
   * zonder te hernummeren, dus gaten in de reeks zijn normaal — zelfde afspraak
   * als `pathComponentGroups`.
   */
  pathTraceGroups?: number[]
  /**
   * E (onderzoek kleurenengine 25-9) — welke kleur de server als invoer voor
   * de paletmapping gebruikte. Alleen aanwezig (`'source'`) met
   * `TRACE_RENDER_COLORS_V2=on`: de editor voert dan `pathSourceFills` (met
   * `pathFills` als terugval) aan `applyAutoDetect`, net als
   * `resolveRenderColor` op de server. Afwezig = `pathFills`, het gedrag van
   * vóór dit veld.
   */
  paletteInput?: 'source' | 'fill'
  viewBoxWidth: number
  viewBoxHeight: number
  /** Client-veilige identiteit van de server-resolved outputinstellingen. */
  profile?: { schemaVersion: 1; product: ProductKind; outputFingerprint: string }
  /** Identiteit van de oorspronkelijke uploadbytes, vóór enige conversie. */
  sourceRef?: { sourceHash: string; sourceId?: string }
  /** Durable pre-SVG workflow provenance; the existing editor owns all later processing. */
  logoWorkflow?: LogoTraceBinding
  pathLengthPx: number
  /** Non-fatal hints surfaced to the user (e.g. low source resolution). */
  warnings?: string[]
  /**
   * C4 — dezelfde waarschuwingen, maar GESTRUCTUREERD (code + severity +
   * params) in plaats van als Nederlandse zin.
   *
   * NAAST `warnings`, niet in plaats van: een gecachete respons van vóór
   * cache-v59 en elke oudere client kennen dit veld niet, en die moeten
   * ongewijzigd blijven werken. Het veld staat er ALLEEN wanneer er iets in
   * zit — dan is de respons van een schone upload byte-identiek aan die van
   * vóór dit bestekpunt.
   *
   * De volgorde volgt de volgorde van `warnings`, en de codes zijn per code
   * uniek (`pushWarningV2` ontdubbelt): twee bronnen die hetzelfde
   * constateren leveren de klant één zin op, geen twee.
   */
  warningsV2?: StructuredWarning[]
  /**
   * Plan H3 — waar de (opgeschaalde) bron-afbeelding in het trace-canvas
   * ligt (crop/padding-correctie uit de voorbewerking). De client lijnt
   * hiermee het "Origineel"-overlay exact uit met de buizen.
   */
  sourcePlacement?: { x: number; y: number; width: number; height: number }
  /** Debug-mode emit from the monoline analyzer — only present when
   *  settings.monolineSwitch.debug is true. The configurator overlays
   *  this onto the design preview for verification. */
  rowAnalysis?: TraceRowAnalysis
  /**
   * C13 — de maat-band waarbinnen de monoline-classificatie van DEZE trace
   * ongewijzigd blijft. Zie {@link MonolineBand} voor de exacte belofte.
   *
   * ADDITIEF EN OPTIONEEL, en dat is hier meer dan een formaliteit: het veld
   * ONTBREEKT in elke gecachete respons van vóór dit bestekpunt, op het
   * svg-direct-pad, bij de potrace-terugval en wanneer een kwaliteits-hertrace
   * won. De client MOET "veld afwezig" lezen als "geen band bekend — re-trace
   * zoals altijd". Alleen mét die lezing is er geen `TRACE_CACHE_VERSION`-bump
   * nodig; zie de toelichting in `lib/trace-route/respond.ts`.
   */
  monolineBand?: MonolineBand
  /** Caption-style text rows detected after trace. Each row carries
   *  its bbox + the path indices it contains, so the editor can offer
   *  to swap the traced wobbly letters for clean font glyphs.
   *  - `suggestedText`: OCR-extracted string (Claude Vision when
   *    configured, otherwise Tesseract fallback).
   *  - `suggestedFontId`: the best-matching Hershey font picked by
   *    pHash render-and-compare against the source crop.
   *  - `ocrConfidence`: `high` triggers auto-replace; `medium`/`low`
   *    just pre-fills the replace input for manual confirmation. */
  detectedTextRows?: Array<{
    bbox: { x: number; y: number; w: number; h: number }
    pathIndices: number[]
    /** Visible-character count of suggestedText, ignoring whitespace.
     *  When suggestedText is absent (legacy fallback path) this falls
     *  back to the geometric path-cluster count from text-row-detect.
     *  Used by the CaptionReplacePanel placeholder. BUG-008 unified
     *  this to a single string-length definition so consumer code can
     *  rely on it. */
    glyphCount: number
    /** True when this row represents the primary brand mark
     *  (Vision wordmark or glyphHeight/viewBoxHeight > brandHeightRatio).
     *  UI hides font-substitution controls; users can force font via an
     *  escape link.
     *
     *  Print-routering: `applyRowConsistency` in
     *  `apps/runtime/lib/editor/print-routing.ts` stelt de paden van
     *  zo'n rij vrij van auto-print zodra ook maar ÉÉN lid neon blijft — een
     *  half-donker woordmerk leest als defect, niet als maakbaarheidskeuze.
     *  Nadrukkelijk GEEN blanco vrijbrief: is de rij in zijn GEHEEL te klein
     *  voor buis, dan gaat hij nog altijd collectief plat. */
    isPrimaryBrand?: boolean
    /** How this row should appear in the final neon design.
     *  - `'font'`  → the row is a slogan/tagline: its traced letter
     *    paths have been REMOVED from `svgPaths`/`pathFills` server-side,
     *    so no neon outline of it exists. The client renders it ONLY as
     *    clean single-line catalogue-font text at this row's bbox using
     *    `suggestedFontId` + `suggestedText`. `pathIndices` is therefore
     *    empty for these rows.
     *  - `'trace'` / absent → the row stays traced as neon (wordmark).
     *  This eliminates the old "double": a slogan that was both traced
     *  AND overlaid with font text. */
    renderMode?: 'font' | 'trace'
    /** Originele per-letter vakken (viewBox-ruimte), server-side gemeten
     *  op de trace-paden VÓÓR die voor een 'font'-rij verwijderd werden.
     *  De client registreert elke vervangings-glyph exact op zijn vak —
     *  zelfde plek en grootte als de originele letter. Zonder dit veld
     *  kan de client de vakken niet meer meten (de paden zijn weg) en
     *  valt hij terug op woord-lay-out over de rij-bbox. Gesorteerd op x
     *  (leesvolgorde); spaties hebben géén vak. */
    letterBoxes?: Array<{ x: number; y: number; w: number; h: number }>
    /** K2d: paletkleur van de originele letters (vóór de server-strip) —
     *  de client kleurt de font-vervanging hiermee i.p.v. warm wit. */
    originalFillHex?: string
    suggestedText?: string
    suggestedFontId?: string
    /** Meetbewijs bij de serverkeuze. Geen drempel: de productlaag beslist. */
    fontMatch?: {
      fontId: string | null
      /** Logo-only reviewed category selection; scores remain null for this method. */
      captionStyleSelection?: NeonLogoCaptionFontSelection
      candidates?: Array<{ fontId: string; score: number | null }>
      score: number | null
      runnerUpScore: number | null
      margin: number | null
      reason: 'matched' | 'empty-text' | 'source-unavailable' | 'no-covered-font' | 'render-unavailable'
    }
    ocrConfidence?: 'high' | 'medium' | 'low'
    /**
     * Kwam deze rij van het TERUGVAL-pad (`buildLegacyTextRows`) in plaats van
     * van de Vision-hoofdweg? Additief; afwezig = hoofdweg, precies het gedrag
     * van voor deze markering. De client weegt 'medium' op het terugval-pad
     * strenger, omdat daar geen Vision-classificatie is die de lezing steunt
     * (016 Starbucks, ronde 33435362083).
     */
    ocrSource?: 'legacy'
    /** All OCR engines' results for this row, run in parallel. The
     *  editor renders a side-by-side comparison view (Tesseract vs
     *  Claude Vision) so the user can pick the better recognition
     *  visually before auto-apply. */
    ocrCandidates?: OcrCandidate[]
    /** True wanneer Vision oordeelde dat deze tekst een gebogen
     *  baseline volgt (cirkel/boog/golf). */
    curved?: boolean
    /** Cirkelboog gefit door de letterpaden van deze rij (alleen bij
     *  curved-rijen met >= 3 paden). De caption-layout legt de
     *  Hershey-vervanging hierlangs i.p.v. op een rechte baseline. */
    arc?: CaptionArc
    /**
     * LETTERVERVANGING V2 (plan 10-9-2026, §A) — het complete plaatsings-
     * antwoord van `lib/trace-route/letter-placement-step.ts`.
     *
     * WAAROM EEN EIGEN VELD EN NIET MEER `letterBoxes`. `letterBoxes` draagt
     * alléén vakken; het zegt niet waar ze vandaan komen, hoeveel vertrouwen
     * eronder zit, of waar de WOORDEN staan. Precies dat laatste is de kern
     * van de klacht die V2 oplost: zonder woordvakken smeert de terugval de
     * regel met gelijke gaten uit en verdwijnt de woordafstand van het logo.
     * Een tweede veld ernaast houdt bovendien de oude weg mechanisch intact —
     * `letterBoxes` blijft exact wat het was, en elke lezer die `placement`
     * niet kent gedraagt zich als voorheen.
     *
     * ADDITIEF EN AFWEZIG ZONDER VLAG. Het veld verschijnt uitsluitend
     * wanneer de tenant-vlag `letterPlacementV2` aanstaat (en de env-noodrem
     * `LETTER_PLACEMENT_V2=off` niet is overgehaald). Zonder vlag is de
     * respons byte-identiek aan die van vóór dit bestekpunt — daarom is er
     * ook geen `TRACE_CACHE_VERSION`-bump nodig; die hoort bij het moment dat
     * de vlag default aan gaat.
     *
     * WIE HET LEEST. `lib/editor/captions.ts` (klant), `lib/final-render.ts`
     * en `packages/render/src/neon-render.ts` (server-render) dispatchen op
     * `v === 2` naar `registerRowV2` — dezelfde drie treden, dezelfde vakken,
     * dus per constructie dezelfde letters op dezelfde plek.
     */
    placement?: {
      /** Contract-versie. Alleen `2` bestaat; het getal is er zodat een
       *  volgende vorm naast deze kan leven in plaats van hem stil te
       *  veranderen. */
      v: 2
      /** Welke as leidend was (§A4): `height` = exact de hoogte van het
       *  origineel, `width` = exact de breedte van het woord. */
      anchor: 'height' | 'width'
      /** De woordvakken uit het kolomprofiel, in leesvolgorde. Minstens één
       *  (dan is het de rij-bbox). Dit is de VLOER: speling blijft binnen een
       *  woord en kan de regel niet meer uitsmeren. */
      wordBoxes: Array<{ x: number; y: number; w: number; h: number; text: string }>
      /** Per-letter vakken in leesvolgorde, of LEEG wanneer de segmentatie
       *  niet vertrouwd werd. Leeg is geen fout maar de bewuste woordvloer:
       *  `registerRowV2` zakt dan één trede en zet per woord. */
      letterBoxes: Array<{
        x: number
        y: number
        w: number
        h: number
        char: string
        /** `dp` = de DP-segmentatie, `trace` = de vakken uit de trace-paden
         *  (tweede hypothese, wint bij een lagere uitlijnfout), `ai` = de
         *  Vision-verfijning van fase 2. */
        source: 'dp' | 'trace' | 'ai'
        /** True only when vertical bounds were measured from actual source ink. */
        sourceInk?: boolean
        conf: number
      }>
      /** Vertrouwen van de segmentatie over de hele rij (0..1) — het MINIMUM
       *  over de woorden, want de rij wordt als geheel geplaatst. */
      segmentConfidence: number
      /** De meetlat van §A6, server-side berekend zodat ronde A en ronde B
       *  per logo vergelijkbaar zijn. */
      metrics?: PlacementMetrics
    }
    /**
     * Volledig, server-gevalideerd vervangingspakket. Het bevat ook de
     * verwijderde brongeometrie, zodat een afgebroken, oude of profielvreemde
     * clientmutatie de tekst zichtbaar kan herstellen zonder nieuwe fetch.
     */
    placementEvidence?: {
      coordinateSpace: 'design'
      method: 'source-components' | 'source-row' | 'legacy-segments'
      sourceBox: { x: number; y: number; w: number; h: number }
      baselineY?: number
      scale?: number
      letters: Array<{
        char: string
        sourceInkBox?: { x: number; y: number; w: number; h: number }
        segmentBox: { x: number; y: number; w: number; h: number }
        glyphBox: { x: number; y: number; w: number; h: number }
        paths: string[]
        measurement: 'source-components' | 'source-row' | 'dp' | 'trace' | 'ai' | 'word-fallback'
      }>
    }
    /** Binding automatic decision; absent on historical traces. Manual editing remains possible. */
    captionReplacementDecision?: { status: 'retained' | 'replaced' | 'blocked'; reason: string }
    /** Provider explicitly reported a caption, but its reading was rejected.
     * Bounds are source evidence only; this record never owns trace paths. */
    captionReadingRejection?: { sourceRole: 'caption'; reason: string; text: string; bbox?: {x:number;y:number;w:number;h:number}; confidence?: number; curved?: boolean }
    captionReplacement?: {
      v: 1
      /** Reconstructed photo text has no original vector paths to restore. */
      origin?: 'photo-text'
      profile: { schemaVersion: 1; product: ProductKind; outputFingerprint: string }
      sourceRef: { sourceHash: string; sourceId?: string }
      text: string
      fontId: string
      paths: string[]
      fills: string[]
      charStrokeCounts: Array<{ char: string; strokeCount: number }>
      source: {
        paths: string[]
        fills: string[]
        sourceFills?: string[]
        componentGroups?: number[]
        subpathRoles?: PathSubpathRole[][]
      }
    }
  }>
  /** Merkteken-gebieden (®/™/©) uit Vision, viewBox-ruimte.
   *  Paden hierbinnen worden client-side als PRINT gemarkeerd — plat
   *  drukwerk, nooit neon (FedEx-case). */
  trademarkBoxes?: Array<{ x: number; y: number; w: number; h: number }>
  /** Raw full-logo Vision OCR payload (Claude Vision). Only attached
   *  when the caller sets `?logoOcrDebug=1`. Used by /test playground
   *  and `test-ocr-batch.mjs` to compare against legacy results — not
   *  consumed by the production configurator. */
  logoOcr?: LogoOcrDebug
  /** Closed-loop kwaliteitsoordeel van de Vision-zelfcheck: hoe goed
   *  lijkt het getracede resultaat op de originele upload. Aanwezig
   *  wanneer de check aanstond en slaagde. `retried` betekent dat er
   *  een tweede trace-poging met aangepaste parameters is gedaan. */
  traceQuality?: TraceQualityReport
  /** Fase- en inhoudgebonden status van de definitieve kwaliteitscontrole. */
  qualityAssessment?: QualityAssessment
  /**
   * C2 — DETERMINISTISCH KWALITEITS-VANGNET. Gemeten dekking van de trace over
   * de bron-inkt, zonder AI. Staat BEWUST náást `traceQuality` en niet erin:
   * dit vangnet bestaat juist voor de gevallen waarin `traceQuality`
   * `undefined` is (anonieme sessie, check uit, Vision-timeout/429, budget op).
   * Zou de meting in `TraceQualityReport` hangen, dan zou hij precies bij de
   * zwaarste logo's — de reden dat dit veld er is — mee verdwijnen, of zou er
   * een `score` verzonnen moeten worden die niemand gemeten heeft.
   *
   * Aanwezig wanneer `quality.deterministic.enabled` aanstaat en de meting
   * slaagde; anders afwezig, en dan is de respons byte-identiek aan die van
   * vóór dit veld (geen cache-versie-bump nodig).
   */
  deterministicQuality?: DeterministicQuality
  /** Stage-tijdlijn van de server-pipeline voor deze upload: welke stap
   *  draaide wanneer, hoelang, en een kort detail (padenaantal,
   *  zelfcheck-score, OCR-latency). Gerenderd in het AdminTuningPanel
   *  zodat een admin per upload precies ziet wat de pipeline deed. */
  pipeline?: PipelineStageEntry[]
  /** Tussenbeelden van de pijplijn (origineel -> voorbewerkt ->
   *  kleurreductie) als data-URL-PNG's. Alleen aanwezig bij ?debug=1 —
   *  gerenderd door de /test-microscoop. */
  debugStages?: { name: string; image: string; note?: string }[]
  /**
   * A5 — de server heeft de bronrand-artefacten (buitenrand, kantstreep,
   * kaderstapel) al weggeknipt; `svgPaths`/`pathFills` zijn dus al schoon en de
   * client MOET zijn eigen drie randfilters overslaan. Dubbel filteren met
   * andere drempels kost paden die geen van beide filters afzonderlijk zou
   * wegknippen.
   *
   * Ontbreekt het veld (oude server, gecachete respons van vóór de vlag, of
   * `TRACE_BORDER_V2=off`), dan filtert de client zoals hij altijd deed — dát
   * is de achterwaartse compatibiliteit. Op het pure-SVG-pad staat het veld óók
   * op `true` terwijl er niets gefilterd is: daar zijn de heuristieken zonder
   * padding-ring onveilig en horen ze aan geen van beide kanten te draaien.
   */
  serverFiltered?: boolean
  /**
   * A11 — WELKE PIJPLIJN DIT RESULTAAT MAAKTE.
   *   - `'raster'`     — de volledige keten (flatten, crop/ademruimte-ring,
   *                      kwantisatie, potrace, skeletonisatie). Er is een
   *                      uitgelijnd bronraster, dus rand-heuristieken en de
   *                      dekkingsmeting zijn zinvol; `sourcePlacement` hoort
   *                      erbij (tenzij de voorbewerking faalde).
   *   - `'svg-direct'` — het ge-uploade SVG-bestand is rechtstreeks geparsed
   *                      (de rasterisatie lukte niet). Geen padding, geen crop,
   *                      geen uitgelijnd raster: een lijn tegen de canvasrand is
   *                      hier gewoon ontwerp en er valt niets te meten.
   *
   * ALTIJD aanwezig in een verse respons; optioneel in het type omdat
   * `traceImage` een `TraceResult` zonder dit veld oplevert (de parser weet niet
   * langs welke route hij is aangeroepen) en omdat een gecachete body van vóór
   * cache-v58 hem mist.
   */
  pipelineRoute?: 'raster' | 'svg-direct'
}

export interface PipelineStageEntry {
  stage: string
  /** Start van deze stage, in ms sinds het begin van de request. */
  startedAtMs: number
  durationMs: number
  detail?: string
}

/** Eén OCR-element zoals vastgelegd in het trace-archief. */
export interface ArchivedOcrElement {
  kind: 'wordmark' | 'caption' | 'print-mark'
  text: string
  confidence: number
  /** S6 (2026-07-17): true wanneer deze rij als automatische single-line
   *  FONT is hertekend (geen belijnings-trace) — het portaal labelt 'm dan
   *  als "automatische font". Afwezig bij oudere records. */
  renderedAsFont?: boolean
  /** Font-id van de automatische hertekening (alleen bij renderedAsFont). */
  fontId?: string | null
}

/** S5 (2026-07-17): één aspect-regel uit de kwaliteitsscore-opbouw. De
 *  weights zijn indicatief (vaste presentatie-gewichten); de hoofdscore
 *  blijft het holistische Vision-oordeel. */
export interface QualityBreakdownEntry {
  label: string
  /** 0..1 per aspect. */
  score: number
  /** Indicatief gewicht 0..1; som ≈ 1. */
  weight: number
}

/**
 * C15 deel 2 — WAAROM IS DEZE RIJ NIET DOOR EEN FONT VERVANGEN?
 *
 * De klacht die dit veld dichttimmert (VAN PAASSEN, 2026-08-11): "HOVENIER"
 * stond met confidence 100% in het OCR-blok, maar de klantweergave verving de
 * rij niet — en nergens (archief, trace-pagina, debug) was te zien wáárom.
 * Elke overslaan-tak van `planCaptionAutoApply` (en van de apply-fase erna)
 * krijgt daarom een machineleesbare code, zodat de trace-pagina naast de
 * "Font"-kolom kan tonen wat er gebeurde in plaats van dat de eigenaar moet
 * gokken.
 *
 *   primary-brand     — merk-/wordmark-rij: de lettervormen ZIJN het ontwerp,
 *                       die worden per eigenaars-besluit nooit vervangen.
 *   keep-outline-size — Plan H (adidas-poort): de letters zijn fysiek zó groot
 *                       dat de originele outline al perfect buigbaar is.
 *   auto-apply-off    — de tenant/admin zette de automatische vervanging uit
 *                       (geldt alleen voor rijen die nog een trace hebben).
 *   low-confidence    — onbetrouwbare OCR (tesseract of lage confidence) op een
 *                       rij die zijn trace nog heeft: liever het origineel.
 *   no-candidate      — geen enkele leesbare kandidaat-tekst voor deze rij.
 *   font-missing      — de font-render (`/api/text-to-paths`) leverde niets op.
 *                       Ook de automatische toepassing haakt met deze reden af
 *                       wanneer er geen bruikbaar font is — vóór enige render.
 *   arc-unsupported   — gebogen rij die het registratie-pad niet aankon.
 */
export type CaptionSkipReason =
  | 'replacement-retained'
  | 'primary-brand'
  | 'keep-outline-size'
  | 'auto-apply-off'
  | 'low-confidence'
  | 'no-candidate'
  | 'font-missing'
  | 'arc-unsupported'

/** Eén overgeslagen caption-rij, zoals het archief en de trace-pagina hem tonen. */
export interface CaptionSkipRecord {
  /** De tekst van de rij zoals de OCR hem las (leeg wanneer er geen was). */
  rowText: string
  reason: CaptionSkipReason
}

/**
 * SCORE-TERUGVAL — met welke meetlat de `qualityScore` van een archief-record
 * tot stand kwam.
 *
 * Bewust APART van `QualityMeasuredBy` (dat beschrijft wat er allemaal gemeten
 * IS: `'vision+deterministic' | 'vision' | 'deterministic' | 'none'`). Dit veld beantwoordt
 * de smallere vraag waar het portaal-label aan hangt: welk van die getallen
 * staat er nu daadwerkelijk in `qualityScore`? Een run kan `measured =
 * 'vision+deterministic'` zijn en toch een deterministische score dragen zodra
 * het Vision-oordeel geen cijfer opleverde.
 */
export type QualityScoreSource = 'vision' | 'deterministic'

/** Galerij-item: het lichte overzicht per trace (zonder zware velden). */
export interface TraceArchiveSummary {
  id: string
  /** ISO-tijd van de upload. */
  createdAt: string
  /** Stabiele grant-id van de klant — voor per-klant filtering in het
   *  portaal. '_intern' bij anonieme/interne traces. */
  grantId: string
  /** Software-slug van de klant (mag leeg zijn). */
  softwareSlug: string
  /** Mens-leesbaar label van de klant/herkomst ('intern' bij geen grant). */
  tenantLabel: string
  fileName: string
  fileType: string
  fileSizeBytes: number
  /**
   * Kwaliteitsscore 0..1, of null wanneer er NIETS gemeten is.
   *
   * Tot de score-terugval was dit uitsluitend het Vision-oordeel; viel dat uit
   * (anonieme upload, check uit, timeout/429, tijdbudget op) dan bleef het veld
   * `null` en toonde het portaal een grijs "—", terwijl de DETERMINISTISCHE
   * meetlat wél gemeten had. Sindsdien draagt dit veld de Vision-score als die
   * er is, en anders de deterministische terugval — `qualityScoreSource` zegt
   * welke van de twee het is.
   */
  qualityScore: number | null
  /**
   * WELKE MEETLAT DE `qualityScore` HIERBOVEN LEVERDE — zodat het portaal het
   * cijfer kan labelen ("AI" vs "meetlat") in plaats van twee onvergelijkbare
   * getallen als één badge te tonen.
   *
   *   `'vision'`        — het AI-eindoordeel (`TraceQualityReport.score`).
   *   `'deterministic'` — de pixel-terugval `deterministicFallbackScore()`:
   *                       0,7 × dekking + 0,3 × (1 − spook). Geen AI-oordeel,
   *                       wél een echte meting.
   *   `null`            — er is niets gemeten; `qualityScore` is dan ook null.
   *
   * LEESKANT / OUDE RECORDS: het veld ONTBREEKT op elk record van vóór deze
   * feature. Behandel afwezig + een score als `'vision'` — historisch kwam elke
   * score in dit veld uit Vision. Afwezig + `qualityScore === null` betekent
   * "niet gemeten", precies als `null`.
   */
  qualityScoreSource?: QualityScoreSource | null
  qualityProblem: string | null
  qualityRetried: boolean
  totalMs: number
  rowCount: number
  warningCount: number
  /** 256px JPEG als data-URL — direct in de galerij te tonen, geen
   *  losse fetch en geen publieke blob-URL nodig. */
  inputThumb: string
  /** Bewaartermijn (dagen) die gold toen dit record werd weggeschreven.
   *  Afwezig bij records van vóór deze feature → de retentie-sweep hanteert
   *  dan de default van 30 dagen (backwards compat). Geklemd op 1–365. */
  retentionDays?: number
  /**
   * D8 — DRAAIDE DE AI-TEKSTHERKENNING VOOR DEZE UPLOAD, JA OF NEE?
   *
   * `true` = overgeslagen (geen sessie/grant, geen API-key, rate-limit of
   * tijdbudget). Tot D8 was dat alleen af te leiden uit een REGEX op de
   * klant-zichtbare waarschuwingstekst (`/OCR niet uitgevoerd|
   * AI-tekstherkenning/i`) — een leeskant die breekt zodra iemand die zin
   * herformuleert. Nu legt de schrijfkant het als expliciete boolean vast.
   *
   * Afwezig bij records van vóór D8; leeskanten houden daarvoor hun
   * regex-terugval.
   */
  ocrSkipped?: boolean
}

/** Volledig archief-record voor de detailweergave. */
export interface TraceArchiveRecord extends TraceArchiveSummary {
  sourceVectorization?: SourceVectorizationReport
  logoRefinement?: LogoRefinementReport
  /** Publieke (onraadbare) blob-URL van de 768px input-render. */
  inputUrl: string | null
  /** Inline SVG-render van het trace-resultaat. */
  outputSvg: string
  viewBoxWidth: number
  viewBoxHeight: number
  pipeline: PipelineStageEntry[]
  ocr: ArchivedOcrElement[]
  qualitySummary: string
  warnings: string[]
  /**
   * D3 — regels over TENANT-CONFIG-waarden die de settings-mapper weigerde
   * (`production.traceAlphaMax=2 buiten [0.6,1.4] — default gebruikt`). Bewust
   * gescheiden van `warnings` hierboven: dat is de klant-zichtbare lijst die ook
   * in de respons zit, terwijl dit beheerders-diagnose over de configuratie is —
   * het zegt niets over dit logo, alles over de instellingen van deze tenant.
   * Afwezig bij oudere records, bij tenants zonder config-fouten en zolang
   * `TENANT_MAPPER_V2` uit staat.
   */
  configWarnings?: string[]
  /** S5: opbouw van de kwaliteitsscore per aspect. Afwezig/null bij oudere
   *  records of wanneer de zelfcheck niet draaide. */
  qualityBreakdown?: QualityBreakdownEntry[] | null
  /** S2: neon-preview van het eindresultaat (donkere wand + gloed) als
   *  data-URL (JPEG) — de blob-store is privé, dus een data-URL toont
   *  direct in het portaal zonder extra auth. Afwezig/null bij oudere
   *  records of een render-fout. */
  outputPreviewUrl?: string | null
  /**
   * C15 deel 1 — DE KLANT-SNAPSHOT: een PNG van de werkelijke editor-preview
   * (plaat, kleuren, captions, verborgen paden exact zoals getoond), door de
   * browser geëxporteerd en via `POST /api/trace-preview` bijgeschreven.
   *
   * De waarde is het BLOB-PAD (`traces/preview/<grant>/<id>.png`), niet de
   * blob-URL: de store is privé, dus het portaal leest 'm hoe dan ook via de
   * SDK. Afwezig = geen klant-snapshot (vlag uit, oude record, of de klant
   * sloot de pagina vóór de eerste export) → de trace-pagina valt terug op de
   * bestaande server-composiet.
   */
  clientPreviewPath?: string | null
  /**
   * C15 deel 2 — de rijen die GEEN font-vervanging kregen, met reden. Komt van
   * de client (de beslissing valt daar, ná de respons) via hetzelfde
   * `POST /api/trace-preview`. Afwezig bij oudere records en bij runs waarin
   * niets is overgeslagen.
   */
  captionSkips?: CaptionSkipRecord[] | null
  /**
   * A05 (issue #113) — DE RUNTIME-VERSIE DIE DEZE TRACE MAAKTE: `APP_VERSION`
   * uit `apps/runtime/lib/app-version.ts` op het moment van de upload
   * (`V1.322.0`). Het beheer-tracedetail toont hem als "Runtime-versie".
   *
   * Alleen op het RECORD, niet op de summary: de lijst toont hem niet, en elk
   * veld op de summary reist mee in elke galerijpagina. Afwezig bij records van
   * vóór dit veld; de leeskant trimt, knipt op 64 tekens en valt terug op "—".
   * `TRACE_CACHE_VERSION` gaat hiervoor niet omhoog: de trace-output verandert
   * niet, alleen wat er over de trace bewaard wordt.
   */
  appVersion?: string | null
}

/**
 * DE MEETLAT VAN DE LETTERVERVANGING (plan 10-9-2026, §A6) — alleen de VORM.
 *
 * De berekening woont in `apps/runtime/lib/placement-metrics.ts`; hier
 * staat uitsluitend het contract, en daar is een reden voor die verder gaat
 * dan netheid: `TraceResult.detectedTextRows[].placement.metrics` draagt deze
 * vorm, en `packages/contracts` mag geen `apps/` importeren (harde regel
 * `.dependency-cruiser.cjs`). Zou het type in de app blijven, dan zou het
 * rij-contract het niet kunnen noemen en zou er een TWEEDE, met de hand
 * bijgehouden kopie ontstaan — precies de stille drift die de meting
 * waardeloos maakt. De app-module importeert deze types en her-exporteert ze,
 * zodat er één definitie is.
 *
 * DE SLEUTELNAMEN LIGGEN VAST. `scripts/logo-testset/render.mjs` (WP0) leest
 * `row.placement.metrics` per rij en breekt een hele ronde af als een
 * grootheid ontbreekt of anders heet. Hernoemen is dus geen opruimactie maar
 * een gebroken — en stil gebroken — meting.
 */
export type PlacementSource = 'dp' | 'trace' | 'ai'

/** Per letter: hoe ver zijn midden van het midden van zijn vak ligt
 *  (genormaliseerd op de rijhoogte) en hoeveel van de vakhoogte hij vult. */
export interface PlacementLetterMetric {
  char: string
  dcx: number
  hRatio: number
}

/** Per woord: hoeveel van zijn vak de gezette inkt vult. De SPREIDING
 *  hiervan over een rij is de vingerafdruk van het uitsmeer-probleem. */
export interface PlacementWordMetric {
  text: string
  wRatio: number
}

export interface PlacementMetrics {
  anchor: 'height' | 'width'
  /** Aantal letters dat daadwerkelijk gemeten kon worden. */
  letters: number
  maxDcx: number
  meanDcx: number
  minHRatio: number
  maxHRatio: number
  /** max(wRatio) − min(wRatio) over de woorden. 0 bij één woord. */
  wRatioSpread: number
  /** Deze rij viel terug op de woordvloer (trede b van de ladder). */
  wordFloor: boolean
  /** Deze rij gebruikte AI-verfijning. In golf 0/1 altijd false; WP4 zet hem. */
  aiUsed: boolean
  segmentConfidence: number
  /** Verdeling van de vak-bronnen over deze rij. */
  sources: Record<PlacementSource, number>
  perLetter: PlacementLetterMetric[]
  perWord: PlacementWordMetric[]
}

/**
 * Cirkelboog-descriptor voor gebogen caption-tekst. Posities op de boog:
 * theta(t) = startAngle + t * sweep, t in [0..1]; punt = centrum + r *
 * (cos theta, sin theta). Sweep is getekend: positief = met de klok mee
 * in SVG-coordinaten (y omlaag).
 */
export interface CaptionArc {
  cx: number
  cy: number
  r: number
  startAngle: number
  sweep: number
}

/**
 * C3 — MET WELKE MEETLAT IS DIT RESULTAAT BEOORDEELD?
 *
 * Het gat dat dit veld dicht: de KPI kon "goed" niet van "niet gemeten"
 * onderscheiden. `logTraceSuccess` schreef `quality.score = null` zowel bij een
 * anonieme upload (geen Vision), bij een Vision-timeout, bij een verlopen
 * tijdbudget als bij een pure-SVG-run — vier totaal verschillende situaties met
 * één en dezelfde uitkomst in het dashboard. Zolang dat onderscheid ontbreekt,
 * is elke uitspraak over "hoeveel procent van de uploads is goed" onbruikbaar:
 * de noemer is onbekend.
 *
 *   `vision+deterministic` — er is een Vision-oordeel (met, waar er een
 *                            uitgelijnd raster was, de pixelmeting ernaast).
 *   `vision`               — alleen het Vision-kanaal is werkelijk geslaagd.
 *   `deterministic`        — geen Vision, wél een pixelmeting (dekkingsmeting
 *                            A12 en/of het C2-vangnet). Dit is het anonieme pad.
 *   `none`                 — niets gemeten. Dít is de toestand waarvan het
 *                            dashboard tot nu toe niet wist dat ze bestond.
 */
export type QualityMeasuredBy = 'vision+deterministic' | 'vision' | 'deterministic' | 'none'

export interface TraceQualityReport {
  /** 0..1 — gelijkenis tussen origineel en trace volgens Vision. */
  score: number
  problem: 'ok' | 'missing-shapes' | 'extra-noise' | 'banding' | 'wrong-colors' | 'other'
  summary: string
  retried: boolean
  /** S5: opbouw per aspect (afwezig bij oudere cache-entries). */
  breakdown?: QualityBreakdownEntry[]
  /**
   * C3 — de meetlat achter deze score. Afwezig bij cache-entries van vóór C3.
   *
   * Staat IN dit rapport en niet ernaast omdat hij hier hoort: hij kwalificeert
   * de score. Voor de gevallen waarin er GEEN rapport is (en `measured` dus
   * `'deterministic'` of `'none'` zou zijn) draagt het KPI-event het veld —
   * `logTraceSuccess` schrijft `quality.measured` onvoorwaardelijk, juist omdat
   * de afwezigheid van een rapport daar de te meten toestand is.
   */
  measured?: QualityMeasuredBy
}

/**
 * C2 — uitkomst van de DETERMINISTISCHE kwaliteitsmeting (geen AI, geen kosten,
 * reproduceerbaar). Meetmethode + drempels staan in
 * `packages/ocr/src/deterministic-quality.ts`; hier alleen de vorm, omdat
 * `TraceResult` (in dit package) hem draagt en `@mark2form/contracts` niets uit
 * `@mark2form/ocr` mag importeren (de richting is precies andersom).
 */
export interface DeterministicQuality {
  /** |bron \ trace| / |bron| — welk deel van de bron-inkt de trace NIET dekt. */
  missingFraction: number
  /** |trace \ bron| / |trace| — welk deel van de trace niet in de bron staat. */
  ghostFraction: number
  /** |bron ∩ trace| / |bron ∪ trace| op de ONgedilateerde maskers; puur
   *  diagnostisch (er hangt geen drempel aan). */
  iou: number
  /**
   * ECONOMIE-SIGNAAL (Van Paassen-regressie 17-8) — dicht de blinde vlek
   * waarmee dekking-alleen de vlakkenschillen-chaos als "perfect" mat:
   * gestapelde antialias-schillen DEKKEN de bron volledig (missing 0,000)
   * maar kosten er een veelvoud aan buis voor (replica: 6,2× de padlengte van
   * de monoline). `pathLenPxPerInkPx` = totale padlengte in canvas-px gedeeld
   * door het aantal bron-inktpixels op datzelfde canvas; een monoline zit
   * grofweg op 1/streekbreedte, schillen-stapels een veelvoud daarvan.
   * SCHADUW-METING: geen gate, geen warning — alleen respons/KPI, zodat de
   * kalibratie op echte data kan. Afwezig bij cache-entries van vóór dit
   * veld én wanneer de inkt-meting niets zag; consumenten moeten dat
   * tolereren (optioneel veld, additief in de respons — geen cache-bump).
   */
  economy?: { pathLenPxPerInkPx: number }
  /** Padboekhouding: hoeveel paden gingen erin, hoeveel bleven er over, en
   *  welke stap knipte hoeveel weg (uit de A2-verliesboekhouding). */
  pathCounts: {
    raw: number
    afterClean: number
    removedByStage: Record<string, number>
  }
}

/**
 * C2 — fasering-schakelaar van het deterministische vangnet, sectie
 * `quality.deterministic`. BEIDE default false, en de sectie ontbreekt tot een
 * tenant hem stuurt: zonder config verandert er niets aan het productiegedrag
 * en blijft ook de trace-cachesleutel byte-identiek.
 *
 *   `enabled`     fase 1 — meten + rapporteren + KPI (shadow).
 *   `gateEnabled` fase 2 — de gemeten `missingFraction` mag ook echt blokkeren.
 *
 * Rollback = beide op false; er is niets te migreren.
 */
export interface DeterministicQualityConfig {
  enabled: boolean
  gateEnabled: boolean
}

export interface OcrCandidate {
  engine: 'claude' | 'tesseract' | 'claude-logo'
  text: string
  fontId: string
  confidence: 'high' | 'medium' | 'low'
}

/**
 * Debug payload from the full-logo Vision OCR — optionally attached to
 * TraceResult when the caller wants raw model output (e.g. /test
 * playground, test-ocr-batch.mjs). Production UI ignores it.
 */
export interface LogoOcrDebug {
  wordmark: LogoOcrDebugElement | null
  captions: LogoOcrDebugElement[]
  printMarks: LogoOcrDebugElement[]
  latencyMs: number
  retried: boolean
  droppedElements: number
  error: string | null
  cached: boolean
  /** Per-element orphan-snap diagnostic, only populated when ?logoOcrDebug=1. */
  orphanSnap?: Array<{
    text: string
    isWordmark: boolean
    visionBbox: { x: number; y: number; w: number; h: number }
    snapped: { x: number; y: number; w: number; h: number } | null
    snapOriginallyNull: boolean
    pathIndices: number[]
    dropReason: string | null
    /**
     * B5: dit record hoort bij de TWEEDE poging, op een door
     * `refineElementBbox` verfijnde bbox. Twee records voor dezelfde tekst
     * betekent dus: eerste poging gedropt, betaalde verfijning geprobeerd.
     * Afwezig op elk record van vóór dit bestekpunt.
     */
    refined?: boolean
  }>
}
export interface LogoOcrDebugElement {
  text: string
  bbox: { x: number; y: number; w: number; h: number }
  fontStyle: 'sans' | 'serif' | 'script' | 'handwritten' | 'display' | 'mono'
  curved: boolean
  confidence: number
}

export interface TraceRowAnalysis {
  /** Width/height of the trace canvas in viewBox units. */
  canvasW: number
  canvasH: number
  /** Physical sign width the analyzer used (cm). */
  widthCm: number
  /** Tube thickness used (mm). */
  tubeMm: number
  /** Threshold used at decision time (mm). */
  thresholdMm: number
  rows: Array<{
    /** Row bbox in viewBox units. */
    bbox: { minX: number; minY: number; maxX: number; maxY: number }
    /** Number of components in this row. */
    memberCount: number
    /** Min stroke thickness across members (mm). */
    minStrokeMm: number
    /** Median stroke thickness across members (mm). */
    medianStrokeMm: number
    /** Decision: 'mono' = skeletonised, 'double' = perimeter-traced. */
    mode: 'mono' | 'double' | 'logo'
    /** Reason the mode was picked, for the debug tooltip. */
    reason: string
    /** Per-member component-metingen (alleen debug) — voor het kalibreren
     *  van solide-vorm-guards: w/h/maxDist/area per component. */
    members?: Array<{ w: number; h: number; maxDist: number; area: number }>
  }>
}

export interface PriceResult {
  tubeLengthMeters: number
  tubePrice: number
  plateSizeCm: { width: number; height: number }
  plateMaterial: PlateMaterial
  /** Resolved from current settings, never a visitor-supplied description or rate. */
  plateVariant?: Pick<NeonPlateVariant, 'id' | 'name' | 'finish' | 'imageUrl'>
  platePrice: number
  /** Number of bends across the design (drives bend-cost line). */
  bendCount: number
  bendPrice: number
  /** Number of separate tube setups (one per visible region). */
  tubeCount: number
  tubeSetupPrice: number
  /**
   * C9 — kosten van de UV-print op de plaat (oppervlakte × `printPricePerCm2`).
   *
   * BEWUST OPTIONEEL EN BEWUST AFWEZIG BIJ 0. De sleutel verschijnt alleen
   * wanneer er daadwerkelijk inkt in rekening wordt gebracht, dus wanneer de
   * eigenaar een tarief heeft ingevuld én er print-oppervlakte is. Zolang dat
   * niet zo is, is een `PriceResult` byte-identiek aan die van vóór C9 — en dat
   * is precies wat elke bestaande serialisatie (golden-snapshots, offerte-JSON,
   * order-mail, share-URL) nodig heeft om ongewijzigd te blijven. `total` telt
   * dit bedrag mee zodra het er is.
   */
  printPrice?: number
  total: number
}

export interface ManufacturabilityReport {
  cornersRounded: number
  clearanceIssues: number
}

// ---------------------------------------------------------------------------
// Taak D3 — AI-ontwerpadviseur (admin-only). Contract voor de suggesties die
// Claude Vision teruggeeft over een afgerond ontwerp, plus de machine-
// actie die de configurator-UI eraan kan koppelen ("Pas toe"-knop). De
// gehele respons wordt server-side STRENG gevalideerd tegen deze vorm
// (zie @mark2form/ocr/design-advice) — alles wat niet valideert wordt
// weggegooid of gedegradeerd naar `geen-actie`. De LLM-output is data,
// nooit instructie.
// ---------------------------------------------------------------------------

/** Type-discriminant van een adviseur-actie. `geen-actie` = puur informatief
 *  (geen mapbare knop). */
export type DesignAdviceActionType =
  | 'print-toggle'
  | 'contour-modus'
  | 'breedte-advies'
  | 'kleur-advies'
  | 'geen-actie'

/**
 * Machine-actie bij een suggestie — een discriminated union. De UI koppelt
 * een deel hiervan aan bestaande handlers (contour → plateShape-setter,
 * breedte → widthCm-setter, print-toggle → print-suggesties). `kleur-advies`
 * en `geen-actie` worden (in deze fase) alleen getoond, niet toegepast.
 */
export type DesignAdviceAction =
  | { type: 'print-toggle' }
  | { type: 'contour-modus' }
  /** Doelbreedte in cm — server-side geclampt op een reëel bereik. */
  | { type: 'breedte-advies'; doelbreedteCm: number }
  /** Korte kleuromschrijving/naam of hex zoals het model die teruggaf. */
  | { type: 'kleur-advies'; kleur: string }
  | { type: 'geen-actie' }

/** Eén concrete verbeter-suggestie: wat te doen (`advies`), waarom (`reden`)
 *  en de gekoppelde actie. Teksten zijn NL, kort, en puur presentatie. */
export interface DesignAdviceSuggestion {
  advies: string
  reden: string
  actie: DesignAdviceAction
}

/** Volledige adviseur-respons (max 3 suggesties). */
export interface DesignAdviceResult {
  suggesties: DesignAdviceSuggestion[]
  meta: {
    latencyMs: number
    /** Null bij succes; anders reden waarom er geen advies is (niet
     *  geconfigureerd, rasterise-fout, API-fout, json-shape-fout). De
     *  caller toont suggesties alleen als error null is. */
    error: string | null
    /** True wanneer het resultaat uit de in-memory cache kwam. */
    cached: boolean
    /** Claude API-tokenverbruik van deze aanroep (0/0 bij cache-hit of
     *  niet-geconfigureerd) — voor het AI-kosten-dashboard van het portaal. */
    usage: { inputTokens: number; outputTokens: number }
  }
}

// ---------------------------------------------------------------------------
// NEON TEKST (uitvoerplan v4, taak ST1) — het tekst-eerst product naast Neon
// Logo. Deze contracten wonen hier omdat DRIE lagen ze delen: de app
// (`lib/studio/compose.ts`), `@mark2form/output` (de `#design=`-hash) en
// `@mark2form/configurator-engine` (de localStorage-autosave). Zonder een
// gedeelde bodem zou `packages/` naar `apps/` moeten wijzen — verboden.
//
// ── DE NAAMGEVING, EIGENAARSBESLUIT PASCAL 21-9-2026 ────────────────────────
//
//     Neon Studio          de CATEGORIE: hier beheer je je instellingen
//     ├── Neon Logo        product, eigen staffel
//     └── Neon Tekst       product, eigen staffel
//
// "Studio" betekende tot die dag het TEKSTPRODUCT; nu betekent het de laag
// erboven. Dat maakt élke vindplaats van het woord een leesvraag en nooit een
// zoek-en-vervang: hier gaat het om het product, dus `DesignOrigin` draagt
// sinds deze ronde `'tekst'`. De TYPE-NAMEN in dit blok (`StudioInput`,
// `StudioLine`, `StudioAlign`, `StudioPlateInput`, `StudioSizePreset`,
// `StudioConfig`) en de configsectiesleutel `studio` staan er nog: die eerste
// groep is een rename dwars door L3 en L4 heen, de tweede een sleutel in
// `software_configs.config` en dus een datamigratie. Allebei een eigen PR —
// zie de overdracht bij issue #74.
// ---------------------------------------------------------------------------

/**
 * G1 (architectuur-grondwet v4) — waar een ontwerp vandaan komt, en daarmee
 * WELK PRODUCT het maakte. Elk ontwerp spreekt hetzelfde snapshot-formaat;
 * alleen dit veld verschilt. Ontbreekt het (elk ontwerp van vóór G1), dan is
 * het per definitie `'logo'`.
 *
 * ── DE WAARDE `'studio'` HEET SINDS 21-9-2026 `'tekst'` ─────────────────────
 * Eigenaarsbesluit van Pascal (zie het kader hierboven): het tekstproduct
 * heet Neon Tekst, en de naam "Neon Studio" is opgeschoven naar de CATEGORIE
 * erboven. Daarmee valt een vertaling weg die er nooit had moeten zijn: de
 * runtime kende `'logo' | 'studio'`, de factuur `'logo' | 'tekst'`
 * (`OutboxModus`), en `apps/runtime/lib/usage-download.ts` hield daar een
 * afbeelding `PRODUCT_NAAR_MODUS` tussen — een tarieftabel met maar één
 * reden van bestaan: twee namen voor hetzelfde ding. Eén lijst in plaats van
 * twee plus een vertaling.
 *
 * De LEESALIAS hieronder is de prijs van die opruiming, en hij is bewust
 * eenrichtingsverkeer. Zie `DESIGN_ORIGIN_LEESALIASSEN`.
 */
export const DESIGN_ORIGINS = ['logo', 'tekst'] as const

export type DesignOrigin = (typeof DESIGN_ORIGINS)[number]

/**
 * Waarden die alleen nog GELEZEN worden — nooit geschreven.
 *
 * ── WAAROM EEN ALIAS EN GEEN DATAMIGRATIE ───────────────────────────────────
 * `'studio'` staat in bewaarde gegevens die wij niet allemaal in handen
 * hebben: de `product`/`pipeline`-velden van een `design_revision`, de
 * localStorage-autosave van elke bezoeker, en — de lastigste — elke
 * `#design=`-deel-link die ooit de deur uit ging. Een rij kun je migreren; een
 * link die in de mailbox van een klant ligt niet. Een bestaande waarde
 * ongeldig verklaren zou dus ontwerpen onleesbaar maken, en dat weegt niet op
 * tegen een nette lijst.
 *
 * ── HET IS EENRICHTINGSVERKEER, EN DAT IS DE HELE AFSPRAAK ──────────────────
 * LEZEN: `leesDesignOrigin('studio')` → `'tekst'`.
 * SCHRIJVEN: er bestaat geen functie die de andere kant op gaat, en
 * `DesignOrigin` bevat `'studio'` niet, dus een schrijver KAN hem niet meer
 * opslaan — hij krijgt hem genormaliseerd of hij compileert niet. Zou de
 * alias twee kanten op werken, dan zou de oude naam nooit uitsterven en had
 * dit package er permanent twee.
 *
 * ── KAN DE ALIAS OOIT WEG? DEELS ────────────────────────────────────────────
 * Voor de DATABASE wel, en het is te tellen:
 *
 *   1. `select count(*) from design_revision where content->>'product' =
 *      'studio'` (idem `content->'production'->>'pipeline'`), plus
 *      `events.metadata->>'source'`, `software_submissions.source` en
 *      `software_configs.config->'embed'->>'standaard'`. Dat zijn vijf
 *      plekken en ze staan in §"overdracht" van issue #74.
 *   2. Eén datamigratie die `'studio'` naar `'tekst'` zet. LET OP: de
 *      `contentHash` van een `design_revision` is een SHA-256 over de
 *      canonieke inhoud, dus die migratie herberekent hashes of markeert de
 *      geraakte revisies als `legacy-unbound` — zie het kader bij
 *      `CanonicalDesignContent.production` in `./design-revision`.
 *   3. Daarna is de alias voor de databasepaden dood.
 *
 * Voor de DEEL-LINKS niet. `ShareablePayload` (`@mark2form/engines-uitvoer`)
 * reist in een URL-hash en heeft geen vervaldatum; een link uit 2026 hoort in
 * 2030 nog te openen. De alias weghalen betekent daar: `ShareablePayload`-
 * `version` ophogen en accepteren dat elke oudere link zijn tekst-ontwerp als
 * logo opent. Dat is een eigenaarsbesluit en geen opruimactie, en tot iemand
 * het neemt is deze alias permanent. Die eerlijkheid is het punt: een alias
 * zonder uitweg mag bestaan, maar dan moet er staan dát hij er geen heeft.
 *
 * ── WAAR DEZE LEZER HOORT ───────────────────────────────────────────────────
 * Op elke PARSE-grens waar bewaarde gegevens binnenkomen, en nergens anders:
 * `decodeShareUrl` en `loadDesign` (beide L4-packages), de snapshot-lezer van
 * de editor (L3) en `leesEmbedStand` (`@mark2form/config-model`). Die vier
 * zetten hem zelf; dit package levert alleen de regel.
 */
export const DESIGN_ORIGIN_LEESALIASSEN = {
  studio: 'tekst',
} as const satisfies Readonly<Record<string, DesignOrigin>>

/** Een waarde die alleen nog uit bewaarde gegevens kan komen. */
export type DesignOriginLeesalias = keyof typeof DESIGN_ORIGIN_LEESALIASSEN

/**
 * Wat er uit BEWAARDE gegevens kan komen: de canonieke waarden plus de
 * aliassen. Gebruik dit type nooit voor iets dat geschreven wordt — een veld
 * dat de deur uit gaat is een `DesignOrigin`.
 */
export type BewaardeDesignOrigin = DesignOrigin | DesignOriginLeesalias

/** Strikt: alleen de canonieke waarden. Een alias is hier GEEN `DesignOrigin`. */
export function isDesignOrigin(waarde: unknown): waarde is DesignOrigin {
  return typeof waarde === 'string' && (DESIGN_ORIGINS as readonly string[]).includes(waarde)
}

/** Is dit een waarde die we alleen nog lezen? */
export function isDesignOriginLeesalias(waarde: unknown): waarde is DesignOriginLeesalias {
  return (
    typeof waarde === 'string' &&
    Object.prototype.hasOwnProperty.call(DESIGN_ORIGIN_LEESALIASSEN, waarde)
  )
}

/**
 * De canonieke waarde achter iets dat uit bewaarde gegevens kwam, of `null`
 * wanneer het geen van beide is.
 *
 * `null` en geen terugval, omdat de aanroeper moet kiezen: een ontbrekend veld
 * is iets anders dan een veld met onzin erin, en alleen hij weet welk van de
 * twee hij in handen heeft. Wie die keuze niet hoeft te maken pakt
 * `designOriginOfLogo`.
 */
export function leesDesignOrigin(waarde: unknown): DesignOrigin | null {
  if (isDesignOrigin(waarde)) return waarde
  if (isDesignOriginLeesalias(waarde)) return DESIGN_ORIGIN_LEESALIASSEN[waarde]
  return null
}

/**
 * Idem, maar met de bestaande terugval: ontbreekt het veld of slaat het
 * nergens op, dan is het `'logo'`.
 *
 * Dat is de regel die vandaag als prozaregel op vier plekken staat ("ontbreekt
 * → logo"), en hij is bovendien de veilige kant op voor het geldpad: `logo`
 * draagt de DUURSTE staffel (§9.1, €25–250 per maand tegen €5–50 voor
 * `tekst`), dus onzin die hierdoorheen komt kan een klant nooit te weinig
 * laten betalen. Andersom terugvallen zou dat wél kunnen.
 */
export function designOriginOfLogo(waarde: unknown): DesignOrigin {
  return leesDesignOrigin(waarde) ?? 'logo'
}

/** Uitlijning van de tekstregels binnen de compositie. */
export type StudioAlign = 'left' | 'center' | 'right'

export const STUDIO_LETTER_SPACINGS = ['narrow', 'normal', 'wide'] as const
export type StudioLetterSpacing = (typeof STUDIO_LETTER_SPACINGS)[number]

/** Eén tekstregel in de Studio. */
export interface StudioLine {
  text: string
  /** Id uit de font-catalogus (`/api/text-to-paths` GET). */
  fontId: string
  /** Kleur van de hele regel (hex, `#rrggbb`). */
  colorHex: string
  /**
   * Per-letter kleur-override. Sleutel = index van het teken in `text`
   * (0-based, spaties tellen mee). Ontbrekende sleutel → `colorHex`.
   * Bewust een record met string-sleutels: dit reist door JSON.
   */
  letterColors?: Record<string, string>
}

/** Plaat-keuze in de Studio — dezelfde drie assen als de logo-flow. */
export interface StudioPlateInput {
  shape: PlateShape
  material: PlateMaterial
  plateVariantId?: string
  mdfColorId?: string
}

/**
 * De VOLLEDIGE gebruikersinvoer van een studio-ontwerp. Dit is wat er in het
 * snapshot bewaard wordt (A-ST2: "bewaar `studioInput` als veld ín het
 * snapshot — niet reverse-engineeren uit paden"), zodat heropenen de Studio-UI
 * exact terugzet.
 */
export interface StudioInput {
  /** Absent replays the exact legacy layout; new text uses contour-aware placement. */
  textLayoutVersion?: 'optical-v1'
  /** Physical tube choice used by optical placement, persisted with that layout. */
  textLayoutTubeMm?: number
  /** Absent retains the existing automatic line layout. */
  composition?: StudioFreeComposition
  lines: StudioLine[]
  /** Breedte stuurt; de hoogte volgt uit de compositie. */
  widthCm: number
  plate: StudioPlateInput
  align: StudioAlign
  /** Three spacing choices within the saved layout version. */
  letterSpacing?: StudioLetterSpacing
  /** Regelafstand-factor. Default 1.0, geklemd op 0.6–1.6. */
  lineGapFactor: number
}

/** Eén maat-preset in de Studio (config-contract `studio.sizePresets`). */
export interface StudioSizePreset {
  id: string
  widthCm: number
}

/**
 * Genormaliseerde per-tenant studio-config (sectie `studio` in
 * `software_configs.config`). Alle velden aanwezig en geklemd — de
 * normalisator + clamps staan in `apps/runtime/lib/studio/config.ts`,
 * die als enige deze vorm produceert.
 */
export interface StudioConfig {
  enabled: boolean
  /** Leeg = alle catalogus-fonts. Ids uit de font-catalogus. */
  fontAllowlist: string[]
  /** Leeg = volledig kleurenpalet van de tenant. */
  colorAllowlist: string[]
  sizePresets: StudioSizePreset[]
  minWidthCm: number
  maxWidthCm: number
  /** `null` = i18n-default. */
  welcomeText: string | null
  /**
   * De twee KRUISLINKS tussen Studio en logo-flow (uitvoerplan v4, ST3):
   * "Heb je een logo? Upload 'm hier" in de Studio, en "Alleen tekst?
   * Ontwerp 'm in de Studio" bij de upload-zone. Default `true` — ze zijn
   * navigatie tussen twee producten van dezelfde maker, geen feature die
   * stil aangaat. Een maker die maar één van de twee verkoopt zet 'm uit en
   * dan verdwijnen ze ALLEBEI.
   *
   * Let op: de logo-flow-kant hangt daarnaast aan `enabled` — een link naar
   * een Studio die uitstaat is een doodlopende weg.
   */
  crossLinks: boolean
}

// ---------------------------------------------------------------------------
// ORDER-EXTRA'S (uitvoerplan v4, golf 2 — taken CV1/CV2/CV3, contracten A-CV1
// t/m A-CV3). Levertijd-tiers, upsells en kortingscodes zijn géén tweede
// prijsengine: het zijn BOM-REGELS die ná de kernprijs komen (G2). De
// rekenregels staan in `@mark2form/pricing/order-extras`, de clamps van de
// config in `apps/runtime/lib/order-extras-config.ts`.
//
// GELDEENHEID — één regel, drie plekken (kruisrepo-contract, G9):
//   • CONFIG (`software_configs.config.delivery|upsells`) rekent in HELE
//     CENTEN (`priceCents`, en `surcharge.value` bij `type:'fixed'`).
//   • PERCENTAGES zijn procentpunten (`-5` = −5%).
//   • De KORTINGSCODE komt uit de database (`discount_codes.value numeric`)
//     en rekent bij `type:'fixed'` in EURO'S — een numeric-geldkolom, zoals
//     het portaal 'm de maker ook laat intypen. `normalizeAppliedDiscount`
//     is de enige plek die dat naar centen omrekent.
// ---------------------------------------------------------------------------

/** Toeslag/korting: percentage van het lopende subtotaal, of een vast bedrag. */
export type OrderExtraValueType = 'percent' | 'fixed'

/** Toeslag van één levertijd-tier. `value`: procentpunten óf hele centen. */
export interface DeliverySurcharge {
  type: OrderExtraValueType
  value: number
}

/**
 * Eén levertijd-tier (config-contract A-CV2). `label: null` = i18n-default;
 * `extraDays` zijn WERKDAGEN bovenop vandaag (weekend telt niet mee).
 */
export interface DeliveryTier {
  id: string
  label: string | null
  extraDays: number
  surcharge: DeliverySurcharge
}

/** Sectie `delivery` in `software_configs.config`, genormaliseerd + geklemd. */
export interface DeliveryConfig {
  enabled: boolean
  tiers: DeliveryTier[]
}

/** Eén upsell (config-contract A-CV2). `label: null` = i18n-default. */
export interface UpsellItem {
  id: string
  label: string | null
  /** Vast bedrag in HELE CENTEN, geklemd op 0..50000. */
  priceCents: number
  /** Staat het vinkje standaard aan bij binnenkomst? */
  defaultOn: boolean
}

/** Sectie `upsells` in `software_configs.config`, genormaliseerd + geklemd. */
export interface UpsellsConfig {
  enabled: boolean
  items: UpsellItem[]
}

/**
 * Sectie `discounts` in `software_configs.config`. Bewust minimaal: de codes
 * leven in de database (A-CV3 — `max_uses` vergt atomiciteit), dit is enkel
 * de vlag die het portaal aanzet bij de eerste actieve code.
 */
export interface DiscountsConfig {
  enabled: boolean
}

/**
 * Een korting die de klant daadwerkelijk heeft toegepast — het antwoord van
 * `POST <website-origin>/api/discount/validate` (A-CV3), genormaliseerd.
 *
 * `code` is de door de MAKER aangemaakte code (een id, geen vrije klanttekst)
 * en mag dus in event-meta (G6). `value`: procentpunten bij `percent`, EURO'S
 * bij `fixed` — zie de eenheid-regel boven aan dit blok.
 */
export interface AppliedDiscount {
  code: string
  type: OrderExtraValueType
  value: number
}

// ---------------------------------------------------------------------------
// MAAKBAARHEID, KLANT-ZICHTBAAR (uitvoerplan v4, golf 2 — taak CV4).
//
// De configurator draait al drie maakbaarheids-checks: print-routering
// (`pathPrintMode`), hoek-afronding op de minimale buigradius
// (`ManufacturabilityReport.cornersRounded`) en de onderlinge buisafstand
// (`clearanceIssues`). Die drie voedden tot nu toe ALLEEN de admin-kant:
// `ManufacturabilityBanner` (geel, "Productie-aanpassingen", met auto-fix) en
// de rode ringen in de preview.
//
// CV4 voegt geen enkele check toe — het draait dezelfde uitkomsten om naar
// GERUSTSTELLING voor de eindklant: "✓ 2 fijne details worden geprint voor
// perfecte kwaliteit". Geen aantallen-met-alarm, geen jargon, geen actie die
// de klant moet ondernemen. Het alarm en de knoppen blijven admin-only.
// ---------------------------------------------------------------------------

/** De drie badge-soorten; één per bestaande maakbaarheids-check. */
export type QualityBadgeKind = 'printed' | 'rounded' | 'clearance'

/**
 * Genormaliseerde per-tenant kwaliteits-config (sectie `quality` in
 * `software_configs.config`). Clamps + normalisator:
 * `apps/runtime/lib/quality-config.ts`.
 *
 * `customerBadges` staat DEFAULT AAN — anders dan `delivery`/`upsells`/
 * `discounts`/`studio`, die default uit staan. Reden: die vier veranderen wat
 * de klant kan kiezen en wat hij betaalt, dus die moeten stil blijven tot een
 * maker ze aanzet. Een badge verandert niets aan prijs, keuze of bestelling —
 * het is puur een zin die vertelt wat er toch al gebeurt. Default uit zou
 * betekenen dat élke tenant handmatig iets moet aanzetten om een feature te
 * krijgen die per definitie niemand kan schaden.
 */
export interface QualityConfig {
  /** Paid judgement is manual by default. OCR and deterministic checks remain independent. */
  aiAssessment?: 'manual' | 'automatic'
  customerBadges: boolean
  /**
   * Tenant-override per badge (G7: tenant-tekst wint van de i18n-default).
   * `null` = geen override → de i18n-tekst. Een override wordt LETTERLIJK
   * getoond, dus zónder het aantal dat de default-tekst van `printed` ervoor
   * zet: een maker die zijn eigen zin schrijft bepaalt zelf of daar een getal
   * in hoort.
   */
  badgeTexts: Record<QualityBadgeKind, string | null>
  /**
   * C2 — deterministisch kwaliteits-vangnet. OPTIONEEL en alleen aanwezig als
   * de tenant de sectie stuurt. Dat is geen slordigheid maar een eis: de
   * volledige `Settings` zit in de trace-cachevingerafdruk
   * (`cache-step.ts` → `traceCacheKey`), dus een veld dat altijd meegeschreven
   * wordt zou élke bestaande cache-entry busten (mass-recompute + Vision-
   * kosten) terwijl de trace-output identiek is. Ontbreekt de sectie, dan
   * levert `resolveDeterministicQuality` de defaults (beide false).
   */
  deterministic?: DeterministicQualityConfig
  /**
   * C4 — gestructureerde klant-waarschuwingen. OPTIONEEL en om exact dezelfde
   * reden als `deterministic` hierboven alleen aanwezig als de tenant de
   * sectie stuurt: de volledige `Settings` gaat door `settingsFingerprintOf`
   * (`cache-step.ts`), en een sleutel die er altijd staat zou élke bestaande
   * cache-entry busten.
   *
   * NOG-TE-DOEN BIJ HET AANZETTEN (bewust hier vastgelegd): deze vlag stuurt
   * uitsluitend de WEERGAVE in de browser aan — de server schrijft `warningsV2`
   * onvoorwaardelijk, in beide standen byte-identiek. Hij hoort dus, net als
   * `traceCleanupV2` (A10) en `printOnTop.rowConsistencyV2` (C6), UIT de
   * cachevingerafdruk. Zolang de sectie afwezig is (de huidige stand van elke
   * tenant) valt hij in `stableStringify` vanzelf weg en is er niets te doen.
   * De eerste tenant die hem aanzet heeft één regel in `settingsFingerprintOf`
   * (`apps/runtime/lib/trace-route/cache-step.ts`) nodig:
   *
   *     quality: settings.quality
   *       ? { ...settings.quality, customerWarnings: undefined }
   *       : undefined,
   *
   * Zonder die regel kost het aanzetten van een WEERGAVE-vlag een volledige
   * hertrace van élk gecachet logo van die tenant — inclusief Vision-kosten —
   * voor een respons die byte-identiek zou zijn.
   */
  customerWarnings?: CustomerWarningsConfig
  /**
   * C3 — zelfcontrole-lus 2.0 (hertrace-ladder + budget vanaf trace-klaar +
   * strengere cache-toets). OPTIONEEL, om exact dezelfde reden als de twee
   * secties hierboven: een sleutel die er altijd staat zou élke bestaande
   * cache-entry busten.
   *
   * ANDERS DAN `customerWarnings`: deze vlag HOORT WÉL in de
   * cachevingerafdruk. Hij verandert server-side het resultaat (een ladder kan
   * een ander traceresultaat publiceren) en de opslagbeslissing. Hij wordt dus
   * NIET uitgesloten in `settingsFingerprintOf`; zie de toelichting bij
   * `SelfCheckV2Config`.
   */
  selfCheckV2?: SelfCheckV2Config
  /**
   * C15 deel 1 — KLANT-SNAPSHOT ALS PRIMAIRE PREVIEW. Aan = de browser
   * exporteert de wérkelijke preview (plaat, kleuren, captions, verborgen
   * paden) als PNG en POST't die naar `/api/trace-preview`, zodat de
   * portaal-trace-pagina toont wat de klant zág in plaats van de
   * server-composiet die op meerdere assen afwijkt.
   *
   * OPTIONEEL, om exact dezelfde reden als de secties hierboven: de volledige
   * `Settings` gaat door `settingsFingerprintOf`, dus een sleutel die er altijd
   * staat zou élke bestaande cache-entry busten.
   *
   * NET ALS `customerWarnings` HOORT HIJ UIT DE CACHEVINGERAFDRUK — en dat is
   * hier al gedaan (`cache-step.ts`). Het is namelijk zuiver CLIENT-gedrag: de
   * browser exporteert en upload. De server-trace is in beide standen
   * byte-identiek; zou de vlag meetellen, dan kostte het aanzetten ervan een
   * volledige hertrace van élk gecachet logo van die tenant — Vision-facturen
   * incluis — voor een respons die letterlijk gelijk is.
   *
   * Uit (de stand van elke tenant vandaag) = geen export, geen POST, nul
   * gedragsverandering.
   */
  clientPreview?: boolean
  /**
   * D9 — PER-GRANT VISION-DAGBUDGET (facturatie-haakjes, bestek deel D §9).
   * Maximum aantal betaalde Vision-runs per dag voor deze tenant. `0` betekent
   * Vision permanent UIT voor deze tenant (de gratis potrace-route blijft);
   * afwezig = platform-default 200 (`VISION_DAILY_BUDGET_DEFAULT` in
   * `lib/quality-config.ts` — gespiegeld aan `src/lib/usage.ts` in het
   * portaal). Geheel getal ≥ 0; de clamp zit in `normalizeQualityConfig`.
   *
   * OPTIONEEL, om exact dezelfde reden als de secties hierboven: de volledige
   * `Settings` gaat door `settingsFingerprintOf`, dus een sleutel die er altijd
   * staat zou élke bestaande cache-entry busten.
   *
   * HOORT UIT DE CACHEVINGERAFDRUK — en dat is gedaan (`cache-step.ts`). Dit
   * is NIET de `selfCheckV2`-regel maar de `pricing`-regel, en het verschil is
   * precies te benoemen: het budget beslist of een NIEUWE run Vision mág
   * draaien, nooit WAT een run oplevert. De enige stand die cache-entries
   * schrijft is "Vision draaide" (`shouldCacheTrace` eist `visionOk`), en
   * binnen die stand is de pijplijn voor elke budgetwaarde byte-identiek. Er
   * bestaat dus geen entry uit "de andere stand" die per ongeluk geserveerd
   * kan worden — het gat dat de vingerafdruk bij `selfCheckV2` dicht, bestaat
   * hier niet. Zie de volledige afweging in `settingsFingerprintOf`.
   */
  visionDailyBudget?: number
}

/**
 * C4 — fasering-schakelaar van de gestructureerde klant-waarschuwingen.
 *
 * `enabled: false` (de default, en de stand zonder config) = EXACT het huidige
 * gedrag: de client draait het legacy-pad met `splitWarnings` en toont de ene
 * generieke tip-regel. Aan per tenant ná visuele controle.
 *
 * Alleen een expliciete `true` telt (de `enabled === true`-regel van
 * `delivery`/`upsells`/`designHelp`): een half-gemigreerde config of `"ja"`
 * levert "uit" op, en dat is de kant waar niets aan de klant-weergave
 * verandert.
 */
export interface CustomerWarningsConfig {
  enabled: boolean
}

/**
 * C3 — fasering-schakelaar van de ZELFCONTROLE-LUS 2.0, sectie
 * `quality.selfCheckV2`. Default false, en de sectie ontbreekt tot een tenant
 * hem stuurt.
 *
 * ── ÉÉN VLAG VOOR DRIE DINGEN, EN DAT IS EEN EIS ────────────────────────────
 * Hij schakelt tegelijk:
 *   1. de hertrace-LADDER (max 2 treden, winnaar op compositescore) in plaats
 *      van de enkele hertrace;
 *   2. het TIJDBUDGET vanaf trace-klaar in plaats van vanaf request-start,
 *      met de wachttijd op een Vision-slot eruit gerekend;
 *   3. de strengere CACHE-TOETS (score ≥ 0,55 én missing ≤ 0,10) plus de
 *      hermeting van een cache-hit.
 * Die drie zijn samen consistent en apart NIET. De ladder zonder het herdefinieerde
 * budget zou bij zware logo's nooit aan zijn tweede trede toekomen (het budget was
 * dan al op vóórdat de kwaliteitsstap begon); het budget zonder de strengere
 * cache-toets zou de extra pogingen wel doen maar het resultaat van een
 * mislukte poging alsnog dertig dagen uitserveren. Vandaar één schakelaar, geen
 * drie.
 *
 * ── RANGORDE T.O.V. DE A12-HERTRACE ─────────────────────────────────────────
 * Staat deze vlag AAN, dan is de ladder de OPVOLGER van de enkele
 * deterministische hertrace achter `TRACE_DET_GATE`: die poort meet en
 * waarschuwt nog steeds, maar hertraceert niet meer zelf. Twee hertrace-motoren
 * op één request zouden elkaars werk overdoen (dezelfde parametertabel!) en
 * samen het tijdbudget opeten. Staat de vlag UIT, dan blijft A12 exact doen wat
 * hij deed. Zie `runCoverageGate` in `quality-step.ts`.
 *
 * ── WAAROM EEN TENANT-VLAG EN GEEN ENV-VLAG ─────────────────────────────────
 * Anders dan `TRACE_DET_GATE` (kalibratie over ál het verkeer) verandert deze
 * schakelaar wat de klant KRIJGT: meer hertraces (dus latentie en Vision-kosten)
 * en minder cache-hits. Dat is een keuze per maker, niet per deployment. Hij
 * telt daarom WÉL mee in de trace-cachevingerafdruk (`settingsFingerprintOf`,
 * `cache-step.ts`) — zelfde regel als `sloganStripGuardV2` (B3) en `ocrRasterV2`
 * (B6): de vlag verandert server-side het resultaat, dus een entry uit de ene
 * stand mag nooit in de andere geserveerd worden. Afwezige sectie ⇒ afwezige
 * sleutel ⇒ byte-identieke vingerafdruk als vóór dit bestekpunt.
 *
 * Rollback = veld op false; er is niets te migreren.
 */
export interface SelfCheckV2Config {
  enabled: boolean
}

/**
 * Eén klant-zichtbare badge zoals `deriveQualityBadges` hem oplevert: welke
 * check hem voedt, hoeveel gevallen die check vond, en — alleen bij een
 * tenant-override — de letterlijke tekst. De i18n-sleutel kiest de UI zelf,
 * zodat deze vorm puur en taal-onafhankelijk blijft.
 */
export interface QualityBadge {
  kind: QualityBadgeKind
  count: number
  overrideText: string | null
}

/**
 * "Badges bij de prijs" — framing (portaal-sectie `framing`, migratie 0023).
 * Twee klant-toggles die onder de totaalprijs een geruststellende regel met
 * vinkje tonen ("Gratis verzending" / "Gratis ontwerpcheck"). Puur weergave:
 * ze veranderen niets aan prijs, keuze of bestelling — maar anders dan de
 * maakbaarheids-badges zijn dit BELOFTES namens de maker (gratis verzenden,
 * gratis ontwerpcheck), dus default UIT tot hij ze zelf aanvinkt.
 * Normalisator: `apps/runtime/lib/framing-config.ts`.
 */
export interface FramingConfig {
  freeShipping: boolean
  freeDesignCheck: boolean
}

// ---------------------------------------------------------------------------
// 24-UURS-DESIGNHULP (uitvoerplan v4, golf 3 — taak WF3).
//
// Het onderzoek achter dit plan: bij ÁLLE concurrenten is "ons team maakt 'm
// binnen 24 uur" het HOOFDPRODUCT — je stuurt een bestand in en wacht. Bij ons
// is het de FALLBACK op de AI: je ziet je ontwerp meteen, en gaat de omzetting
// een keer niet goed genoeg, dan ligt het vangnet eronder. Beste van beide
// werelden, en precies daarom mag dit NOOIT als een foutmelding voelen.
//
// TWEE INGANGEN, ÉÉN FLOW:
//   'low-score'        automatisch aanbod wanneer de trace-kwaliteitsscore
//                      onder `scoreThreshold` ligt — wij beginnen erover
//                      vóórdat de klant teleurgesteld afhaakt.
//   'customer-request' de altijd-beschikbare bescheiden knop bij de prijs,
//                      ook (juist) bij een prima score: smaak is geen score.
//
// VERSCHIL MET DE LAGE-SCORE-GATE (`lib/low-score-gate.ts`, K6c): die BLOKKEERT
// onder 0.5 het bestellen — het resultaat is dan aantoonbaar onbruikbaar. Dit
// hier blokkeert niets: de klant kan gewoon bestellen én krijgt een aanbod.
// Daarom ligt de default-drempel (0.55) er bewust bóven: de zone 0.5–0.55 is
// "bestelbaar maar het kan beter", en dat is precies waar een aanbod helpt.
// ---------------------------------------------------------------------------

/** Waarom de designhulp-kaart verscheen. Enum, dus veilig in event-meta (G6). */
export type DesignRequestReason = 'low-score' | 'customer-request'

/**
 * Genormaliseerde per-tenant designhulp-config (sectie `designHelp` in
 * `software_configs.config`). Clamps, defaults en normalisator:
 * `./config/design-help.ts` (`normalizeDesignHelpConfig`).
 *
 * `enabled` staat DEFAULT AAN sinds C-E2 (`DESIGN_HELP_DEFAULT_ENABLED`); een
 * tenant zet het aanbod uit met één expliciete `enabled: false`. Tot 28-9-2026
 * stond hier nog "default uit" — dat was de regel van vóór C-E2 en klopte
 * niet meer met wat de runtime deed.
 */
export interface DesignHelpConfig {
  enabled: boolean
  /**
   * Onder deze trace-kwaliteitsscore (0–1) biedt de configurator uit zichzelf
   * hulp aan. Geklemd op 0–1; default `DESIGN_HELP_BAND_TOP` (0,7). Op 0
   * zetten laat alleen de knop over (een score is nooit < 0), op 1 biedt hij
   * het altijd aan.
   */
  scoreThreshold: number
  /**
   * De termijn in UREN, geklemd op 1–168 (één week). In het portaal is dit
   * de SLA-klok op de job-kaart (default 24). Tegenover de bezoeker is het
   * GEEN productbelofte (besluit KP6, 28-9): het getal verschijnt alleen in
   * een klanttekst als de maker zelf `{{slaHours}}` in zijn eigen tekst zet.
   */
  slaHours: number
  /**
   * Tenant-override voor de knop bij de prijs (G7: tenant wint van i18n).
   * `null` = de i18n-default. Wordt LETTERLIJK getoond, op `{{slaHours}}` na —
   * die vult de UI ook in een eigen tekst in, anders zou een maker het getal
   * hard moeten intypen en bij een SLA-wijziging stil kunnen liegen.
   */
  buttonText: string | null
  /**
   * Tenant-titel van de ontwerphulp-kaart (issue #258, besluit 28-9-2026),
   * max `DESIGN_HELP_CARD_TITLE_MAX` tekens, platte tekst. `null` = de
   * neutrale standaardtitel van de runtime (i18n).
   */
  cardTitle: string | null
  /**
   * Tenant-tekst van de ontwerphulp-kaart (#258), max
   * `DESIGN_HELP_CARD_TEXT_MAX` tekens, platte tekst. `null` = de neutrale
   * standaardtekst van de runtime ("Twijfel je over je logo? Vraag hulp bij
   * het ontwerp; we nemen contact met je op.").
   */
  cardText: string | null
}

// ---------------------------------------------------------------------------
// AI-ONTWERPADVISEUR NAAR DE EINDKLANT (uitvoerplan v4, golf 5 — taak GR3)
// ---------------------------------------------------------------------------
// De adviseur zelf bestaat al (taak D3): POST /api/design-advice legt het
// originele logo + het neon-eindontwerp aan Claude Vision voor en geeft max 3
// suggesties terug. Tot nu toe was dat ADMIN-ONLY. Deze sectie opent hetzelfde
// endpoint voor de EINDKLANT — per tenant, met eigen caps.
//
// WAAROM DEFAULT UIT: elke klant-aanvraag kost de maker een echte Vision-call.
// Dat is de reden dat `customerEnabled` de `enabled === true`-regel volgt van
// `delivery`/`upsells`/`discounts`/`studio`/`designHelp` en niet de default-aan
// van `quality.customerBadges`: een badge is gratis, een AI-call niet.
// ---------------------------------------------------------------------------

/**
 * Genormaliseerde per-tenant adviseur-config (sectie `advisor` in
 * `software_configs.config`). Clamps + normalisator:
 * `apps/runtime/lib/advisor-config.ts`.
 *
 * Raakt de ADMIN-kant nooit: een admin houdt zijn adviseur ook als deze sectie
 * ontbreekt of `customerEnabled: false` is. Deze config beslist uitsluitend of
 * het KLANT-pad open staat en hoeveel adviezen dat pad per dag mag kosten.
 */
export interface AdvisorConfig {
  /**
   * Mag de EINDKLANT de adviseur zien en gebruiken? Default `false`. Alleen
   * een expliciete `true` opent het pad — een half-gemigreerde config, `null`
   * of `"ja"` levert dus "uit" op, en dat is de kant waar niemand ongevraagd
   * AI-kosten maakt.
   */
  customerEnabled: boolean
  /**
   * Maximaal aantal KLANT-adviezen per tenant per dag (UTC), geklemd op
   * 1–500; default 25. Bovengrens is een vangrail tegen een typefout in het
   * portaal ("2500"), niet een bedrijfslimiet. Telt uitsluitend het klant-pad:
   * admin-adviezen gaan buiten deze teller om (die vallen al onder het
   * bestaande Vision-dagbudget van de tenant).
   */
  dailyLimit: number
  /**
   * Tenant-tekst boven de klant-suggesties (G7: tenant wint van i18n).
   * `null` = de i18n-default. Wordt LETTERLIJK getoond en is gecapt, zodat een
   * maker de toon kan zetten ("Onze ontwerper kijkt straks nog mee") zonder
   * dat een lap tekst het prijsblok kan kapen.
   */
  introText: string | null
}

// ---------------------------------------------------------------------------
// TWEE PRODUCTEN ONDER ÉÉN EMBEDCODE (plan 9-9-2026, "Neon Studio als tweede
// product"). Neon Studio wordt een APART verkoopbaar product naast de
// logo-configurator: eigen grant, eigen abonnement, eigen telling. De klant
// plakt echter één embedcode op zijn site en bepaalt in het portaal wat die
// code toont.
//
// WAAROM DEZE CONTRACTEN HIER WONEN. Ze worden aan BEIDE kanten van de streep
// gelezen: het portaal schrijft ze (sessie-respons + config-secties) en de app
// leest ze (`lib/gebruik-config.ts`, `lib/embed-instellingen.ts`,
// `lib/producten.ts`). Een tweede beschrijving aan één van beide kanten zou
// precies de stille drift opleveren die dit contract moet voorkomen.
//
// FAIL-SOFT IS DE HOOFDREGEL. Elk veld hier is NIEUW: een portaal dat ze nog
// niet stuurt (de stand van vandaag) moet byte-gelijk het gedrag van vandaag
// opleveren. Daarom zijn de defaults gekozen als "wat er nu gebeurt" en niet
// als "wat het veiligst klinkt" — `stand: 'volledig'`, `prijs: 'tonen'`.
// ---------------------------------------------------------------------------

/**
 * Welk PRODUCT een bezoeker voor zich heeft: Neon Logo of Neon Tekst. Bewust
 * hetzelfde alias als `DesignOrigin` en niet een tweede, eigen unie: de
 * herkomst van een ontwerp ÍS het product dat het maakte, en twee losse unies
 * zouden vroeg of laat uit elkaar lopen (een derde product zou dan op twee
 * plekken bij moeten). De naam bestaat wél apart, omdat de lezer op de ene
 * plek over een opgeslagen ontwerp praat en op de andere over een draaiende
 * embed.
 *
 * Sinds 21-9-2026 zijn dit dezelfde twee waarden als `OutboxModus`
 * (`./outbox`) — product en modus zijn hetzelfde ding, en het contract zegt
 * dat nu ook. Ze blijven twee declaraties omdat dit package geen enkel ander
 * package importeert (§5.1) en `OutboxModus` de lijst is die een PAYLOAD
 * keurt: daar staat een `z.enum` met de letterlijke waarden, fail-closed,
 * omdat een onbekende modus op een geldpad stil gratis zou zijn. Eén test
 * legt de twee lijsten naast elkaar.
 */
export type ProductKind = DesignOrigin

/**
 * De canonieke lijst, onder de productnaam. Dezelfde binding als
 * `DESIGN_ORIGINS` — niet een tweede lijst, want dat is precies wat deze
 * ronde opruimde. Hij bestaat zodat een lezer die over producten praat niet
 * hoeft te weten dat de lijst naar een ontwerp-herkomst vernoemd is;
 * `apps/runtime/lib/producten.ts` hield hier tot deze ronde zijn eigen kopie
 * voor.
 */
export const PRODUCT_KINDS = DESIGN_ORIGINS

/**
 * Hoever een bezoeker met dít product mag gaan (eigenaarsbesluit 2 van
 * 9-9-2026: "de klant stelt in het portaal in wat zijn embedcode toont").
 *
 *  - `'uit'`             — het product doet in deze embed niet mee. Het portaal
 *                          stuurt het toch mee (zodat het portaal één lijst kan
 *                          houden); de APP laat het weg, zie
 *                          `resolveBeschikbareProducten`.
 *  - `'alleen-bekijken'` — de bezoeker mag een voorbeeld maken en een offerte
 *                          vragen, maar niet doorconfigureren.
 *  - `'volledig'`        — alles, precies zoals vandaag.
 */
export type GebruikStand = 'uit' | 'alleen-bekijken' | 'volledig'

/**
 * Toont deze embed bedragen? `'verbergen'` = voorbeeld + "Offerte aanvragen"
 * (eigenaarsbesluit 3). Puur KLANT-presentatie: de maker en de admin zien het
 * bedrag altijd, en er wordt niets anders gerekend.
 */
export type GebruikPrijs = 'tonen' | 'verbergen'

/**
 * De sectie `gebruik` van één productconfig (`software_configs.config.gebruik`).
 * Genormaliseerd + geklemd door `apps/runtime/lib/gebruik-config.ts`, die
 * als enige deze vorm produceert — zelfde afspraak als `studio`/`quality`.
 *
 * DEFAULTS ZIJN HET HUIDIGE GEDRAG, niet de veiligste stand: een tenant zonder
 * deze sectie hoort niets te merken.
 */
export interface GebruikConfig {
  stand: GebruikStand
  prijs: GebruikPrijs
}

/**
 * De sectie `embed` — hoe de ENE embedcode zich gedraagt wanneer een bedrijf
 * meerdere producten heeft. Gedeeld over de neon-software (hij hoort bij de
 * code, niet bij één product) en daarom in het portaal een sectie van de
 * gedeelde basis; hij reist top-level in de sessie-respons mee.
 *
 * ONTBREEKT DE HELE SECTIE, dan is dat een OUD PORTAAL en geen "alles op
 * default": de app valt dan terug op de route als hint (`/c/` → Neon Logo,
 * `/s/` → Neon Tekst) — exact het gedrag van vandaag. Zie
 * `readEmbedInstellingen`, dat om die reden `null` kan teruggeven.
 */
export interface EmbedInstellingen {
  /** Met welk product de embed opent. */
  standaard: ProductKind
  /** Mag de bezoeker zelf wisselen (de tabs Neon Logo | Neon Tekst)? */
  schakelaar: boolean
  /** Blijven de kruislinks Neon Tekst ⇄ Neon Logo staan? */
  kruislinks: boolean
}
